const EMPTY_TEXT = '';
const DEFAULT_NORMATIVE_REVIEW_DATE = '05/05/2026';
const NORMATIVE_REVIEW_DATE = DEFAULT_NORMATIVE_REVIEW_DATE;

export type AgriculturalFieldHelpDetails = {
  readonly title: string;
  readonly summary: string;
  readonly officialTerm?: string;
  readonly why?: string;
  readonly whereToFind?: string;
  readonly example?: string;
  readonly nextStep?: string;
  readonly normativeNote?: string;
};

export type AgriculturalHelpContext = {
  readonly formName?: string | undefined;
  readonly formHeader?: string | undefined;
  readonly sectionName?: string | undefined;
};

type FieldHelpRule = {
  readonly keywords: readonly string[];
  readonly help: AgriculturalFieldHelpDetails;
};

const FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['id_cliente', 'cliente', 'titular'],
    help: {
      title: 'Titular o cliente',
      summary: 'Es la persona, empresa o entidad a la que pertenece la explotación.',
      officialTerm: 'Titular / cliente',
      why: 'Sirve para que las fincas, parcelas, trabajos y documentos queden asociados a la persona correcta.',
      whereToFind: 'En el listado de clientes o titulares de la aplicación.',
      example: 'Juan García, Cooperativa San Isidro, Explotaciones Norte S.L.',
      nextStep: 'Después de elegirlo, revisa que sus datos de contacto e identificación sean correctos.',
    },
  },
  {
    keywords: ['razon_social', 'razón social', 'razon social'],
    help: {
      title: 'Nombre legal de la empresa o entidad',
      summary: 'Es el nombre oficial con el que una empresa, cooperativa o entidad aparece en documentos.',
      officialTerm: 'Razón social',
      why: 'Ayuda a identificar correctamente a empresas o entidades en documentos y registros.',
      whereToFind: 'En facturas, escrituras, documentación fiscal o datos del cliente.',
      example: 'Agrícola Las Lomas S.L.',
    },
  },
  {
    keywords: ['nombre de finca', 'nombre de explotación', 'nombre de explotacion', 'nombre granja'],
    help: {
      title: 'Nombre de la explotación',
      summary: 'Es el nombre fácil con el que reconocerás esta finca o explotación dentro de la aplicación.',
      officialTerm: 'Nombre de finca / explotación',
      why: 'Sirve para encontrarla rápido y no confundirla con otras explotaciones.',
      whereToFind: 'Puedes usar el nombre habitual que usas en tu trabajo diario.',
      example: 'Finca Las Lomas, Granja Norte, Explotación familiar',
      nextStep: 'Después de poner el nombre, revisa los datos de identificación y dirección.',
    },
  },
  {
    keywords: ['finca', 'granja'],
    help: {
      title: 'Explotación o granja',
      summary: 'Es la unidad principal de trabajo: dentro de ella se organizan parcelas, sectores, recursos y tareas.',
      officialTerm: 'REA / explotación',
      why: 'Sirve para no mezclar datos de distintas explotaciones y para generar el cuaderno correcto.',
      whereToFind: 'En el registro autonómico de explotaciones o en los datos que ya tenga la aplicación.',
      example: 'Finca Las Lomas, Explotación familiar, Granja Norte',
      nextStep: 'Después de crear la finca, revisa o crea sus parcelas.',
    },
  },
  {
    keywords: ['titular_nombre', 'titular nombre'],
    help: {
      title: 'Nombre del titular',
      summary: 'Es la persona o entidad responsable de la explotación.',
      officialTerm: 'Titular de la explotación',
      why: 'Permite saber quién responde legalmente por los datos de la finca y sus trabajos.',
      whereToFind: 'En la documentación de la explotación, REA o datos del cliente.',
      example: 'María López García',
    },
  },
  {
    keywords: ['representante_nombre', 'representante nombre'],
    help: {
      title: 'Nombre del representante',
      summary: 'Es la persona que puede actuar en nombre del titular.',
      officialTerm: 'Representante',
      why: 'Sirve cuando una gestoría, asesoría o persona autorizada realiza trámites por el titular.',
      whereToFind: 'En la autorización firmada, sede electrónica o documentación de representación.',
      example: 'Pedro Martínez Ruiz',
      nextStep: 'Comprueba que también estén bien su NIF y correo.',
    },
  },
  {
    keywords: ['titular_nif', 'representante_nif', 'nif', 'dni', 'cif', 'nie'],
    help: {
      title: 'Documento identificativo',
      summary: 'Identifica a una persona, empresa o entidad.',
      officialTerm: 'NIF / DNI / NIE / CIF',
      why: 'Evita confusiones entre titulares, representantes o clientes con nombres parecidos.',
      whereToFind: 'En el DNI, NIE, CIF, documentación fiscal o ficha del cliente.',
      example: '12345678Z',
    },
  },
  {
    keywords: ['codigo_postal', 'titular_codigo_postal', 'código postal', 'codigo postal', 'cp'],
    help: {
      title: 'Código postal',
      summary: 'Es el número postal de la dirección indicada.',
      why: 'Ayuda a completar correctamente la ubicación administrativa.',
      whereToFind: 'En la dirección postal, documentación del titular o datos del cliente.',
      example: '46001',
    },
  },
  {
    keywords: ['codigo_REA', 'codigo rea', 'código rea', 'rea'],
    help: {
      title: 'Número de registro de la explotación',
      summary: 'Es el número con el que tu comunidad autónoma tiene registrada la explotación.',
      officialTerm: 'REA',
      why: 'Permite importar o contrastar datos oficiales de la explotación y sus parcelas.',
      whereToFind: 'En la documentación o sede electrónica de tu comunidad autónoma.',
      nextStep: 'Si importas REA, la aplicación puede necesitar autorización para tratar esos datos.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}.`,
    },
  },
  {
    keywords: ['registro_autonomico', 'registro autonómico', 'registro autonomico'],
    help: {
      title: 'Registro autonómico',
      summary: 'Es el registro de tu comunidad autónoma donde consta la explotación.',
      officialTerm: 'Registro Autonómico de Explotaciones',
      why: 'Sirve para relacionar la finca con los datos oficiales de tu comunidad autónoma.',
      whereToFind: 'En la sede electrónica o documentación agraria de tu comunidad autónoma.',
      example: 'Número asignado por la comunidad autónoma',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}.`,
    },
  },
  {
    keywords: ['registro_nacional', 'registro nacional'],
    help: {
      title: 'Registro nacional',
      summary: 'Es una referencia estatal o general relacionada con la explotación.',
      officialTerm: 'Registro nacional',
      why: 'Ayuda a identificar la explotación cuando se trabaja con datos de ámbito estatal.',
      whereToFind: 'En la documentación oficial de la explotación, si tu caso dispone de este dato.',
      example: 'Código o referencia nacional asignada',
    },
  },
  {
    keywords: ['codigo siex', 'código siex'],
    help: {
      title: 'Código único de la explotación',
      summary: 'Identifica la explotación cuando las administraciones intercambian información agraria.',
      officialTerm: 'Código SIEX',
      why: 'Ayuda a relacionar la explotación con registros oficiales y comunicaciones administrativas.',
      whereToFind: 'Normalmente lo asigna la Administración o se recupera desde los datos oficiales.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}.`,
    },
  },
  {
    keywords: ['codigo', 'código'],
    help: {
      title: 'Código interno',
      summary: 'Es una referencia corta para identificar esta finca dentro de la aplicación.',
      officialTerm: 'Código interno',
      why: 'Ayuda a buscar, ordenar o diferenciar fincas con nombres parecidos.',
      whereToFind: 'Puedes crearlo tú o usar el código que ya utilice tu empresa o asesoría.',
      example: 'FIN-001, LOMAS-2026',
    },
  },
  {
    keywords: ['direccion', 'dirección', 'titular_direccion', 'titular dirección'],
    help: {
      title: 'Dirección',
      summary: 'Indica la dirección postal de la finca, titular o cliente.',
      why: 'Sirve para localizar correctamente la explotación o los datos de contacto.',
      whereToFind: 'En la documentación de la explotación, ficha del cliente o dirección postal.',
      example: 'Camino Viejo, 12',
    },
  },
  {
    keywords: ['localidad', 'titular_localidad'],
    help: {
      title: 'Localidad',
      summary: 'Es el municipio o población relacionada con la dirección.',
      why: 'Ayuda a distinguir fincas, titulares o clientes de distintas zonas.',
      whereToFind: 'En la dirección postal o documentación de la explotación.',
      example: 'Alzira',
    },
  },
  {
    keywords: ['provincia', 'titular_provincia'],
    help: {
      title: 'Provincia',
      summary: 'Es la provincia donde se ubica la dirección indicada.',
      why: 'Completa la ubicación administrativa y evita confusiones entre localidades repetidas.',
      whereToFind: 'En la dirección postal, documentación de la explotación o datos del cliente.',
      example: 'Valencia',
    },
  },
  {
    keywords: ['municipio', 'localidad', 'provincia'],
    help: {
      title: 'Ubicación administrativa',
      summary: 'Ayuda a identificar dónde está la explotación, parcela o persona.',
      why: 'Evita confusiones entre fincas o parcelas con nombres parecidos.',
      whereToFind: 'En la documentación de la explotación, catastro, SIGPAC o dirección postal.',
    },
  },
  {
    keywords: ['telefono', 'teléfono', 'titular_telefono', 'titular teléfono'],
    help: {
      title: 'Teléfono',
      summary: 'Es un número de contacto de la persona, cliente o titular.',
      why: 'Sirve para avisos, soporte o comprobaciones posteriores.',
      whereToFind: 'En los datos de contacto del titular, cliente o representante.',
      example: '961234567',
    },
  },
  {
    keywords: ['movil', 'móvil'],
    help: {
      title: 'Móvil',
      summary: 'Es el teléfono móvil de contacto.',
      why: 'Puede usarse para avisos rápidos o contacto directo con el titular o cliente.',
      whereToFind: 'En los datos de contacto de la persona.',
      example: '612345678',
    },
  },
  {
    keywords: ['email', 'correo', 'titular_email', 'titular email', 'representante_email', 'representante email'],
    help: {
      title: 'Correo electrónico',
      summary: 'Es la dirección de correo de contacto.',
      why: 'Sirve para comunicaciones, avisos, soporte o envío de documentación.',
      whereToFind: 'En los datos del titular, cliente o representante.',
      example: 'nombre@correo.com',
    },
  },
  {
    keywords: ['telefono', 'teléfono', 'email', 'correo'],
    help: {
      title: 'Dato de contacto',
      summary: 'Permite localizar a la persona o entidad relacionada con el registro.',
      why: 'Es útil para avisos, soporte o comprobaciones posteriores.',
      whereToFind: 'En tus datos de contacto o los de la persona autorizada.',
    },
  },
  {
    keywords: ['superficie', 'ha', 'hectarea', 'hectárea'],
    help: {
      title: 'Superficie de la explotación',
      summary: 'Indica el tamaño total o aproximado de la finca o explotación.',
      officialTerm: 'Superficie declarada / superficie de explotación',
      why: 'Ayuda a revisar coherencia con parcelas, trabajos, dosis y documentación.',
      whereToFind: 'En SIGPAC, REA, declaración agraria o documentación de la finca.',
      example: '12,50 ha',
      nextStep: 'Si vas a apuntar una tarea, revisa también la superficie concreta trabajada.',
    },
  },
  {
  keywords: ['tipo_explotacion', 'tipo de explotación', 'tipo de explotacion'],
  help: {
    title: 'Forma de gestionar las plagas',
    summary: 'Indica cómo cumple esta explotación la Gestión Integrada de Plagas.',
    officialTerm: 'Gestión Integrada de Plagas (GIP)',
    why: 'Sirve para saber si la finca trabaja con producción integrada, certificación, agrupación o asesoramiento técnico.',
    whereToFind: 'En la documentación de la explotación, certificación, contrato con el asesor o datos facilitados por la entidad.',
    example: 'Producción Integrada, Certificación Privada, Asistida de un asesor',
    nextStep: 'Elige la opción que mejor represente cómo se controla y justifica la gestión de plagas en esta explotación.',
    normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE};
puede variar según cultivo, comunidad autónoma y normativa aplicable.`,
  },
},
  {
    keywords: ['alquilado', 'alquiler', 'arrendado', 'arrendamiento'],
    help: {
      title: 'Finca alquilada',
      summary: 'Indica si la finca se trabaja en alquiler o arrendamiento.',
      officialTerm: 'Arrendamiento',
      why: 'Ayuda a diferenciar fincas propias de fincas alquiladas o gestionadas por acuerdo.',
      whereToFind: 'En el contrato de alquiler, acuerdo con el propietario o documentación interna.',
      example: 'Activado si la finca no es propia y se trabaja alquilada.',
    },
  },
  {
    keywords: ['ids_productos', 'productos fertirrigación', 'productos fertirrigacion'],
    help: {
      title: 'Productos usados en fertirrigación',
      summary: 'Son los fertilizantes o productos que se aplican mezclados con el agua de riego.',
      officialTerm: 'Fertirrigación',
      why: 'Permite tener preparados los productos que luego se usarán al apuntar riegos o fertilizaciones.',
      whereToFind: 'En la etiqueta, factura, ficha del producto o listado de fertilizantes.',
      example: 'NPK soluble, ácido nítrico, corrector nutricional',
      nextStep: 'Selecciona solo los productos que realmente usas en esta finca.',
    },
  },
  {
    keywords: ['observaciones cliente', 'observaciones', 'observacion', 'observación', 'comentario', 'nota'],
    help: {
      title: 'Observaciones',
      summary: 'Campo libre para añadir aclaraciones útiles sobre la finca o el cliente.',
      why: 'Ayuda a recordar detalles importantes que no encajan en otros campos.',
      example: 'Cliente prefiere recibir avisos por la mañana.',
    },
  },
  {
    keywords: ['propiedad', 'propietario'],
    help: {
      title: 'Propiedad',
      summary: 'Indica si la finca es propia, alquilada o gestionada de otra forma.',
      officialTerm: 'Régimen de propiedad',
      why: 'Ayuda a saber qué relación tiene el titular o cliente con la finca.',
      whereToFind: 'En escrituras, contrato de arrendamiento o documentación interna.',
      example: 'Propia, alquilada, cedida',
    },
  },
  {
    keywords: ['ccc'],
    help: {
      title: 'Código CCC',
      summary: 'Es un código específico usado por algunas entidades o configuraciones de la aplicación.',
      officialTerm: 'CCC',
      why: 'Sirve para identificar datos internos o administrativos cuando tu organización lo necesita.',
      whereToFind: 'En la documentación interna, ficha del cliente o datos facilitados por la entidad.',
      example: 'Código asignado por la organización',
    },
  },
  {
    keywords: ['titular_tipo', 'tipo representación', 'tipo representacion'],
    help: {
      title: 'Tipo de representación',
      summary: 'Indica cómo actúa una persona o entidad respecto al titular.',
      officialTerm: 'Tipo de representación',
      why: 'Aclara si alguien actúa como titular, representante, autorizado o gestor.',
      whereToFind: 'En la autorización firmada, sede electrónica o documentación de representación.',
      example: 'Titular, representante autorizado, gestoría',
      nextStep: 'Comprueba que la autorización sea correcta antes de importar o enviar datos oficiales.',
    },
  },
  {
    keywords: ['autorizacion', 'autorización', 'representante', 'consentimiento'],
    help: {
      title: 'Autorización o representación',
      summary: 'Permite que la aplicación, asesoría o entidad actúe con datos de la explotación.',
      officialTerm: 'Representación / autorización de tratamiento de datos',
      why: 'Hace falta para importar REA o enviar información oficial cuando corresponda.',
      whereToFind: 'En la pantalla de autorización, sede electrónica o documento firmado por el titular.',
      nextStep: 'Lee bien qué autorizas antes de continuar.',
    },
  },
  {
    keywords: ['cue', 'cuaderno', 'quaderno'],
    help: {
      title: 'Cuaderno de explotación',
      summary: 'Recoge las actuaciones realizadas en la explotación, como tratamientos o fertilizaciones.',
      officialTerm: 'CUE / Cuaderno Digital de Explotación',
      why: 'Sirve para revisar, guardar o comunicar las actuaciones cuando la normativa lo exija.',
      whereToFind: 'Se genera desde Fincas cuando ya has registrado las tareas necesarias.',
      nextStep: 'Antes de enviarlo, comprueba finca, parcelas, productos, fechas, dosis y aplicadores.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; puede variar por norma sectorial y comunidad autónoma.`,
    },
  },
  {
    keywords: ['parcela', 'recinto', 'sigpac', 'referencia sigpac'],
    help: {
      title: 'Parcela o recinto de cultivo',
      summary: 'Es la zona concreta de terreno donde haces una labor o aplicas un producto.',
      officialTerm: 'SIGPAC / DGC cuando proceda',
      why: 'Si la parcela no está bien elegida, el cuaderno o el CUE pueden quedar mal.',
      whereToFind: 'En el mapa SIGPAC, en la declaración agraria o importando datos desde REA.',
      example: 'Polígono, parcela, recinto y cultivo correspondiente',
      nextStep: 'Si haces la misma actuación en varias parcelas, puedes agruparlas en un sector.',
    },
  },
  {
    keywords: ['dgc', 'declaracion grafica', 'declaración gráfica'],
    help: {
      title: 'Recinto concreto de cultivo',
      summary: 'Es el trozo de terreno donde hay un cultivo determinado y donde se registra la actuación.',
      officialTerm: 'DGC',
      why: 'Permite indicar con precisión dónde se ha hecho una tarea.',
      whereToFind: 'En los datos gráficos o declaraciones agrarias que correspondan a la explotación.',
      example: 'Un recinto de olivar dentro de una parcela SIGPAC',
    },
  },
  {
    keywords: ['sector', 'uha', 'unidad homogenea', 'unidad homogénea', 'grupo'],
    help: {
      title: 'Grupo de parcelas',
      summary: 'Agrupa parcelas que se trabajan igual para no repetir la misma tarea una por una.',
      officialTerm: 'Sector / UHA según el caso',
      why: 'Ahorra tiempo cuando varias parcelas tienen el mismo cultivo, riego, tratamiento o manejo.',
      whereToFind: 'Se crea desde el módulo Sectores o desde la pantalla equivalente de agrupación.',
      example: 'Sector Olivar regadío, Sector Cítricos norte',
      nextStep: 'Al crear una tarea, podrás seleccionar el sector en lugar de cada parcela.',
    },
  },
  {
    keywords: ['cultivo', 'variedad', 'producto vegetal'],
    help: {
      title: 'Cultivo y variedad',
      summary: 'Indica qué tienes sembrado o plantado y, si hace falta, la variedad.',
      officialTerm: 'Cultivo / variedad en catálogos oficiales',
      why: 'Ayuda a comprobar si un producto o tarea corresponde a esa parcela.',
      whereToFind: 'En la declaración agraria, en tus registros de campo o en la propia parcela.',
      example: 'Olivar arbequina, trigo duro, tomate pera',
    },
  },
  {
    keywords: ['producto fitosanitario', 'fitosanitario', 'vademecum', 'vademécum'],
    help: {
      title: 'Producto fitosanitario',
      summary: 'Es el producto usado contra plagas, enfermedades o malas hierbas.',
      officialTerm: 'Vademécum / Registro de Productos Fitosanitarios',
      why: 'Conviene revisar usos autorizados, cultivo, dosis, plazo de seguridad y condiciones de aplicación.',
      whereToFind: 'En el envase, factura o Vademécum oficial.',
      example: 'Herbicida autorizado para el cultivo indicado',
      nextStep: 'Antes de guardarlo en una tarea, confirma que la dosis y el cultivo son correctos.',
    },
  },
  {
    keywords: ['producto fertilizante', 'fertilizante', 'fertilizacion', 'fertilización', 'abono', 'abonado'],
    help: {
      title: 'Fertilizante o abono',
      summary: 'Es el producto que aporta nutrientes al cultivo.',
      officialTerm: 'Fertilización / abonado',
      why: 'Permite dejar constancia de qué se aplicó, cuánto, cuándo y dónde.',
      whereToFind: 'En el saco, etiqueta, factura o ficha del producto.',
      example: 'NPK, urea, compost, estiércol, corrector nutricional',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; puede variar según la normativa aplicable.`,
    },
  },
  {
    keywords: ['dosis', 'cantidad', 'kg', 'litro', 'l/ha', 'kg/ha', 'unidad', 'volumen'],
    help: {
      title: 'Dosis o cantidad aplicada',
      summary: 'Indica cuánto producto se ha aplicado.',
      officialTerm: 'Dosis / cantidad total',
      why: 'Es un dato sensible: se usa para comprobar consumos, cuaderno y posibles comunicaciones oficiales.',
      whereToFind: 'En la receta, recomendación técnica, etiqueta del producto o cálculo de campo.',
      example: '2 l/ha, 150 kg/ha o 25 kg totales',
      nextStep: 'Si dudas, revisa si el formulario pide dosis por hectárea o cantidad total.',
    },
  },
  {
    keywords: ['fecha', 'dia', 'día'],
    help: {
      title: 'Fecha de la actuación',
      summary: 'Es el día en que se realizó o se registró la labor.',
      why: 'Permite ordenar el cuaderno y justificar cuándo se hizo el trabajo.',
      whereToFind: 'En tus notas de campo, parte de trabajo o memoria de la aplicación.',
      example: '05/05/2026',
      nextStep: 'Escribe la fecha en el formato que muestra el campo.',
    },
  },
  {
    keywords: ['hora'],
    help: {
      title: 'Hora de trabajo',
      summary: 'Sirve para indicar cuándo empezó o terminó la actuación.',
      why: 'Puede ayudar a justificar condiciones de aplicación o turnos de trabajo.',
      whereToFind: 'En el parte de trabajo o en la planificación de la labor.',
      example: '08:30',
    },
  },
  {
    keywords: ['trabajador', 'aplicador', 'responsable', 'operario', 'asesor'],
    help: {
      title: 'Persona que participa en la tarea',
      summary: 'Indica quién ha realizado, supervisado o asesorado la actuación.',
      officialTerm: 'Aplicador / asesor / responsable',
      why: 'Da trazabilidad y permite saber quién hizo cada trabajo.',
      whereToFind: 'En el listado de trabajadores o aplicadores de la explotación.',
      example: 'Aplicador con carné o asesor técnico autorizado',
    },
  },
  {
    keywords: ['ropo', 'numropo'],
    help: {
      title: 'Número profesional fitosanitario',
      summary: 'Identifica a la persona o empresa autorizada en el ámbito fitosanitario.',
      officialTerm: 'NumROPO',
      why: 'Puede ser necesario para justificar quién aplica o asesora un tratamiento fitosanitario.',
      whereToFind: 'En el carné, certificado, documentación profesional o registro ROPO.',
      nextStep: 'Si no lo sabes, revisa la documentación del aplicador antes de enviar el registro.',
    },
  },
  {
    keywords: ['maquinaria', 'maquina', 'máquina', 'tractor', 'equipo', 'apero'],
    help: {
      title: 'Maquinaria o equipo utilizado',
      summary: 'Indica con qué máquina o equipo se hizo la labor.',
      officialTerm: 'Equipo de aplicación / maquinaria agrícola',
      why: 'Ayuda a documentar cómo se hizo la aplicación o trabajo en campo.',
      whereToFind: 'En el listado de maquinaria de la explotación o en la documentación del equipo.',
      example: 'Tractor, atomizador, cuba, abonadora',
    },
  },
  {
    keywords: ['roma', 'numroma'],
    help: {
      title: 'Número oficial de la máquina',
      summary: 'Identifica una máquina inscrita en el registro de maquinaria agrícola.',
      officialTerm: 'NumROMA',
      why: 'Puede ser necesario para documentar equipos de aplicación o maquinaria oficial.',
      whereToFind: 'En la documentación de la máquina o registro ROMA.',
      nextStep: 'Si la máquina no es inscribible, revisa qué alternativa permite el formulario.',
    },
  },
  {
    keywords: ['plaga', 'enfermedad', 'malas hierbas', 'mala hierba', 'problema', 'justificacion', 'justificación'],
    help: {
      title: 'Problema tratado',
      summary: 'Explica por qué se ha hecho el tratamiento.',
      officialTerm: 'Justificación de la actuación',
      why: 'Sirve para justificar el uso del producto y dejar constancia de la necesidad del tratamiento.',
      whereToFind: 'En la observación de campo, recomendación técnica o aviso fitosanitario.',
      example: 'Repilo, pulgón, malas hierbas, mildiu',
    },
  },
  {
    keywords: ['fenologico', 'fenológico', 'estado fenologico', 'estado fenológico'],
    help: {
      title: 'Estado del cultivo',
      summary: 'Describe en qué fase estaba el cultivo cuando se hizo la actuación.',
      officialTerm: 'Estado fenológico',
      why: 'Algunos productos o labores dependen del momento de desarrollo del cultivo.',
      whereToFind: 'En la observación del cultivo o recomendación técnica.',
      example: 'Floración, brotación, maduración',
    },
  },
  {
    keywords: ['meteorologia', 'meteorología', 'temperatura', 'viento', 'humedad', 'lluvia'],
    help: {
      title: 'Condiciones meteorológicas',
      summary: 'Recoge el tiempo que había durante la aplicación.',
      why: 'Ayuda a justificar que el tratamiento se hizo en condiciones adecuadas.',
      whereToFind: 'En la estación meteorológica, parte de campo o aplicación del tiempo.',
      example: 'Sin viento fuerte, sin lluvia, temperatura moderada',
    },
  },
  {
    keywords: ['riego', 'fertirrigacion', 'fertirrigación', 'agua'],
    help: {
      title: 'Riego o fertirrigación',
      summary: 'Indica cómo se ha regado o si se han aplicado nutrientes con el agua.',
      officialTerm: 'Riego / fertirrigación',
      why: 'Puede formar parte del cuaderno y ayuda a controlar el manejo del cultivo.',
      whereToFind: 'En los registros de riego, contador, programador o parte de campo.',
      example: 'Goteo, aspersión, riego por gravedad',
    },
  },
];


const SECTORES_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['id_finca', 'finca'],
    help: {
      title: 'Finca del sector',
      summary: 'Elige la finca o explotación a la que pertenece este sector.',
      officialTerm: 'Explotación / finca',
      why: 'Sirve para no mezclar sectores de distintas fincas y para aplicar tareas sobre el grupo correcto de parcelas.',
      whereToFind: 'En el listado de fincas o explotaciones ya creadas en la aplicación.',
      example: 'Finca Las Lomas',
      nextStep: 'Después de elegir la finca, pon un nombre claro al sector y revisa los datos de riego si aparecen.',
    },
  },
  {
    keywords: ['nombre sector', 'nombre del sector', 'nombre grupo', 'nombre agrupación', 'nombre agrupacion'],
    help: {
      title: 'Nombre del sector',
      summary: 'Es el nombre con el que reconocerás este sector o grupo de parcelas dentro de la finca.',
      officialTerm: 'Sector / grupo de parcelas',
      why: 'Ayuda a elegir el grupo correcto al apuntar tratamientos, fertilizaciones, riegos u otras tareas.',
      whereToFind: 'Puedes usar el nombre que ya utilizas en el campo o en tus notas.',
      example: 'Olivar norte, Cítricos goteo 1, Invernadero 2, Parcela baja',
      nextStep: 'Usa un nombre corto y fácil de reconocer.',
    },
  },
  {
    keywords: ['id_jardin', 'jardin', 'jardín'],
    help: {
      title: 'Jardín o zona ajardinada',
      summary: 'Elige el jardín al que pertenece este sector.',
      officialTerm: 'Jardín / zona de mantenimiento',
      why: 'Sirve para organizar sectores dentro de un jardín o zona concreta.',
      whereToFind: 'En el listado de jardines creados en la aplicación.',
      example: 'Jardín principal, Zona piscina, Jardín norte',
      nextStep: 'Después podrás diferenciar mejor los trabajos por zona.',
    },
  },
  {
    keywords: ['partida'],
    help: {
      title: 'Partida o zona de campo',
      summary: 'Indica el nombre tradicional o local de la zona donde está el sector.',
      officialTerm: 'Partida',
      why: 'Ayuda a ubicar el sector con nombres conocidos por el agricultor o la cuadrilla.',
      whereToFind: 'En tus notas, documentación de la finca o forma habitual de nombrar la zona.',
      example: 'La Solana, El Pla, Camino Viejo',
    },
  },
  {
    keywords: [
      'goteros',
      'nº goteros',
      'numero goteros',
      'número goteros',
      'nº goteros/árbol',
      'nº goteros/arbol',
      'goteros árbol',
      'goteros arbol',
    ],
    help: {
      title: 'Goteros por árbol',
      summary: 'Indica cuántos goteros riegan cada árbol o planta.',
      officialTerm: 'Número de emisores por planta',
      why: 'Sirve para calcular cuánta agua recibe cada planta durante el riego.',
      whereToFind: 'Contando los goteros que llegan a un árbol o revisando el diseño de la instalación de riego.',
      example: '2 goteros por árbol, 4 goteros por árbol',
      nextStep: 'Después revisa el caudal de cada gotero en litros por hora.',
    },
  },
  {
    keywords: ['caudal_gotero', 'caudal gotero', 'caudal gotero (l/h)', 'caudal del gotero', 'l/h gotero'],
    help: {
      title: 'Agua que da cada gotero',
      summary: 'Indica cuántos litros por hora aporta cada gotero.',
      officialTerm: 'Caudal del gotero (l/h)',
      why: 'Con este dato y el número de goteros se puede estimar el agua que recibe cada planta.',
      whereToFind: 'En la ficha del gotero, en el diseño del riego o midiendo el agua que sale durante una hora.',
      example: '2 l/h, 4 l/h, 8 l/h',
      nextStep: 'Escribe solo el número si el campo ya indica la unidad l/h.',
    },
  },
  {
    keywords: ['caudal_cuba', 'capacidad cuba', 'capacidad cuba (l)', 'cuba', 'litros cuba'],
    help: {
      title: 'Capacidad de la cuba',
      summary: 'Indica cuántos litros caben en la cuba o depósito que usas.',
      officialTerm: 'Capacidad de cuba (l)',
      why: 'Ayuda a calcular mezclas, aplicaciones y número de cargas necesarias.',
      whereToFind: 'En la placa, documentación de la cuba o capacidad indicada por el fabricante.',
      example: '600 l, 1000 l, 2000 l',
      nextStep: 'Escribe la capacidad total en litros.',
    },
  },
  {
    keywords: ['caudal_inyectora', 'caudal inyectora', 'caudal inyectora (l/h)', 'inyectora', 'inyector', 'l/h inyectora'],
    help: {
      title: 'Caudal de la inyectora',
      summary: 'Indica cuántos litros por hora aporta la inyectora al sistema de riego.',
      officialTerm: 'Caudal de inyectora (l/h)',
      why: 'Sirve para controlar cuánto producto o solución entra en el riego.',
      whereToFind: 'En la ficha técnica de la inyectora, configuración del equipo o medición de campo.',
      example: '50 l/h, 100 l/h',
      nextStep: 'Comprueba que el valor corresponde al caudal real usado en la instalación.',
    },
  },
  {
    keywords: ['distancia_goteros', 'distancia goteros', 'distancia entre goteros'],
    help: {
      title: 'Distancia entre goteros',
      summary: 'Indica la separación que hay entre un gotero y el siguiente.',
      officialTerm: 'Separación entre emisores',
      why: 'Ayuda a entender cómo está repartido el riego en la línea.',
      whereToFind: 'En el diseño de la instalación, ficha de la tubería o midiendo la distancia en campo.',
      example: '0,50 m, 0,75 m, 1 m',
      nextStep: 'Usa la misma unidad que indique el formulario o el listado.',
    },
  },
  {
    keywords: ['sup_cultivada', 's. cultivada', 's cultivada', 'superficie cultivada', 'superficie sector'],
    help: {
      title: 'Superficie cultivada del sector',
      summary: 'Indica la parte del sector que realmente está cultivada.',
      officialTerm: 'Superficie cultivada',
      why: 'Sirve para calcular trabajos, riegos, fertilizaciones y consumos de forma más ajustada.',
      whereToFind: 'En la medición de la parcela, SIGPAC, REA, declaración agraria o datos internos de la finca.',
      example: '1,25 ha',
      nextStep: 'No pongas la superficie total si solo una parte está cultivada.',
    },
  },
  {
    keywords: ['total_plantas', 'nº plantas', 'numero plantas', 'número plantas', 'total plantas'],
    help: {
      title: 'Número total de plantas',
      summary: 'Muestra cuántas plantas o árboles hay en este sector.',
      officialTerm: 'Total de plantas',
      why: 'Ayuda a calcular riego, tratamientos, fertilización y previsiones de trabajo.',
      whereToFind: `Puede venir calculado por la aplicación a partir de la superficie y el marco de plantación,
o estar registrado en los datos del sector.`,
      example: '350 plantas, 1200 árboles',
      nextStep: 'Si no puedes editarlo, revisa los campos de superficie o plantación de los que se calcula.',
    },
  },
];


const PARCELAS_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['id_finca', 'finca'],
    help: {
      title: 'Finca de la parcela',
      summary: 'Elige la finca o explotación a la que pertenece esta parcela.',
      officialTerm: 'Explotación / finca',
      why: 'Sirve para no mezclar parcelas de distintas fincas y para que los trabajos se apunten en el lugar correcto.',
      whereToFind: 'En el listado de fincas ya creadas en la aplicación.',
      example: 'Finca Las Lomas',
      nextStep: 'Después puedes elegir un sector si esta parcela pertenece a un grupo de trabajo.',
    },
  },
  {
    keywords: ['id_sector', 'sector'],
    help: {
      title: 'Sector o grupo de trabajo',
      summary: 'Elige el sector donde está incluida esta parcela, si usas sectores.',
      officialTerm: 'Sector / grupo de parcelas',
      why: 'Ayuda a juntar parcelas que se trabajan igual y evita repetir tareas una por una.',
      whereToFind: 'En el módulo Sectores, dentro de la finca seleccionada.',
      example: 'Sector Olivar norte, Cítricos goteo 1',
      nextStep: 'Si no usas sectores, puedes dejarlo sin seleccionar.',
    },
  },
  {
    keywords: ['nombre parcela', 'nombre de parcela', 'nombre de la parcela'],
    help: {
      title: 'Nombre de la parcela',
      summary: 'Es el nombre fácil con el que reconocerás esta parcela en la aplicación.',
      officialTerm: 'Nombre interno de parcela',
      why: 'Sirve para encontrarla rápido cuando apuntes tratamientos, riegos, fertilizaciones o visitas.',
      whereToFind: 'Puedes usar el nombre habitual que empleas en campo.',
      example: 'Olivar casa, Parcela 12, Cítricos norte',
      nextStep: 'Pon un nombre corto y claro.',
    },
  },
  {
    keywords: ['numero_parcela_agricola', 'número de parcela agrícola', 'numero de parcela agricola'],
    help: {
      title: 'Número de parcela agrícola',
      summary: 'Es un número interno o agrícola para identificar esta parcela.',
      officialTerm: 'Número de parcela agrícola',
      why: 'Ayuda a ordenar parcelas y a distinguirlas cuando tienen nombres parecidos.',
      whereToFind: 'En tus registros internos, documentación de la explotación o numeración usada por la empresa.',
      example: '12, 25, 103',
    },
  },
  {
    keywords: ['pendiente', '% pendiente'],
    help: {
      title: 'Pendiente de la parcela',
      summary: 'Indica si el terreno tiene inclinación y cuánto representa en porcentaje.',
      officialTerm: 'Pendiente (%)',
      why: 'Puede influir en riego, erosión, manejo del suelo y aplicación de productos.',
      whereToFind: 'En mediciones de campo, mapas técnicos, SIGPAC si está disponible o estimación de la finca.',
      example: '5 %, 12 %',
    },
  },
  {
    keywords: ['id_cultivo', 'cultivo siex', 'género/especie siex', 'genero/especie siex', 'especie siex'],
    help: {
      title: 'Cultivo oficial',
      summary: 'Indica qué cultivo hay en esta parcela usando el listado oficial.',
      officialTerm: 'Cultivo SIEX',
      why: 'Sirve para relacionar la parcela con datos oficiales, tratamientos, fertilizaciones y cuaderno.',
      whereToFind: 'En la declaración agraria, REA, SIGPAC o datos oficiales cargados en la aplicación.',
      example: 'Olivar, almendro, trigo duro, tomate',
      nextStep: 'Después selecciona la variedad si el formulario la pide.',
    },
  },
  {
    keywords: ['id_variedad', 'variedad siex'],
    help: {
      title: 'Variedad oficial',
      summary: 'Indica la variedad del cultivo usando el listado oficial.',
      officialTerm: 'Variedad SIEX',
      why: 'Ayuda a concretar mejor el cultivo y puede ser necesaria para ciertos registros o controles.',
      whereToFind: 'En la declaración agraria, ficha de la parcela, planta comprada o datos del cultivo.',
      example: 'Arbequina, Marcona, Cabernet, tomate pera',
      nextStep: 'Si no aparece la variedad, revisa primero que el cultivo elegido sea correcto.',
    },
  },
  {
    keywords: ['id_cultivo_legacy', 'cultivo_legacy', 'cultivo', 'género/especie', 'genero/especie', 'especie'],
    help: {
      title: 'Cultivo propio',
      summary: 'Indica qué cultivo tienes en la parcela usando el listado propio de la aplicación.',
      officialTerm: 'Cultivo',
      why: 'Sirve para trabajar con cultivos usados por la empresa aunque no vengan del listado SIEX.',
      whereToFind: 'En tus registros internos, fichas de cultivo o datos habituales de campo.',
      example: 'Olivar, viña, cítricos, lechuga',
      nextStep: 'Después selecciona la variedad si corresponde.',
    },
  },
  {
    keywords: ['id_variedad_legacy', 'variedad_legacy', 'variedad'],
    help: {
      title: 'Variedad del cultivo',
      summary: 'Indica la variedad concreta del cultivo seleccionado.',
      officialTerm: 'Variedad',
      why: 'Ayuda a diferenciar parcelas con el mismo cultivo pero distinto manejo o producción.',
      whereToFind: 'En la ficha de plantación, vivero, factura de planta o registros de campo.',
      example: 'Arbequina, Navelina, Marcona, Roma',
    },
  },
  {
    keywords: ['marco', 'marco plantación', 'marco plantacion', 'filas x plantas'],
    help: {
      title: 'Marco de plantación',
      summary: 'Indica la distancia o distribución entre filas y plantas.',
      officialTerm: 'Marco de plantación',
      why: 'Ayuda a calcular número de plantas, densidad, riego y necesidades de tratamiento.',
      whereToFind: 'En el diseño de plantación, medición de campo o ficha técnica de la parcela.',
      example: '6 x 4, 7 x 5, 3,5 x 1,5',
      nextStep: 'Usa siempre el mismo orden que muestra el campo: filas x plantas.',
    },
  },
  {
    keywords: ['nº cintas riego', 'numero cintas riego', 'número cintas riego'],
    help: {
      title: 'Número de cintas de riego',
      summary: 'Indica cuántas líneas o cintas de riego tiene la parcela.',
      officialTerm: 'Cintas de riego',
      why: 'Sirve para entender cómo está montado el riego y calcular necesidades de agua o fertirrigación.',
      whereToFind: 'Contando las cintas en campo o revisando el diseño de la instalación.',
      example: '1 cinta por cama, 2 cintas por línea',
    },
  },
  {
    keywords: ['pies'],
    help: {
      title: 'Pies de la planta',
      summary: 'Indica el número de pies o troncos principales de la planta, cuando se controla ese dato.',
      officialTerm: 'Pies',
      why: 'Puede afectar al manejo, poda, producción y cálculo de tratamientos.',
      whereToFind: 'Observando la planta en campo o en la ficha de cultivo.',
      example: '1 pie, 2 pies',
    },
  },
  {
    keywords: ['anyo_plantacion', 'año plantación', 'ano plantacion', 'semana plantación', 'semana plantacion'],
    help: {
      title: 'Fecha de plantación',
      summary: 'Indica cuándo se plantó el cultivo.',
      officialTerm: 'Año o semana de plantación',
      why: 'Ayuda a conocer la edad del cultivo y a planificar trabajos, producción y tratamientos.',
      whereToFind: 'En la ficha de plantación, factura de planta, cuaderno o memoria de campo.',
      example: '2020, Semana 12',
    },
  },
  {
    keywords: ['plantas', 'nº árboles', 'nº arboles', 'numero arboles', 'número árboles', 'kg/ha'],
    help: {
      title: 'Plantas, árboles o densidad',
      summary: 'Indica cuántas plantas hay o, en algunos cultivos, el valor de kilos por hectárea.',
      officialTerm: 'Número de plantas / densidad',
      why: 'Sirve para calcular riego, fertilización, tratamientos y previsión de producción.',
      whereToFind: 'En el conteo de campo, marco de plantación, ficha de la parcela o datos de siembra.',
      example: '350 árboles, 1200 plantas, 25 kg/ha',
      nextStep: 'Revisa la etiqueta del campo, porque según la empresa puede pedir plantas o kg/ha.',
    },
  },
  {
    keywords: ['patron', 'patrón', 'portainjertos'],
    help: {
      title: 'Patrón o portainjerto',
      summary: 'Indica la base o portainjerto sobre el que está injertada la variedad.',
      officialTerm: 'Patrón / portainjerto',
      why: 'Ayuda a conocer vigor, resistencia, manejo y comportamiento del cultivo.',
      whereToFind: 'En la factura del vivero, etiqueta de planta o ficha de plantación.',
      example: 'Carrizo, Macrophylla, R-110, pie franco',
    },
  },
  {
    keywords: ['formato'],
    help: {
      title: 'Formato',
      summary: 'Indica la forma de presentación, tamaño o tipo comercial de la planta o producto.',
      officialTerm: 'Formato',
      why: 'Ayuda a diferenciar materiales o presentaciones dentro de vivero o producción.',
      whereToFind: 'En la ficha del producto, vivero, pedido o etiqueta.',
      example: 'Bandeja, maceta, alveolo, formato comercial',
    },
  },
  {
    keywords: ['cama'],
    help: {
      title: 'Cama de cultivo',
      summary: 'Indica la cama o línea de cultivo donde está situada la parcela o plantación.',
      officialTerm: 'Cama',
      why: 'Ayuda a organizar cultivos hortícolas o intensivos por zonas de trabajo.',
      whereToFind: 'En el plano de la finca, organización interna o identificación en campo.',
      example: 'Cama 1, Cama norte',
    },
  },
  {
    keywords: ['diametro_copa', 'diámetro de copa', 'diametro de copa'],
    help: {
      title: 'Diámetro de copa',
      summary: 'Indica el ancho aproximado de la copa del árbol.',
      officialTerm: 'Diámetro de copa',
      why: 'Puede ayudar a estimar volumen de vegetación, necesidades de tratamiento o estado del cultivo.',
      whereToFind: 'Midiendo de lado a lado la copa del árbol.',
      example: '2,5 m, 4 m',
    },
  },
  {
    keywords: ['captacion_agua', 'captación agua', 'captacion agua'],
    help: {
      title: 'Captación de agua',
      summary: 'Marca si la parcela tiene o usa una captación de agua.',
      officialTerm: 'Captación de agua',
      why: 'Ayuda a identificar el origen o punto desde donde se toma agua para riego.',
      whereToFind: 'En la documentación de riego, concesión, pozo, balsa o instalación de campo.',
      example: 'Pozo, balsa, toma de comunidad de regantes',
    },
  },
  {
    keywords: ['zonas_especificas', 'zonas específicas', 'zonas especificas'],
    help: {
      title: 'Zonas específicas',
      summary: 'Marca si la parcela está dentro de una zona con condiciones especiales.',
      officialTerm: 'Zona específica',
      why: 'Puede afectar a restricciones, controles, fertilización, tratamientos o manejo.',
      whereToFind: 'En mapas oficiales, documentación de la explotación o información de la entidad.',
      nextStep: 'Si marcas este campo, revisa si la zona afecta total o parcialmente a la parcela.',
    },
  },
  {
    keywords: ['es_zona_vulnerable', 'zona vulnerable'],
    help: {
      title: 'Zona vulnerable',
      summary: 'Marca si la parcela está en una zona vulnerable por contaminación de nitratos u otra condición ambiental.',
      officialTerm: 'Zona vulnerable',
      why: 'Puede afectar a fertilización, dosis, periodos de aplicación y documentación necesaria.',
      whereToFind: 'En mapas o resoluciones de tu comunidad autónoma, asesoría o datos de la explotación.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; puede variar según comunidad autónoma.`,
    },
  },
  {
    keywords: ['zona_vulnerable', 'zona vulnerable ha', 'zona vulnerable (ha)'],
    help: {
      title: 'Superficie en zona vulnerable',
      summary: 'Indica cuánta superficie de la parcela está dentro de una zona vulnerable.',
      officialTerm: 'Zona vulnerable (ha)',
      why: 'Ayuda a calcular qué parte de la parcela puede tener condiciones especiales de fertilización o manejo.',
      whereToFind: 'En mapas oficiales, visor de la comunidad autónoma o medición técnica.',
      example: '0,75 ha',
    },
  },
  {
    keywords: ['herbaceo_lenyoso', 'herbáceo / leñoso', 'herbaceo / lenyoso', 'herbáceo', 'leñoso'],
    help: {
      title: 'Tipo de cultivo: herbáceo o leñoso',
      summary: 'Indica si el cultivo es anual o de planta permanente.',
      officialTerm: 'Herbáceo / leñoso',
      why: 'Ayuda a clasificar el cultivo y aplicar manejos o controles adecuados.',
      whereToFind: 'Según el cultivo real de la parcela.',
      example: 'Trigo: herbáceo. Olivar: leñoso.',
    },
  },
  {
    keywords: ['producto'],
    help: {
      title: 'Producto de la parcela',
      summary: 'Indica el producto o cultivo comercial asociado a esta parcela.',
      officialTerm: 'Producto',
      why: 'Sirve para trazabilidad, producción y organización comercial.',
      whereToFind: 'En la planificación de cultivo, parte de producción o datos comerciales.',
      example: 'Tomate pera, naranja, uva de mesa',
    },
  },
  {
    keywords: ['prevision_produccion', 'previsión producción', 'prevision produccion'],
    help: {
      title: 'Previsión de producción',
      summary: 'Indica cuánto se espera producir en esta parcela.',
      officialTerm: 'Previsión de producción',
      why: 'Ayuda a planificar recolección, ventas, logística y seguimiento del cultivo.',
      whereToFind: 'En estimaciones de campo, campañas anteriores o previsión técnica.',
      example: '12.000 kg, 25 t/ha',
    },
  },
  {
    keywords: ['codigo_trazabilidad', 'código de trazabilidad', 'codigo de trazabilidad'],
    help: {
      title: 'Código de trazabilidad',
      summary: 'Es una referencia para seguir el origen y recorrido del producto.',
      officialTerm: 'Código de trazabilidad',
      why: 'Ayuda a relacionar la parcela con lotes, cosecha, almacén o venta.',
      whereToFind: 'En el sistema interno de trazabilidad, albaranes, etiquetas o planificación de campaña.',
      example: 'TRZ-2026-001, PAR12-NARANJA',
    },
  },
  {
    keywords: ['lote', 'nº lote', 'numero lote', 'número lote'],
    help: {
      title: 'Número de lote',
      summary: 'Identifica un conjunto de producto, planta o producción asociado a esta parcela.',
      officialTerm: 'Lote',
      why: 'Sirve para trazabilidad, control de producción y seguimiento de incidencias.',
      whereToFind: 'En etiquetas, albaranes, pedidos, vivero o sistema interno.',
      example: 'L-2026-15',
    },
  },
  {
    keywords: ['pozo', 'pozos'],
    help: {
      title: 'Pozo',
      summary: 'Indica el pozo o pozos relacionados con el riego de la parcela.',
      officialTerm: 'Pozo / punto de captación',
      why: 'Ayuda a saber de dónde viene el agua usada en la parcela.',
      whereToFind: 'En la documentación de riego, concesión, comunidad de regantes o plano de la finca.',
      example: 'Pozo norte, Pozo 2',
    },
  },
  {
    keywords: ['rios', 'ríos'],
    help: {
      title: 'Ríos o cauces cercanos',
      summary: 'Indica si hay ríos o cauces relacionados con la parcela.',
      officialTerm: 'Cauce / río',
      why: 'Puede ser importante para riego, protección ambiental o restricciones de aplicación.',
      whereToFind: 'En mapas, documentación de la finca o revisión en campo.',
      example: 'Río cercano, barranco, acequia principal',
    },
  },
  {
    keywords: ['estacion_control', 'estación de control', 'estacion de control'],
    help: {
      title: 'Estación de control',
      summary: 'Indica la estación o punto de control asociado a la parcela.',
      officialTerm: 'Estación de control',
      why: 'Sirve para relacionar la parcela con mediciones, seguimiento o controles técnicos.',
      whereToFind: 'En la red de control, documentación de la entidad o datos internos.',
      example: 'Estación 3, Control norte',
    },
  },
  {
    keywords: ['regimen_tenencia', 'régimen tenencia', 'regimen tenencia'],
    help: {
      title: 'Régimen de tenencia',
      summary: 'Indica si la parcela es propia, arrendada, cedida o trabajada con otro acuerdo.',
      officialTerm: 'Régimen de tenencia',
      why: 'Aclara la relación del titular con la parcela.',
      whereToFind: 'En escrituras, contrato de arrendamiento, cesión o documentación interna.',
      example: 'Propiedad, arrendamiento, cesión',
    },
  },
  {
    keywords: ['produccion_ecologica', 'producción ecológica', 'produccion ecologica'],
    help: {
      title: 'Producción ecológica',
      summary: 'Marca si esta parcela se trabaja bajo producción ecológica.',
      officialTerm: 'Producción ecológica',
      why: 'Puede afectar a productos permitidos, controles, certificación y trazabilidad.',
      whereToFind: 'En el certificado ecológico, organismo de control o documentación de campaña.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; revisa siempre la certificación aplicable.`,
    },
  },
  {
    keywords: ['uds_bandeja', 'uds. bandeja', 'unidades bandeja'],
    help: {
      title: 'Unidades por bandeja',
      summary: 'Indica cuántas unidades contiene cada bandeja.',
      officialTerm: 'Unidades por bandeja',
      why: 'Ayuda a controlar producción, vivero, pedidos o existencias.',
      whereToFind: 'En la ficha del vivero, etiqueta, pedido o formato de producción.',
      example: '40 uds, 60 uds, 104 uds',
    },
  },
  {
    keywords: ['distancia_menor_50', 'distancia menor a 50m', 'distancia menor a 50 m'],
    help: {
      title: 'Distancia menor a 50 metros',
      summary: 'Marca si la parcela está a menos de 50 metros del punto o elemento que se controla.',
      officialTerm: 'Distancia menor a 50 m',
      why: 'Puede activar datos adicionales o restricciones según el control que aplique.',
      whereToFind: 'Midiendo en campo, sobre plano o con visor cartográfico.',
      nextStep: 'Si lo marcas, indica después el número de metros si aparece el campo.',
    },
  },
  {
    keywords: ['numero_metros', 'número de metros', 'numero de metros'],
    help: {
      title: 'Número de metros',
      summary: 'Indica la distancia aproximada en metros.',
      officialTerm: 'Distancia en metros',
      why: 'Sirve para concretar una distancia cuando la parcela está cerca de un punto sensible o elemento controlado.',
      whereToFind: 'Midiendo en campo, sobre plano, SIGPAC o visor cartográfico.',
      example: '25 m, 40 m',
    },
  },
  {
    keywords: ['tipo_siembra', 'tipo de siembra'],
    help: {
      title: 'Tipo de siembra',
      summary: 'Indica cómo se ha sembrado o implantado el cultivo.',
      officialTerm: 'Tipo de siembra',
      why: 'Ayuda a describir mejor el manejo del cultivo y la forma de implantación.',
      whereToFind: 'En la planificación de siembra o parte de trabajo.',
      example: 'Directa, trasplante, siembra convencional',
    },
  },
  {
    keywords: ['aplicadores', 'asesor'],
    help: {
      title: 'Aplicadores o asesor',
      summary: 'Selecciona las personas que aplican productos o asesoran esta parcela.',
      officialTerm: 'Aplicador / asesor',
      why: 'Da trazabilidad y permite saber quién participa en tratamientos o asesoramiento.',
      whereToFind: 'En el listado de aplicadores, asesores o trabajadores autorizados.',
      example: 'Aplicador con carné, asesor técnico',
    },
  },
  {
    keywords: ['id_responsable_asignado', 'responsable_nombre', 'grabador'],
    help: {
      title: 'Grabador',
      summary: 'Es la persona encargada de introducir o revisar los datos de esta parcela.',
      officialTerm: 'Grabador / responsable asignado',
      why: 'Permite saber quién gestiona la información dentro de la aplicación.',
      whereToFind: 'En el listado de usuarios o responsables con acceso a la plataforma.',
      example: 'Responsable de campo, técnico de la entidad',
    },
  },
  {
    keywords: ['id_comunidad_regantes', 'comunidad_regantes', 'comunidad de regantes'],
    help: {
      title: 'Comunidad de regantes',
      summary: 'Indica la comunidad de regantes a la que pertenece la parcela.',
      officialTerm: 'Comunidad de regantes',
      why: 'Ayuda a relacionar la parcela con su organización de riego y origen de agua.',
      whereToFind: 'En recibos, documentación de riego o datos de la propia comunidad.',
      example: 'Comunidad de Regantes de la Acequia Mayor',
    },
  },
  {
    keywords: ['activo'],
    help: {
      title: 'Parcela activa',
      summary: 'Indica si esta parcela se está usando actualmente.',
      officialTerm: 'Estado activo',
      why: 'Permite ocultar o separar parcelas que ya no se trabajan sin perder su historial.',
      nextStep: 'Desactívala solo si ya no quieres usarla en nuevos trabajos.',
    },
  },
  {
    keywords: ['codigo_procedencia_agua', 'procedencia_agua', 'procedencia agua', 'origen agua'],
    help: {
      title: 'Origen del agua',
      summary: 'Indica de dónde procede el agua usada para regar esta parcela.',
      officialTerm: 'Procedencia del agua / origen de riego',
      why: 'Ayuda a documentar el riego y su relación con la explotación.',
      whereToFind: 'En la comunidad de regantes, pozo, concesión, balsa o documentación de riego.',
      example: 'Pozo, comunidad de regantes, agua superficial, balsa',
    },
  },
  {
    keywords: ['linde'],
    help: {
      title: 'Linde',
      summary: 'Marca si la parcela o actuación afecta a una zona de borde o límite.',
      officialTerm: 'Linde',
      why: 'Puede ser importante para tratamientos, malas hierbas, protección ambiental o separación con otras parcelas.',
      whereToFind: 'Observando el límite de la parcela en campo o sobre el mapa.',
      example: 'Tratamiento solo en lindes',
    },
  },
  {
    keywords: ['gip'],
    help: {
      title: 'Gestión Integrada de Plagas',
      summary: 'Marca si la parcela se gestiona siguiendo criterios de Gestión Integrada de Plagas.',
      officialTerm: 'GIP',
      why: 'Ayuda a justificar que se aplican medidas de control razonadas antes de tratar.',
      whereToFind: 'En el asesoramiento técnico, cuaderno, certificación o sistema de gestión de la explotación.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}.`,
    },
  },
  {
    keywords: ['masas_agua', 'masas de agua superficie', 'masas de agua superficial'],
    help: {
      title: 'Masas de agua superficial',
      summary: 'Marca si hay masas de agua superficial cercanas o relacionadas con la parcela.',
      officialTerm: 'Masas de agua superficial',
      why: 'Puede afectar a precauciones ambientales, aplicación de productos o gestión del riego.',
      whereToFind: 'En mapas oficiales, visor cartográfico, documentación ambiental o revisión en campo.',
      example: 'Río, acequia, balsa, canal',
    },
  },
  {
    keywords: ['puntos_agua', 'puntos de extracción de agua', 'consumo humano'],
    help: {
      title: 'Puntos de extracción de agua para consumo humano',
      summary: 'Marca si hay puntos de extracción de agua para consumo humano cerca o dentro de la zona controlada.',
      officialTerm: 'Punto de extracción de agua',
      why: 'Puede implicar precauciones especiales para proteger el agua.',
      whereToFind: 'En mapas oficiales, documentación de captaciones o información de la entidad gestora.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; revisa la normativa aplicable en tu zona.`,
    },
  },
  {
    keywords: ['codigo_DGC', 'codigo dgc', 'código dgc', 'dgc'],
    help: {
      title: 'Código DGC',
      summary: 'Identifica el recinto o delimitación gráfica de cultivo asociado a esta parcela.',
      officialTerm: 'DGC',
      why: 'Sirve para relacionar la parcela con datos oficiales REA/SIEX cuando procede.',
      whereToFind: 'Normalmente lo recupera la aplicación desde datos oficiales o importados.',
      nextStep: 'Si no puedes editarlo, es porque viene de una fuente oficial o calculada.',
    },
  },
  {
    keywords: ['notas'],
    help: {
      title: 'Notas de la parcela',
      summary: 'Campo libre para escribir aclaraciones útiles sobre esta parcela.',
      why: 'Ayuda a recordar detalles que no encajan en otros campos.',
      example: 'Zona con mala nascencia, revisar riego en verano.',
    },
  },
  {
    keywords: ['partida'],
    help: {
      title: 'Partida',
      summary: 'Indica el nombre tradicional o local de la zona donde está la parcela.',
      officialTerm: 'Partida',
      why: 'Ayuda a ubicar la parcela con nombres conocidos por el agricultor o la cuadrilla.',
      whereToFind: 'En tus notas, documentación de la finca o forma habitual de nombrar la zona.',
      example: 'La Solana, El Pla, Camino Viejo',
    },
  },
  {
    keywords: ['comercio'],
    help: {
      title: 'Comercio o destino',
      summary: 'Indica el comercio, cliente o destino relacionado con esta parcela.',
      officialTerm: 'Comercio',
      why: 'Ayuda a organizar producción, ventas o entregas por parcela.',
      whereToFind: 'En tus acuerdos comerciales, pedidos o listado de comercios.',
      example: 'Cooperativa, almacén, cliente mayorista',
    },
  },
  {
    keywords: ['turbo'],
    help: {
      title: 'Turbo',
      summary: 'Marca si esta parcela se trabaja con equipo turbo o sistema equivalente.',
      officialTerm: 'Turbo',
      why: 'Ayuda a diferenciar el tipo de equipo o manejo usado en la parcela.',
      whereToFind: 'En la maquinaria o forma habitual de aplicación.',
      example: 'Turbo Sí si se usa atomizador o equipo turbo.',
    },
  },
  {
    keywords: ['uso_sigpac', 'uso sigpac'],
    help: {
      title: 'Uso SIGPAC',
      summary: 'Indica el uso oficial que SIGPAC asigna al recinto.',
      officialTerm: 'Uso SIGPAC',
      why: 'Ayuda a comprobar si la información oficial coincide con el uso real de la parcela.',
      whereToFind: 'En el visor SIGPAC o importando datos oficiales.',
      example: 'TA, OV, FY, VI según catálogo SIGPAC',
    },
  },
  {
    keywords: ['superficie_sigpac', 'superficie sigpac'],
    help: {
      title: 'Superficie SIGPAC',
      summary: 'Es la superficie oficial del recinto o parcela según SIGPAC.',
      officialTerm: 'Superficie SIGPAC',
      why: 'Sirve como referencia oficial para comprobar superficies y declaraciones.',
      whereToFind: 'En el visor SIGPAC o datos importados.',
      example: '1,2540 ha',
    },
  },
  {
    keywords: ['superficie_cultivada', 'superficie cultivada'],
    help: {
      title: 'Superficie cultivada',
      summary: 'Indica la parte de la parcela que realmente está cultivada.',
      officialTerm: 'Superficie cultivada',
      why: 'Sirve para calcular dosis, riegos, fertilización y producción de forma más ajustada.',
      whereToFind: 'En medición de campo, SIGPAC, REA, declaración agraria o datos internos.',
      example: '1,10 ha',
      nextStep: 'No pongas la superficie total si solo una parte está cultivada.',
    },
  },
  {
    keywords: ['cod_provincia', 'cod. provincia', 'codigo provincia', 'código provincia'],
    help: {
      title: 'Código de provincia',
      summary: 'Es el código oficial de la provincia en la referencia SIGPAC.',
      officialTerm: 'Código de provincia',
      why: 'Forma parte de la identificación oficial de la parcela.',
      whereToFind: 'En SIGPAC, declaración agraria o datos importados.',
      example: '46 para Valencia',
    },
  },
  {
    keywords: ['municipio'],
    help: {
      title: 'Municipio',
      summary: 'Indica el municipio donde se encuentra la parcela.',
      officialTerm: 'Municipio SIGPAC',
      why: 'Forma parte de la localización oficial de la parcela.',
      whereToFind: 'En SIGPAC, catastro, declaración agraria o datos importados.',
      example: 'Alzira, Carcaixent, Requena',
    },
  },
  {
    keywords: ['poligono', 'polígono'],
    help: {
      title: 'Polígono',
      summary: 'Es una parte de la referencia oficial de la parcela en SIGPAC o catastro.',
      officialTerm: 'Polígono',
      why: 'Sirve para localizar la parcela de forma oficial junto con parcela y recinto.',
      whereToFind: 'En SIGPAC, catastro, declaración agraria o datos importados.',
      example: 'Polígono 12',
    },
  },
  {
    keywords: ['numero', 'nº parcela', 'numero parcela', 'número parcela'],
    help: {
      title: 'Número de parcela SIGPAC',
      summary: 'Es el número oficial de la parcela dentro del polígono.',
      officialTerm: 'Parcela SIGPAC',
      why: 'Sirve para localizar la parcela junto con provincia, municipio, polígono y recinto.',
      whereToFind: 'En SIGPAC, catastro, declaración agraria o datos importados.',
      example: 'Parcela 45',
    },
  },
  {
    keywords: ['recinto'],
    help: {
      title: 'Recinto SIGPAC',
      summary: 'Es la parte concreta de una parcela SIGPAC con un uso determinado.',
      officialTerm: 'Recinto SIGPAC',
      why: 'Permite indicar con más precisión dónde está el cultivo o la actuación.',
      whereToFind: 'En el visor SIGPAC o datos importados.',
      example: 'Recinto 1, Recinto 3',
    },
  },
  {
    keywords: ['agregado'],
    help: {
      title: 'Agregado',
      summary: 'Es un dato de la referencia SIGPAC usado en algunas zonas para completar la identificación.',
      officialTerm: 'Agregado SIGPAC',
      why: 'Ayuda a formar la referencia oficial completa de la parcela.',
      whereToFind: 'En SIGPAC o datos importados.',
      example: 'Agregado 0',
    },
  },
  {
    keywords: ['zona'],
    help: {
      title: 'Zona SIGPAC',
      summary: 'Es un dato de la referencia SIGPAC usado en algunas zonas para completar la identificación.',
      officialTerm: 'Zona SIGPAC',
      why: 'Ayuda a formar la referencia oficial completa de la parcela.',
      whereToFind: 'En SIGPAC o datos importados.',
      example: 'Zona 0',
    },
  },
  {
    keywords: ['sigpac'],
    help: {
      title: 'Referencia SIGPAC',
      summary: 'Es la referencia oficial que identifica la parcela agrícola en el mapa.',
      officialTerm: 'SIGPAC',
      why: 'Sirve para localizar la parcela y relacionarla con datos oficiales.',
      whereToFind: 'En el visor SIGPAC, declaración agraria o datos importados.',
      example: 'Provincia, municipio, polígono, parcela y recinto',
    },
  },
  {
    keywords: ['coordenadas'],
    help: {
      title: 'Coordenadas',
      summary: 'Indican la ubicación de la parcela en el mapa.',
      officialTerm: 'Coordenadas geográficas',
      why: 'Ayudan a localizar la parcela cuando no basta con el nombre o la referencia SIGPAC.',
      whereToFind: 'En el visor de mapas, GPS, SIGPAC o datos importados.',
      example: '39.4699, -0.3763',
    },
  },
  {
    keywords: ['codigo_secano_regadio', 'secano / regadío', 'secano / regadio'],
    help: {
      title: 'Secano o regadío',
      summary: 'Indica si el cultivo depende de la lluvia o si se riega.',
      officialTerm: 'Sistema secano / regadío',
      why: 'Afecta al manejo, riego, fertilización y datos oficiales de la parcela.',
      whereToFind: 'En la declaración agraria, REA, SIGPAC o datos de la explotación.',
      example: 'Secano, regadío',
      nextStep: 'Si eliges regadío, revisa también el sistema de riego.',
    },
  },
  {
    keywords: ['codigo_sistema_riego', 'sistema de riego'],
    help: {
      title: 'Sistema de riego',
      summary: 'Indica cómo se riega la parcela.',
      officialTerm: 'Sistema de riego',
      why: 'Sirve para documentar el manejo del agua y puede afectar a fertilización o fertirrigación.',
      whereToFind: 'En la instalación de riego, comunidad de regantes o datos de la explotación.',
      example: 'Goteo, aspersión, gravedad',
    },
  },
  {
    keywords: ['zona_riego', 'zona de riego'],
    help: {
      title: 'Zona de riego',
      summary: 'Indica la zona de riego a la que pertenece la parcela.',
      officialTerm: 'Zona de riego',
      why: 'Ayuda a organizar turnos, sectores o áreas con distinta presión o disponibilidad de agua.',
      whereToFind: 'En la comunidad de regantes, plano de riego o datos internos.',
      example: 'Zona Alta, Zona Baja',
    },
  },
  {
    keywords: ['codigo_sistema_cultivo', 'sistema de cultivo'],
    help: {
      title: 'Sistema de cultivo',
      summary: 'Indica la forma general en que se maneja el cultivo.',
      officialTerm: 'Sistema de cultivo',
      why: 'Ayuda a clasificar la parcela y aplicar criterios de manejo adecuados.',
      whereToFind: 'En la declaración agraria, datos técnicos o forma real de explotación.',
      example: 'Aire libre, invernadero, forestal, cultivo protegido',
    },
  },
  {
    keywords: ['codigo_observaciones_forestales', 'observaciones de forestales'],
    help: {
      title: 'Observaciones forestales',
      summary: 'Añade información específica cuando el uso o sistema de cultivo es forestal.',
      officialTerm: 'Observaciones forestales',
      why: 'Permite concretar mejor el tipo de terreno o aprovechamiento forestal.',
      whereToFind: 'En la documentación forestal, declaración agraria o datos técnicos.',
    },
  },
  {
    keywords: ['sistema_asesoramiento', 'sistema de asesoramiento'],
    help: {
      title: 'Sistema de asesoramiento',
      summary: 'Indica cómo recibe asesoramiento técnico esta parcela o explotación.',
      officialTerm: 'Sistema de asesoramiento',
      why: 'Ayuda a justificar quién orienta el manejo, tratamientos o Gestión Integrada de Plagas.',
      whereToFind: 'En el contrato con el asesor, entidad, certificación o documentación técnica.',
      example: 'Asesor externo, técnico de cooperativa, asesoramiento propio',
    },
  },
  {
    keywords: ['estacion', 'estación'],
    help: {
      title: 'Estación del cultivo',
      summary: 'Indica la época o estación relacionada con esta parcela o actuación.',
      officialTerm: 'Estación',
      why: 'Ayuda a organizar trabajos por campaña o periodo del año.',
      whereToFind: 'Según la fecha o planificación de la campaña.',
      example: 'Invierno, primavera, verano',
    },
  },
  {
    keywords: ['incluido_en_parcela', 'incluido en la parcela'],
    help: {
      title: 'Incluido en la parcela',
      summary: 'Marca si el elemento medioambiental está dentro de la parcela.',
      officialTerm: 'Elemento incluido en parcela',
      why: 'Ayuda a saber si el punto o zona afecta directamente a la parcela.',
      whereToFind: 'Revisando el mapa, visita de campo o documentación ambiental.',
    },
  },
  {
    keywords: ['distancia', 'distancia (m3)'],
    help: {
      title: 'Distancia al elemento',
      summary: 'Indica la distancia entre la parcela y el elemento que se está registrando.',
      officialTerm: 'Distancia',
      why: 'Puede ser necesaria para justificar proximidad a zonas sensibles o elementos ambientales.',
      whereToFind: 'Midiendo en campo o sobre un visor cartográfico.',
      example: '20 m, 150 m',
      nextStep: 'Revisa la unidad que muestra el formulario antes de escribir el valor.',
    },
  },
  {
    keywords: ['coordenadas_utm', 'coordenadas utm'],
    help: {
      title: 'Coordenadas UTM',
      summary: 'Indican la ubicación de un punto usando coordenadas UTM.',
      officialTerm: 'Coordenadas UTM',
      why: 'Permiten localizar con precisión elementos ambientales o puntos de control.',
      whereToFind: 'En GPS, visor cartográfico, SIGPAC o informe técnico.',
      example: 'X: 723456, Y: 4378901',
    },
  },
  {
    keywords: ['denominacion', 'denominación'],
    help: {
      title: 'Denominación',
      summary: 'Es el nombre del elemento, zona o punto que se está registrando.',
      officialTerm: 'Denominación',
      why: 'Ayuda a reconocerlo más adelante sin depender solo de coordenadas.',
      whereToFind: 'En documentación ambiental, mapa, informe técnico o nombre usado en campo.',
      example: 'Balsa norte, Acequia principal, Punto de control 1',
    },
  },
  {
    keywords: ['zona_total', 'totalmente'],
    help: {
      title: 'Afecta totalmente',
      summary: 'Marca esta opción si toda la parcela está dentro de la zona específica.',
      officialTerm: 'Zona específica total',
      why: 'Ayuda a saber si las condiciones especiales afectan a toda la parcela.',
      nextStep: 'Si solo afecta a una parte, marca “Parcialmente” en lugar de esta opción.',
    },
  },
  {
    keywords: ['zona_parcial', 'parcialmente'],
    help: {
      title: 'Afecta parcialmente',
      summary: 'Marca esta opción si solo una parte de la parcela está dentro de la zona específica.',
      officialTerm: 'Zona específica parcial',
      why: 'Ayuda a diferenciar la parte afectada de la parte no afectada.',
      whereToFind: 'En mapas oficiales, medición técnica o revisión de campo.',
      nextStep: 'Si el formulario lo permite, indica la superficie afectada.',
    },
  },
];

const MAQUINARIA_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['nombre', 'nombre marca modelo', 'nombre, marca y modelo', 'nombre marca y modelo'],
    help: {
      title: 'Nombre, marca y modelo',
      summary: 'Indica cómo reconocerás esta máquina dentro de la aplicación.',
      officialTerm: 'Identificación de maquinaria',
      why: 'Sirve para escoger la máquina correcta cuando registres tratamientos, fertilizaciones, trabajos o mantenimientos.',
      whereToFind: 'En la propia máquina, factura, documentación técnica o placa del fabricante.',
      example: 'Tractor John Deere 5100M, Atomizador Fede 1000 l, Cuba Aguirre 2000 l',
      nextStep: 'Usa un nombre fácil de reconocer. Si puedes, incluye marca y modelo.',
    },
  },
  {
    keywords: ['tipo'],
    help: {
      title: 'Tipo de máquina',
      summary: 'Indica qué clase de máquina o equipo estás registrando.',
      officialTerm: 'Tipo de maquinaria',
      why: 'Ayuda a clasificar la máquina y a seleccionarla correctamente cuando hagas una tarea.',
      whereToFind: 'En la documentación de la máquina o según el uso real que le das en campo.',
      example: 'Tractor, atomizador, cuba, abonadora, sembradora, apero',
      nextStep: 'Elige el tipo que mejor describa el uso principal de la máquina.',
    },
  },
  {
    keywords: ['capacidad', 'capacidad litros', 'capacidad (litros)'],
    help: {
      title: 'Capacidad',
      summary: 'Indica cuántos litros caben en la cuba, depósito o equipo.',
      officialTerm: 'Capacidad (l)',
      why: 'Sirve para preparar mezclas, calcular cargas y registrar aplicaciones correctamente.',
      whereToFind: 'En la placa del equipo, ficha técnica, factura o documentación del fabricante.',
      example: '600 l, 1000 l, 2000 l',
      nextStep: 'Escribe solo el número si el campo ya indica litros.',
    },
  },
  {
    keywords: ['roma'],
    help: {
      title: 'Número ROMA',
      summary: 'Es el número oficial de una máquina inscrita en el Registro Oficial de Maquinaria Agrícola.',
      officialTerm: 'ROMA',
      why: 'Puede ser necesario para identificar oficialmente tractores, equipos o maquinaria agrícola.',
      whereToFind: 'En la documentación de la máquina, certificado de inscripción o registro ROMA.',
      example: 'Número de inscripción ROMA de la máquina',
      nextStep: 'Si no lo conoces, consulta la documentación de la máquina o a tu gestoría.',
    },
  },
  {
    keywords: ['id_cliente', 'propietario', 'cliente asignado'],
    help: {
      title: 'Propietario',
      summary: 'Indica a quién pertenece o a quién se asigna esta máquina.',
      officialTerm: 'Titular / propietario / cliente asignado',
      why: 'Evita mezclar máquinas de distintos clientes, fincas o titulares.',
      whereToFind: 'En la factura, documentación de la máquina o listado de clientes de la aplicación.',
      example: 'Juan García, Agrícola Norte S.L., Finca Las Lomas',
      nextStep: 'Comprueba que la máquina queda asociada al titular correcto.',
    },
  },
  {
    keywords: ['fecha_adquisicion', 'fecha adquisición', 'fecha adquisicion'],
    help: {
      title: 'Fecha de adquisición',
      summary: 'Indica cuándo se compró o empezó a usarse esta máquina.',
      officialTerm: 'Fecha de adquisición',
      why: 'Ayuda a controlar antigüedad, amortización, mantenimiento y vida útil.',
      whereToFind: 'En la factura, contrato de compra, albarán o documentación interna.',
      example: '15/03/2022',
      nextStep: 'Si no conoces la fecha exacta, usa la fecha más aproximada que tengas documentada.',
    },
  },
  {
    keywords: ['fecha_ultima_inspeccion', 'fecha última inspección iteaf', 'fecha ultima inspeccion iteaf'],
    help: {
      title: 'Fecha última inspección ITEAF',
      summary: 'Indica la fecha de la última inspección técnica del equipo de aplicación.',
      officialTerm: 'ITEAF',
      why: 'Sirve para controlar que el equipo de aplicación ha sido revisado cuando corresponde.',
      whereToFind: 'En el certificado o informe de inspección ITEAF.',
      example: '10/04/2025',
      nextStep: 'Revisa también la fecha de caducidad para saber cuándo tocará la próxima inspección.',
    },
  },
  {
    keywords: ['fecha_caducidad', 'fecha caducidad iteaf', 'fecha caducidad'],
    help: {
      title: 'Fecha caducidad ITEAF',
      summary: 'Indica hasta cuándo es válida la inspección ITEAF.',
      officialTerm: 'Fecha de caducidad ITEAF',
      why: 'Ayuda a evitar usar un equipo de aplicación con la inspección caducada.',
      whereToFind: 'En el certificado de inspección ITEAF o documentación del equipo.',
      example: '10/04/2028',
      nextStep: 'Si el campo no se puede editar, puede estar calculado a partir de la última inspección.',
    },
  },
  {
    keywords: ['fecha_alerta', 'fecha alerta'],
    help: {
      title: 'Fecha de alerta',
      summary: 'Indica cuándo quieres recordar una revisión, inspección o mantenimiento.',
      officialTerm: 'Fecha de alerta',
      why: 'Sirve para no olvidar avisos importantes relacionados con la máquina.',
      whereToFind: 'Puedes elegirla según tu planificación de trabajo o mantenimiento.',
      example: '01/03/2026',
      nextStep: 'Pon una fecha anterior al vencimiento si quieres tener margen para actuar.',
    },
  },
  {
    keywords: ['fecha_ultimo_mantenimiento', 'fecha último mantenimiento', 'fecha ultimo mantenimiento'],
    help: {
      title: 'Fecha último mantenimiento',
      summary: 'Indica cuándo se hizo la última revisión o mantenimiento de la máquina.',
      officialTerm: 'Fecha de mantenimiento',
      why: 'Ayuda a controlar el estado del equipo y a prevenir averías.',
      whereToFind: 'En el parte de taller, factura, libreta de mantenimiento o registro interno.',
      example: '20/02/2026',
      nextStep: 'Actualiza esta fecha después de cada mantenimiento importante.',
    },
  },
  {
    keywords: ['vida_util', 'vida útil estimada', 'vida util estimada', 'vida útil estimada horas', 'vida util estimada horas'],
    help: {
      title: 'Vida útil estimada',
      summary: 'Indica cuántas horas se espera que pueda trabajar esta máquina.',
      officialTerm: 'Vida útil estimada (horas)',
      why: 'Sirve para planificar costes, renovación, amortización y mantenimiento.',
      whereToFind: 'En la ficha técnica, recomendación del fabricante o criterio interno de la empresa.',
      example: '5000 h, 8000 h',
      nextStep: 'Usa horas de trabajo, no años.',
    },
  },
  {
    keywords: ['precio', 'precio €', 'precio euros'],
    help: {
      title: 'Precio',
      summary: 'Indica el coste de compra o valor asignado a esta máquina.',
      officialTerm: 'Precio (€)',
      why: 'Ayuda a calcular costes, amortización o valoración interna.',
      whereToFind: 'En la factura, contrato de compra o valoración de la empresa.',
      example: '12000 €',
      nextStep: 'Introduce el importe sin símbolo de euro si el campo ya lo indica.',
    },
  },
  {
    keywords: ['precio_hora', 'coste/hora', 'coste hora', 'precio hora'],
    help: {
      title: 'Coste por hora',
      summary: 'Indica cuánto cuesta usar esta máquina por cada hora de trabajo.',
      officialTerm: 'Coste/hora (€)',
      why: 'Sirve para calcular el coste real de trabajos, tratamientos o servicios.',
      whereToFind: 'Puede calcularse con combustible, mantenimiento, amortización y mano de obra.',
      example: '35 €/h, 50 €/h',
      nextStep: 'Si no lo sabes, usa el valor que te indique tu empresa o asesoría.',
    },
  },
  {
    keywords: ['id_comercio', 'empresa servicios agrícolas', 'empresa servicios agricolas', 'proveedor'],
    help: {
      title: 'Proveedor o empresa de servicios',
      summary: 'Indica la empresa relacionada con esta máquina o servicio agrícola.',
      officialTerm: 'Proveedor / empresa de servicios agrícolas',
      why: 'Ayuda a saber quién presta el servicio, vende, mantiene o gestiona la máquina.',
      whereToFind: 'En facturas, contratos, albaranes o listado de comercios de la aplicación.',
      example: 'Servicios Agrícolas Martínez, Talleres Agrarios S.L.',
      nextStep: 'Selecciona la empresa correcta para evitar mezclar facturas, servicios o mantenimientos.',
    },
  },
  {
    keywords: ['gps', 'identificador gps'],
    help: {
      title: 'Identificador GPS',
      summary: 'Es el código o nombre con el que se reconoce el dispositivo GPS de la máquina.',
      officialTerm: 'Identificador GPS',
      why: 'Sirve para relacionar la máquina con su seguimiento o localización.',
      whereToFind: 'En la plataforma GPS, etiqueta del dispositivo o configuración del equipo.',
      example: 'GPS-TRACTOR-01',
      nextStep: 'Usa el mismo identificador que aparece en la plataforma de seguimiento.',
    },
  },
  {
    keywords: ['imei'],
    help: {
      title: 'IMEI',
      summary: 'Es el número único que identifica un dispositivo móvil o GPS con tarjeta SIM.',
      officialTerm: 'IMEI',
      why: 'Ayuda a identificar el dispositivo instalado en la máquina para seguimiento o soporte.',
      whereToFind: 'En la etiqueta del dispositivo, caja, factura o configuración del equipo.',
      example: '356789012345678',
      nextStep: 'Revísalo con cuidado: suele ser un número largo y un dígito incorrecto puede impedir localizar el equipo.',
    },
  },
  {
    keywords: ['activo'],
    help: {
      title: 'Activo',
      summary: 'Indica si esta máquina se usa actualmente.',
      officialTerm: 'Estado activo',
      why: 'Permite separar máquinas en uso de máquinas antiguas, vendidas o fuera de servicio.',
      nextStep: 'Desactívala solo si ya no quieres usarla en nuevos trabajos.',
    },
  },
  {
    keywords: ['mail_trat', 'recibir mail tratamientos'],
    help: {
      title: 'Recibir mail tratamientos',
      summary: 'Marca si quieres recibir avisos por correo relacionados con tratamientos.',
      officialTerm: 'Notificación por email',
      why: 'Sirve para estar informado cuando se registren o gestionen tratamientos relacionados.',
      whereToFind: 'Activa o desactiva esta opción según cómo quiera trabajar el usuario.',
      nextStep: 'Comprueba que el correo del usuario esté actualizado.',
    },
  },
  {
    keywords: ['adjunto'],
    help: {
      title: 'Archivo adjunto',
      summary: 'Permite guardar documentación relacionada con la máquina.',
      officialTerm: 'Adjunto',
      why: 'Ayuda a tener a mano facturas, certificados, fotos o documentación técnica.',
      whereToFind: 'Puedes subirlo desde tu ordenador, móvil o gestor documental.',
      example: 'Factura de compra, foto de placa, certificado ROMA',
    },
  },
  {
    keywords: ['adjunto_pdf'],
    help: {
      title: 'PDF adjunto',
      summary: 'Permite guardar un documento PDF relacionado con la máquina.',
      officialTerm: 'Adjunto PDF',
      why: 'Sirve para conservar certificados, informes ITEAF, documentación ROMA o facturas en PDF.',
      whereToFind: 'En tus archivos, correo electrónico, gestoría, taller o entidad que emitió el documento.',
      example: 'Certificado ITEAF.pdf, Inscripción ROMA.pdf',
    },
  },
  {
    keywords: ['observaciones'],
    help: {
      title: 'Observaciones',
      summary: 'Campo libre para añadir aclaraciones útiles sobre esta máquina.',
      why: 'Ayuda a recordar detalles que no encajan en otros campos.',
      example: 'Revisar boquillas antes de campaña. Pierde presión en la barra derecha.',
      nextStep: 'Escribe solo información útil para futuras revisiones, tratamientos o mantenimientos.',
    },
  },
];

const APLICADORES_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['aplicador_nombre', 'trabajador_nombre', 'nombre aplicador', 'nombre trabajador'],
    help: {
      title: 'Nombre de la persona',
      summary: 'Es el nombre de la persona que trabaja, aplica productos o participa en las tareas.',
      officialTerm: 'Trabajador / aplicador',
      why: 'Sirve para saber quién ha realizado, supervisado o participado en cada trabajo.',
      whereToFind: 'En el DNI, contrato, ficha del trabajador o datos internos de la explotación.',
      example: 'Juan, María, Pedro',
      nextStep: 'Después completa los apellidos para identificar bien a la persona.',
    },
  },
  {
    keywords: ['primer_apellido', 'primer apellido'],
    help: {
      title: 'Primer apellido',
      summary: 'Indica el primer apellido de la persona.',
      officialTerm: 'Primer apellido',
      why: 'Ayuda a identificar correctamente al trabajador o aplicador y evita confusiones con personas del mismo nombre.',
      whereToFind: 'En el DNI, NIE, contrato o ficha del trabajador.',
      example: 'García',
    },
  },
  {
    keywords: ['segundo_apellido', 'segundo apellido'],
    help: {
      title: 'Segundo apellido',
      summary: 'Indica el segundo apellido de la persona, si corresponde.',
      officialTerm: 'Segundo apellido',
      why: 'Ayuda a identificar mejor a la persona en listados, partes de trabajo o documentación.',
      whereToFind: 'En el DNI, NIE, contrato o ficha del trabajador.',
      example: 'López',
    },
  },
  {
    keywords: ['nif'],
    help: {
      title: 'Documento identificativo',
      summary: 'Identifica oficialmente a la persona.',
      officialTerm: 'NIF / DNI / NIE',
      why: 'Evita confusiones entre trabajadores y puede ser necesario en documentación, autorizaciones o registros.',
      whereToFind: 'En el DNI, NIE o documentación de la persona.',
      example: '12345678Z',
      nextStep: 'Escríbelo sin espacios y revisa que la letra sea correcta.',
    },
  },
  {
    keywords: ['password_fichaje', 'password fichaje'],
    help: {
      title: 'Contraseña de fichaje',
      summary: 'Es la clave que usa la persona para fichar o registrar entradas y salidas.',
      officialTerm: 'Contraseña de fichaje',
      why: 'Sirve para que cada trabajador pueda identificarse al usar el sistema de fichaje.',
      whereToFind: 'La puede crear la empresa o la persona responsable de la aplicación.',
      nextStep: 'Usa una contraseña fácil de recordar para el trabajador, pero no evidente para otras personas.',
    },
  },
  {
    keywords: ['password', 'contraseña'],
    help: {
      title: 'Contraseña de acceso',
      summary: 'Es la clave que usará esta persona para entrar en la aplicación.',
      officialTerm: 'Contraseña',
      why: 'Protege el acceso a los datos y evita que otras personas entren con su usuario.',
      whereToFind: 'La crea la persona responsable o el propio usuario si el sistema lo permite.',
      nextStep: 'Usa una contraseña segura y no la compartas.',
    },
  },
  {
    keywords: ['password_repeat', 'repetir contraseña', 'repetir contrasena'],
    help: {
      title: 'Repetir contraseña',
      summary: 'Vuelve a escribir la misma contraseña para comprobar que no hay errores.',
      officialTerm: 'Confirmación de contraseña',
      why: 'Evita guardar una contraseña mal escrita.',
      nextStep: 'Debe coincidir exactamente con la contraseña anterior.',
    },
  },
  {
    keywords: ['cuadrilla'],
    help: {
      title: 'Cuadrilla',
      summary: 'Indica el grupo de trabajo al que pertenece esta persona.',
      officialTerm: 'Cuadrilla',
      why: 'Ayuda a organizar trabajadores por equipos, zonas o responsables.',
      whereToFind: 'En la organización interna de la empresa o en el listado de cuadrillas.',
      example: 'Cuadrilla poda, Cuadrilla recolección, Equipo norte',
    },
  },
  {
    keywords: ['numss', 'nº afiliacion', 'nº afiliación', 'numero afiliacion', 'número afiliación'],
    help: {
      title: 'Número de afiliación',
      summary: 'Es el número de afiliación a la Seguridad Social de la persona.',
      officialTerm: 'Número de afiliación',
      why: 'Puede ser necesario para gestión laboral o documentación interna.',
      whereToFind: 'En la documentación laboral, contrato, nómina o datos facilitados por el trabajador.',
      example: '12/1234567890',
    },
  },
  {
    keywords: ['id_maquina', 'máquina', 'maquina'],
    help: {
      title: 'Máquina asignada',
      summary: 'Indica la máquina que suele usar esta persona o que tiene asignada.',
      officialTerm: 'Maquinaria asignada',
      why: 'Ayuda a relacionar trabajadores, aplicadores y equipos usados en campo.',
      whereToFind: 'En el listado de maquinaria activa de la aplicación.',
      example: 'Tractor John Deere, atomizador, cuba de tratamientos',
      nextStep: 'Si no aparece una máquina, revisa que esté activa y asociada al cliente correcto.',
    },
  },
  {
    keywords: ['id_cargo', 'cargo'],
    help: {
      title: 'Cargo o función',
      summary: 'Indica qué función tiene esta persona dentro de la explotación o empresa.',
      officialTerm: 'Cargo',
      why: 'Ayuda a saber si actúa como operario, responsable, encargado, aplicador o técnico.',
      whereToFind: 'En la organización interna, contrato o ficha del trabajador.',
      example: 'Operario, encargado, aplicador, responsable de campo',
    },
  },
  {
    keywords: ['email', 'correo'],
    help: {
      title: 'Correo electrónico',
      summary: 'Es el correo de contacto de esta persona.',
      officialTerm: 'Email',
      why: 'Sirve para avisos, acceso, comunicaciones o recuperación de datos.',
      whereToFind: 'En los datos de contacto del trabajador o aplicador.',
      example: 'nombre@correo.com',
    },
  },
  {
    keywords: ['id_comercio', 'empresa servicios agrícolas', 'empresa servicios agricolas', 'proveedor'],
    help: {
      title: 'Empresa o proveedor',
      summary: 'Indica la empresa de servicios agrícolas o proveedor relacionado con esta persona.',
      officialTerm: 'Empresa de servicios agrícolas / proveedor',
      why: 'Sirve para saber si el trabajador o aplicador pertenece a una empresa externa.',
      whereToFind: 'En facturas, contratos, albaranes o listado de comercios de la aplicación.',
      example: 'Servicios Agrícolas Martínez, Proveedor Norte S.L.',
    },
  },
  {
    keywords: ['username', 'usuario'],
    help: {
      title: 'Usuario de acceso',
      summary: 'Es el nombre que usará esta persona para entrar en la aplicación.',
      officialTerm: 'Usuario',
      why: 'Permite identificar quién accede y qué acciones realiza.',
      whereToFind: 'Lo puede crear la persona responsable de la aplicación.',
      example: 'jgarcia, maria.lopez',
      nextStep: 'Usa un usuario corto y fácil de reconocer.',
    },
  },
  {
    keywords: ['acceso_plataforma', 'acceso a plataforma'],
    help: {
      title: 'Acceso a la plataforma',
      summary: 'Marca si esta persona puede entrar en la aplicación.',
      officialTerm: 'Acceso a plataforma',
      why: 'Controla quién puede consultar o modificar datos.',
      nextStep: 'Actívalo solo para personas que necesiten usar la aplicación.',
    },
  },
  {
    keywords: ['observaciones aplicador', 'observaciones trabajador'],
    help: {
      title: 'Observaciones',
      summary: 'Campo libre para añadir aclaraciones útiles sobre esta persona.',
      why: 'Ayuda a recordar detalles que no encajan en otros campos.',
      example: 'Solo trabaja en campaña de recolección. Revisar carné antes de tratamientos.',
    },
  },
  {
    keywords: ['id_cliente_asignado', 'cliente asignado', 'titular asignado'],
    help: {
      title: 'Cliente o titular asignado',
      summary: 'Indica a qué cliente, titular o explotación queda asociada esta persona.',
      officialTerm: 'Cliente asignado / titular asignado',
      why: 'Evita mezclar trabajadores o aplicadores de distintos clientes.',
      whereToFind: 'En el listado de clientes o titulares de la aplicación.',
      example: 'Finca Las Lomas, Juan García, Agrícola Norte S.L.',
      nextStep: 'Comprueba que la persona queda asociada al cliente correcto.',
    },
  },
  {
    keywords: ['propiedad'],
    help: {
      title: 'Propiedad',
      summary: 'Indica la relación de esta persona o recurso con la empresa o explotación.',
      officialTerm: 'Propiedad',
      why: 'Ayuda a diferenciar recursos propios, externos o asociados a otra entidad.',
      whereToFind: 'En la organización interna o documentación de la empresa.',
      example: 'Propio, externo, cedido',
    },
  },
  {
    keywords: ['es_almacen', 'almacén', 'almacen'],
    help: {
      title: 'Almacén',
      summary: 'Marca si esta persona o registro actúa como almacén dentro de la aplicación.',
      officialTerm: 'Almacén',
      why: 'Sirve para organizar movimientos, entregas o gestión interna cuando la app lo use así.',
      nextStep: 'Actívalo solo si este registro debe comportarse como almacén.',
    },
  },
  {
    keywords: ['activo'],
    help: {
      title: 'Persona activa',
      summary: 'Indica si esta persona se usa actualmente en la aplicación.',
      officialTerm: 'Estado activo',
      why: 'Permite separar trabajadores en uso de personas antiguas sin borrar su historial.',
      nextStep: 'Desactívala solo si ya no debe aparecer en nuevos trabajos.',
    },
  },
  {
    keywords: ['adjunto'],
    help: {
      title: 'Archivo adjunto',
      summary: 'Permite guardar documentación relacionada con esta persona.',
      officialTerm: 'Adjunto',
      why: 'Ayuda a tener a mano documentos, fotos, carnés o autorizaciones.',
      whereToFind: 'Puedes subirlo desde tu ordenador, móvil o gestor documental.',
      example: 'Foto del trabajador, carné fitosanitario, autorización firmada',
    },
  },
];

const CARNET_APLICADOR_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['ropo'],
    help: {
      title: 'Número ROPO',
      summary: 'Identifica a la persona o empresa inscrita para actividades relacionadas con productos fitosanitarios.',
      officialTerm: 'ROPO',
      why: 'Puede ser necesario para justificar quién aplica, asesora o realiza trabajos fitosanitarios.',
      whereToFind: 'En el carné, certificado, documentación profesional o consulta ROPO.',
      example: 'Número de inscripción ROPO',
      nextStep: 'Si no lo sabes, revisa la documentación del aplicador antes de guardar o enviar el registro.',
    },
  },
  {
    keywords: ['tipo carnet', 'tipo de carnet', 'tipo carné', 'tipo de carné'],
    help: {
      title: 'Tipo de carné fitosanitario',
      summary: 'Indica el nivel de capacitación que tiene la persona para usar productos fitosanitarios.',
      officialTerm: 'Carné de usuario profesional de productos fitosanitarios',
      why: 'Sirve para saber qué tipo de trabajos fitosanitarios puede realizar esta persona.',
      whereToFind: 'En el carné fitosanitario o certificado de formación.',
      example: 'Básico, cualificado, fumigador, piloto aplicador',
      nextStep: 'Elige el tipo que aparece en el carné de la persona.',
    },
  },
  {
    keywords: ['exp', 'fecha expedición', 'fecha expedicion'],
    help: {
      title: 'Fecha de expedición',
      summary: 'Es la fecha en que se emitió el carné fitosanitario.',
      officialTerm: 'Fecha de expedición',
      why: 'Ayuda a controlar la validez y antigüedad del carné.',
      whereToFind: 'En el propio carné o certificado emitido por la autoridad competente.',
      example: '15/03/2023',
    },
  },
  {
    keywords: ['cad', 'fecha caducidad'],
    help: {
      title: 'Fecha de caducidad',
      summary: 'Es la fecha hasta la que el carné fitosanitario es válido.',
      officialTerm: 'Fecha de caducidad',
      why: 'Ayuda a evitar asignar tratamientos a una persona con el carné caducado.',
      whereToFind: 'En el propio carné o certificado.',
      example: '15/03/2033',
      nextStep: 'Si está caducado, revisa la renovación antes de usarlo en tratamientos.',
    },
  },
];

const PRODUCTOS_FITOSANITARIOS_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['otra_denominacion_comun', 'otra denominacion comun', 'otra denominación común'],
    help: {
      title: 'Buscar por otra denominación',
      summary: 'Activa esta opción si el producto no aparece con el nombre habitual o estándar.',
      officialTerm: 'Otra denominación común',
      why: 'Algunos productos pueden aparecer con otra referencia o denominación en el registro. Esta opción cambia la forma de búsqueda.',
      whereToFind: 'En la etiqueta del producto, factura, albarán o información del Registro de Productos Fitosanitarios.',
      example: 'Buscar por una denominación alternativa del producto.',
      nextStep: 'Actívalo solo si no encuentras el producto con la búsqueda normal.',
    },
  },
  {
    keywords: ['num_registro', 'nº registro', 'numero registro', 'número registro'],
    help: {
      title: 'Número de registro del producto',
      summary: 'Es el número oficial que identifica un producto fitosanitario autorizado.',
      officialTerm: 'Número de Registro de Producto Fitosanitario',
      why: 'Sirve para seleccionar el producto correcto y revisar sus usos, condiciones y autorización.',
      whereToFind: 'En la etiqueta del envase, factura, ficha del producto o Registro de Productos Fitosanitarios del MAPA.',
      example: 'ES-00000 o número de registro indicado en la etiqueta.',
      nextStep: 'Selecciona el producto desde el listado y revisa la información antes de usarlo en una tarea.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE};
comprueba siempre el registro oficial antes de aplicar el producto.`,
    },
  },
  {
    keywords: ['nombre producto', 'nombre del producto'],
    help: {
      title: 'Nombre del producto',
      summary: 'Es el nombre comercial con el que reconocerás este fitosanitario.',
      officialTerm: 'Nombre comercial del producto',
      why: 'Ayuda a identificarlo rápidamente al crear tratamientos, compras o movimientos de almacén.',
      whereToFind: 'En la etiqueta del envase, factura, albarán o registro oficial.',
      example: 'Nombre comercial que aparece en el envase.',
      nextStep: 'Comprueba que coincide con el número de registro seleccionado.',
    },
  },
  {
    keywords: ['imagen'],
    help: {
      title: 'Imagen del producto',
      summary: 'Muestra una imagen o referencia visual del producto, si la aplicación la tiene disponible.',
      officialTerm: 'Imagen informativa',
      why: 'Ayuda a reconocer el producto, pero no sustituye la etiqueta ni el registro oficial.',
      whereToFind: 'Puede venir cargada desde la ficha del producto o desde un archivo interno.',
      nextStep: 'Usa la imagen solo como apoyo visual. Revisa siempre nombre, registro y etiqueta.',
    },
  },
  {
    keywords: ['precio', 'precio coste', 'precio costo'],
    help: {
      title: 'Precio de coste',
      summary: 'Indica cuánto cuesta comprar este producto.',
      officialTerm: 'Precio de coste',
      why: 'Sirve para calcular el coste de tratamientos, stock y compras.',
      whereToFind: 'En la factura, albarán, tarifa del proveedor o datos internos de la empresa.',
      example: '25,50 €',
      nextStep: 'Introduce el precio según la unidad de medida que uses en el producto.',
    },
  },
  {
    keywords: ['precio_venta', 'precio de venta'],
    help: {
      title: 'Precio de venta',
      summary: 'Indica a qué precio se vende o se repercute este producto.',
      officialTerm: 'Precio de venta',
      why: 'Sirve para calcular márgenes, ventas o servicios asociados al producto.',
      whereToFind: 'En la tarifa de venta, acuerdo comercial o datos internos de la empresa.',
      example: '32,00 €',
    },
  },
  {
    keywords: ['familia'],
    help: {
      title: 'Familia del producto',
      summary: 'Indica el grupo o categoría al que pertenece el producto dentro de la aplicación.',
      officialTerm: 'Familia de producto',
      why: 'Ayuda a ordenar y buscar productos de forma más rápida.',
      whereToFind: 'En la clasificación interna de la empresa o en el tipo de producto.',
      example: 'Herbicida, fungicida, insecticida, acaricida',
      nextStep: 'Elige la familia que mejor ayude a encontrar el producto después.',
    },
  },
  {
    keywords: ['unidades', 'uds medida', 'uds. medida', 'unidades medida'],
    help: {
      title: 'Unidad de medida',
      summary: 'Indica cómo se mide este producto en compras, almacén o costes.',
      officialTerm: 'Unidad de medida',
      why: 'Sirve para calcular precios, stock, consumos y tratamientos sin mezclar unidades.',
      whereToFind: 'En la etiqueta, factura, envase o forma habitual de trabajo.',
      example: 'Litros, kilos, unidades, envases',
      nextStep: 'Usa siempre la misma unidad para evitar errores de cálculo.',
    },
  },
  {
    keywords: ['envase'],
    help: {
      title: 'Envase',
      summary: 'Indica el tamaño o presentación del producto.',
      officialTerm: 'Envase / presentación comercial',
      why: 'Ayuda a controlar compras, stock y consumo real del producto.',
      whereToFind: 'En la etiqueta del producto, factura o albarán.',
      example: '1 l, 5 l, 20 l, 1 kg, 25 kg',
      nextStep: 'Escribe la capacidad del envase con su unidad.',
    },
  },
  {
    keywords: ['dosis'],
    help: {
      title: 'Dosis recomendada',
      summary: 'Indica la cantidad de producto que se suele aplicar.',
      officialTerm: 'Dosis de aplicación',
      why: 'Es un dato sensible: una dosis incorrecta puede provocar errores en tratamientos, costes o cuaderno.',
      whereToFind: 'En la etiqueta del producto, ficha técnica, registro oficial o recomendación del asesor.',
      example: '2 l/ha, 250 cc/hl, 1 kg/ha',
      nextStep: 'Antes de aplicar, confirma cultivo, plaga, dosis, plazo de seguridad y condiciones de uso.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE};
la dosis válida es la autorizada para el cultivo y uso concreto.`,
    },
  },
  {
    keywords: ['num_maximo_aplicaciones', 'nº maximo aplicaciones', 'nº máximo aplicaciones', 'numero maximo aplicaciones'],
    help: {
      title: 'Número máximo de aplicaciones',
      summary: 'Indica cuántas veces como máximo puede aplicarse el producto en una campaña o periodo.',
      officialTerm: 'Número máximo de aplicaciones',
      why: 'Ayuda a evitar superar los límites indicados para el producto y el cultivo.',
      whereToFind: 'En la etiqueta, ficha técnica o Registro de Productos Fitosanitarios.',
      example: '1 aplicación, 2 aplicaciones por campaña',
      nextStep: 'Revisa este dato antes de repetir un tratamiento con el mismo producto.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; puede variar según cultivo, plaga y autorización.`,
    },
  },
  {
    keywords: ['observaciones producto fitosanitario', 'observaciones fitosanitario'],
    help: {
      title: 'Observaciones del producto',
      summary: 'Campo libre para añadir aclaraciones útiles sobre este fitosanitario.',
      why: 'Ayuda a recordar condiciones especiales, advertencias internas o detalles de uso.',
      whereToFind: 'En la etiqueta, ficha técnica, recomendación del asesor o experiencia de campo.',
      example: 'Usar solo en lindes. Revisar plazo de seguridad antes de cosecha.',
    },
  },
  {
    keywords: ['ultima_fecha_compra', 'ultima fecha compra', 'última fecha compra'],
    help: {
      title: 'Última fecha de compra',
      summary: 'Indica cuándo se compró este producto por última vez.',
      officialTerm: 'Fecha de última compra',
      why: 'Ayuda a controlar stock, antigüedad del producto y seguimiento de compras.',
      whereToFind: 'En la factura, albarán, pedido o registro de almacén.',
      example: '05/05/2026',
    },
  },
  {
    keywords: ['materia_activa', 'materia activa'],
    help: {
      title: 'Materia activa',
      summary: 'Es la sustancia que hace efecto contra la plaga, enfermedad o mala hierba.',
      officialTerm: 'Materia activa',
      why: 'Ayuda a entender qué lleva el producto y a evitar repetir siempre materias activas parecidas.',
      whereToFind: 'En la etiqueta, ficha técnica o Registro de Productos Fitosanitarios.',
      example: 'Cobre, azufre, glifosato, acetamiprid',
      nextStep: 'Si el campo no se puede editar, normalmente viene del producto seleccionado en el registro.',
    },
  },
];

const PRODUCTOS_FERTILIZANTES_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['nombre fertilizante', 'nombre o marca comercial', 'fertilizante'],
    help: {
      title: 'Nombre del fertilizante',
      summary: 'Es el nombre comercial o nombre fácil con el que reconocerás este producto.',
      officialTerm: 'Producto fertilizante',
      why: 'Sirve para encontrarlo rápido al crear compras, almacén o tareas de fertilización.',
      whereToFind: 'En el saco, etiqueta, ficha técnica, factura o albarán.',
      example: 'NPK 15-15-15, Urea 46 %, Compost vegetal',
      nextStep: 'Usa el mismo nombre que aparece en la etiqueta o factura.',
    },
  },
  {
    keywords: ['familia fertilizante', 'familia abono'],
    help: {
      title: 'Familia del fertilizante',
      summary: 'Indica el grupo o categoría al que pertenece este producto.',
      officialTerm: 'Familia de producto fertilizante',
      why: 'Ayuda a ordenar y buscar fertilizantes de forma más rápida.',
      whereToFind: 'En la clasificación interna, ficha del producto o tipo de fertilizante.',
      example: 'Mineral, orgánico, órgano-mineral, corrector, bioestimulante',
    },
  },
  {
    keywords: ['descripcion', 'descripción', 'tipo fertilizante', 'descripcion tipo fertilizante'],
    help: {
      title: 'Descripción del fertilizante',
      summary: 'Añade una explicación corta sobre qué tipo de fertilizante es o para qué se usa.',
      officialTerm: 'Descripción / tipo de fertilizante',
      why: 'Ayuda a diferenciar productos parecidos y a entender su uso en campo.',
      whereToFind: 'En la etiqueta, ficha técnica, factura o criterio interno de la empresa.',
      example: 'Abono orgánico granulado para fondo. Fertilizante líquido para fertirrigación.',
    },
  },
  {
    keywords: ['composicion', 'composición'],
    help: {
      title: 'Composición',
      summary: 'Indica qué nutrientes o componentes contiene el fertilizante.',
      officialTerm: 'Composición declarada',
      why: 'Sirve para saber qué aporta el producto al cultivo y evitar errores en la fertilización.',
      whereToFind: 'En la etiqueta, ficha técnica o documentación del fabricante.',
      example: 'NPK 12-8-16, 30 % materia orgánica, 2 % MgO',
      nextStep: 'Revisa que la composición coincida con los porcentajes N, P y K si también los rellenas.',
    },
  },
  {
    keywords: ['imagen fertilizante', 'imagen abono'],
    help: {
      title: 'Imagen del fertilizante',
      summary: 'Muestra una imagen o referencia visual del producto, si está disponible.',
      officialTerm: 'Imagen informativa',
      why: 'Ayuda a reconocer el producto, pero no sustituye la etiqueta ni la ficha técnica.',
      whereToFind: 'Puede venir cargada desde la ficha del producto o desde un archivo interno.',
      nextStep: 'Usa la imagen solo como apoyo visual. Revisa siempre nombre, composición y envase.',
    },
  },
  {
    keywords: ['precio fertilizante', 'precio coste fertilizante', 'precio coste abono'],
    help: {
      title: 'Precio de coste',
      summary: 'Indica cuánto cuesta comprar este fertilizante.',
      officialTerm: 'Precio de coste',
      why: 'Sirve para calcular costes de abonado, stock y compras.',
      whereToFind: 'En la factura, albarán, tarifa del proveedor o datos internos de la empresa.',
      example: '18,50 €',
      nextStep: 'Introduce el precio según la unidad de medida usada.',
    },
  },
  {
    keywords: ['precio_venta', 'precio venta fertilizante', 'precio de venta'],
    help: {
      title: 'Precio de venta',
      summary: 'Indica a qué precio se vende o se repercute este fertilizante.',
      officialTerm: 'Precio de venta',
      why: 'Sirve para calcular márgenes, ventas o servicios asociados al producto.',
      whereToFind: 'En la tarifa de venta, acuerdo comercial o datos internos de la empresa.',
      example: '24,00 €',
    },
  },
  {
    keywords: ['unidades fertilizante', 'unidades medida fertilizante', 'unidades medida'],
    help: {
      title: 'Unidad de medida',
      summary: 'Indica cómo se mide este fertilizante en compras, almacén o costes.',
      officialTerm: 'Unidad de medida',
      why: 'Evita mezclar kilos, litros, unidades o envases al calcular stock y aplicaciones.',
      whereToFind: 'En la etiqueta, factura, envase o forma habitual de trabajo.',
      example: 'kg, l, saco, envase, tonelada',
      nextStep: 'Usa siempre la misma unidad para evitar errores de cálculo.',
    },
  },
  {
    keywords: ['envase fertilizante', 'envase abono'],
    help: {
      title: 'Envase',
      summary: 'Indica el tamaño o presentación del fertilizante.',
      officialTerm: 'Envase / presentación comercial',
      why: 'Ayuda a controlar compras, stock y consumo real del producto.',
      whereToFind: 'En la etiqueta, factura o albarán.',
      example: '25 kg, 1000 l, big bag 600 kg',
      nextStep: 'Escribe la capacidad del envase con su unidad.',
    },
  },
  {
    keywords: ['tipo_abono_organico', 'tipo de abono orgánico', 'tipo de abono organico'],
    help: {
      title: 'Tipo de abono orgánico',
      summary: 'Indica qué clase de abono orgánico es el producto.',
      officialTerm: 'Tipo de abono orgánico',
      why: 'Sirve para diferenciar estiércol, compost, purín u otros materiales orgánicos.',
      whereToFind: 'En la etiqueta, ficha técnica, factura o documentación del proveedor.',
      example: 'Estiércol, compost, purín, gallinaza',
      nextStep: 'Elige el tipo que mejor coincida con el producto real.',
    },
  },
  {
    keywords: ['abono_con_inhibidor', 'abono con inhibidor de nitrogeno', 'abono con inhibidor de nitrógeno'],
    help: {
      title: 'Abono con inhibidor de nitrógeno',
      summary: 'Marca si el fertilizante lleva un inhibidor para reducir pérdidas de nitrógeno.',
      officialTerm: 'Inhibidor de nitrógeno',
      why: 'Ayuda a identificar productos con manejo especial del nitrógeno.',
      whereToFind: 'En la etiqueta, ficha técnica o información del fabricante.',
      example: 'Abono nitrogenado con inhibidor de nitrificación o ureasa',
      nextStep: 'Márcalo solo si aparece indicado en la documentación del producto.',
    },
  },
  {
    keywords: ['rotura_stock', 'rotura stock'],
    help: {
      title: 'Aviso de falta de stock',
      summary: 'Indica el nivel mínimo de stock a partir del cual conviene reponer el producto.',
      officialTerm: 'Rotura de stock',
      why: 'Sirve para evitar quedarse sin fertilizante cuando hace falta usarlo.',
      whereToFind: 'Puedes definirlo según consumo habitual, campaña o criterio de almacén.',
      example: 'Avisar cuando queden menos de 5 sacos o 100 l',
    },
  },
  {
    keywords: ['subtipo fertilizante', 'subtipo abono'],
    help: {
      title: 'Subtipo',
      summary: 'Indica una clasificación más concreta dentro del tipo de fertilizante.',
      officialTerm: 'Subtipo de fertilizante',
      why: 'Ayuda a diferenciar productos parecidos dentro de una misma familia.',
      whereToFind: 'En la ficha técnica, etiqueta o clasificación interna.',
      example: 'NPK, nitrogenado, fosfatado, potásico, enmienda orgánica',
    },
  },

  // Simple nutrient percentages
  {
    keywords: ['N', 'n%', 'n porcentaje'],
    help: {
      title: 'Nitrógeno total',
      summary: 'Indica el porcentaje de nitrógeno que contiene el fertilizante.',
      officialTerm: 'N (%)',
      why: 'El nitrógeno influye en el crecimiento vegetativo del cultivo y en el cálculo de abonado.',
      whereToFind: 'En la etiqueta, composición o ficha técnica del producto.',
      example: '15 % N',
      nextStep: 'Escribe solo el número si el campo ya muestra el símbolo %.',
    },
  },
  {
    keywords: ['P', 'p%', 'p porcentaje'],
    help: {
      title: 'Fósforo',
      summary: 'Indica el porcentaje de fósforo declarado para este fertilizante.',
      officialTerm: 'P (%)',
      why: 'Ayuda a calcular el aporte de fósforo al cultivo.',
      whereToFind: 'En la etiqueta, composición o ficha técnica.',
      example: '8 % P',
    },
  },
  {
    keywords: ['K', 'k%', 'k porcentaje'],
    help: {
      title: 'Potasio',
      summary: 'Indica el porcentaje de potasio declarado para este fertilizante.',
      officialTerm: 'K (%)',
      why: 'Ayuda a calcular el aporte de potasio al cultivo.',
      whereToFind: 'En la etiqueta, composición o ficha técnica.',
      example: '16 % K',
    },
  },
  {
    keywords: ['Ca', 'ca%', 'calcio porcentaje'],
    help: {
      title: 'Calcio',
      summary: 'Indica el porcentaje de calcio que aporta el producto.',
      officialTerm: 'Ca (%)',
      why: 'Puede ser importante para la estructura del cultivo, calidad del fruto o correcciones nutricionales.',
      whereToFind: 'En la etiqueta, composición o ficha técnica.',
      example: '12 % Ca',
    },
  },
  {
    keywords: ['S', 's%', 'azufre porcentaje'],
    help: {
      title: 'Azufre',
      summary: 'Indica el porcentaje de azufre que aporta el producto.',
      officialTerm: 'S (%)',
      why: 'Ayuda a calcular el aporte de azufre y ajustar la fertilización.',
      whereToFind: 'En la etiqueta, composición o ficha técnica.',
      example: '10 % S',
    },
  },
  {
    keywords: ['Mg', 'mg%', 'magnesio porcentaje'],
    help: {
      title: 'Magnesio',
      summary: 'Indica el porcentaje de magnesio que aporta el producto.',
      officialTerm: 'Mg (%)',
      why: 'Ayuda a calcular el aporte de magnesio al cultivo.',
      whereToFind: 'En la etiqueta, composición o ficha técnica.',
      example: '4 % Mg',
    },
  },

  // Material fertilizante
  {
    keywords: ['material_ferti', 'material fertilizante'],
    help: {
      title: 'Material fertilizante',
      summary: 'Selecciona el material o tipo oficial de fertilizante que corresponde al producto.',
      officialTerm: 'Material fertilizante',
      why: 'Sirve para clasificar correctamente el producto y sus nutrientes.',
      whereToFind: 'En la etiqueta, ficha técnica o documentación del fabricante.',
      example: 'Abono orgánico, enmienda orgánica, fertilizante mineral',
      nextStep: 'Elige el material que mejor coincida con la ficha del producto.',
    },
  },
  {
    keywords: ['n_total', 'n total'],
    help: {
      title: 'Nitrógeno total',
      summary: 'Indica todo el nitrógeno que contiene el material fertilizante.',
      officialTerm: 'N total',
      why: 'Sirve para calcular el aporte total de nitrógeno en la fertilización.',
      whereToFind: 'En el análisis, etiqueta o ficha técnica del producto.',
      example: '4,5 %',
    },
  },
  {
    keywords: ['n_organico', 'n orgánico', 'n organico'],
    help: {
      title: 'Nitrógeno orgánico',
      summary: 'Indica la parte del nitrógeno que está en forma orgánica.',
      officialTerm: 'N orgánico',
      why: 'Ayuda a saber qué parte del nitrógeno se libera más lentamente.',
      whereToFind: 'En el análisis del producto, etiqueta o ficha técnica.',
      example: '2,8 %',
    },
  },
  {
    keywords: ['n_nitrico', 'n nítrico', 'n nitrico'],
    help: {
      title: 'Nitrógeno nítrico',
      summary: 'Indica la parte del nitrógeno que está en forma nítrica.',
      officialTerm: 'N nítrico',
      why: 'Es una forma de nitrógeno normalmente más rápida para el cultivo.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '1,2 %',
    },
  },
  {
    keywords: ['n_amoniacal', 'n amoniacal'],
    help: {
      title: 'Nitrógeno amoniacal',
      summary: 'Indica la parte del nitrógeno que está en forma amoniacal.',
      officialTerm: 'N amoniacal',
      why: 'Ayuda a conocer cómo se comporta el nitrógeno del producto en el suelo.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '3,0 %',
    },
  },
  {
    keywords: ['n_ureico', 'n ureico'],
    help: {
      title: 'Nitrógeno ureico',
      summary: 'Indica la parte del nitrógeno que procede de urea.',
      officialTerm: 'N ureico',
      why: 'Ayuda a diferenciar la forma de nitrógeno y su manejo.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '10 %',
    },
  },
  {
    keywords: ['p2o5_total', 'p2o5 total', 'p205 total'],
    help: {
      title: 'Fósforo total',
      summary: 'Indica el fósforo total expresado como P2O5.',
      officialTerm: 'P2O5 total',
      why: 'Sirve para calcular el aporte total de fósforo del fertilizante.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '8 % P2O5',
      nextStep: 'Revisa que la etiqueta hable de P2O5 y no de P elemental.',
    },
  },
  {
    keywords: ['p2o5_agua', 'p2o5 soluble en agua', 'p205 soluble en agua'],
    help: {
      title: 'P2O5 soluble en agua',
      summary: 'Indica la parte del fósforo que es soluble en agua.',
      officialTerm: 'P2O5 soluble en agua',
      why: 'Ayuda a saber qué parte del fósforo está más disponible.',
      whereToFind: 'En el análisis, etiqueta o ficha técnica.',
      example: '5 % P2O5 soluble en agua',
    },
  },
  {
    keywords: ['p2o5_amonico', 'p2o5 soluble en citrato amonico neutro y agua', 'p205 soluble en citrato amonico neutro y agua'],
    help: {
      title: 'P2O5 soluble en citrato amónico neutro y agua',
      summary: 'Indica la parte del fósforo soluble según este método de análisis.',
      officialTerm: 'P2O5 soluble en citrato amónico neutro y agua',
      why: 'Sirve para describir mejor la disponibilidad del fósforo del fertilizante.',
      whereToFind: 'En el análisis oficial, etiqueta o ficha técnica del producto.',
      example: '7 %',
    },
  },
  {
    keywords: ['k20_total', 'k2o total', 'k20 total'],
    help: {
      title: 'Potasio total',
      summary: 'Indica el potasio total expresado como K2O.',
      officialTerm: 'K2O total',
      why: 'Sirve para calcular el aporte total de potasio del fertilizante.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '16 % K2O',
      nextStep: 'Si puedes, revisa si el label debería mostrarse como K2O en lugar de K20.',
    },
  },
  {
    keywords: ['materia_organica', 'materia orgánica', 'materia organica'],
    help: {
      title: 'Materia orgánica',
      summary: 'Indica el porcentaje de materia orgánica que aporta el producto.',
      officialTerm: 'Materia orgánica',
      why: 'Ayuda a conocer el aporte orgánico y el efecto sobre el suelo.',
      whereToFind: 'En el análisis, etiqueta o ficha técnica del producto.',
      example: '35 %',
    },
  },
  {
    keywords: ['calcio', 'calcio cao', 'calcio (cao)'],
    help: {
      title: 'Calcio expresado como CaO',
      summary: 'Indica el calcio del producto expresado como óxido de calcio.',
      officialTerm: 'CaO',
      why: 'Sirve para calcular el aporte de calcio cuando la ficha lo expresa como CaO.',
      whereToFind: 'En la etiqueta, análisis o ficha técnica.',
      example: '12 % CaO',
    },
  },
  {
    keywords: ['nombre_tipo', 'nombre del tipo'],
    help: {
      title: 'Nombre del tipo',
      summary: 'Indica el nombre del tipo de fertilizante o material que estás registrando.',
      officialTerm: 'Nombre del tipo',
      why: 'Ayuda a identificar correctamente el material fertilizante.',
      whereToFind: 'En la etiqueta, ficha técnica o clasificación del producto.',
      example: 'Abono orgánico sólido, enmienda orgánica compostada',
    },
  },
  {
    keywords: ['dosis fertilizante', 'dosis abono'],
    help: {
      title: 'Dosis orientativa',
      summary: 'Indica la cantidad de fertilizante que se suele aplicar.',
      officialTerm: 'Dosis',
      why: 'Ayuda a preparar tareas de fertilización y calcular consumos.',
      whereToFind: 'En la recomendación técnica, plan de abonado, etiqueta o experiencia de campo.',
      example: '500 kg/ha, 20 l/ha',
      nextStep: 'Confirma siempre la dosis según cultivo, parcela, análisis y recomendación técnica.',
    },
  },
  {
    keywords: ['tratamiento_recibido', 'tratamiento recibido'],
    help: {
      title: 'Tratamiento recibido',
      summary: 'Indica si el material fertilizante ha recibido algún tratamiento previo.',
      officialTerm: 'Tratamiento recibido',
      why: 'Puede ser importante en productos orgánicos, residuos valorizados o materiales tratados.',
      whereToFind: 'En la ficha técnica, certificado, análisis o documentación del proveedor.',
      example: 'Compostaje, higienización, secado, granulado',
    },
  },
  {
    keywords: ['id_comercio', 'empresa suministradora'],
    help: {
      title: 'Empresa suministradora',
      summary: 'Indica la empresa que suministra este fertilizante o material.',
      officialTerm: 'Empresa suministradora',
      why: 'Sirve para trazabilidad, compras y seguimiento del producto.',
      whereToFind: 'En la factura, albarán, contrato o listado de proveedores.',
      example: 'Fertilizantes Levante S.L.',
    },
  },

  // Heavy metals
  {
    keywords: ['m_cadmio', 'cadmio', 'cadmio cd'],
    help: {
      title: 'Cadmio',
      summary: 'Indica el contenido de cadmio del material fertilizante, si se exige este dato.',
      officialTerm: 'Cadmio (Cd)',
      why: 'Es un metal pesado que puede estar limitado por normativa o controles técnicos.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o documentación técnica.',
      example: 'Valor indicado en el análisis',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; revisa el análisis y la normativa aplicable.`,
    },
  },
  {
    keywords: ['m_cobre', 'cobre', 'cobre cu'],
    help: {
      title: 'Cobre',
      summary: 'Indica el contenido de cobre del material fertilizante, si corresponde.',
      officialTerm: 'Cobre (Cu)',
      why: 'Puede ser un nutriente o un metal a controlar según producto, dosis y normativa.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o ficha técnica.',
      example: 'Valor indicado en el análisis',
    },
  },
  {
    keywords: ['m_plomo', 'plomo', 'plomo pb'],
    help: {
      title: 'Plomo',
      summary: 'Indica el contenido de plomo del material fertilizante, si se exige este dato.',
      officialTerm: 'Plomo (Pb)',
      why: 'Es un metal pesado que puede estar sujeto a límites y controles.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o documentación técnica.',
      example: 'Valor indicado en el análisis',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; revisa el análisis y la normativa aplicable.`,
    },
  },
  {
    keywords: ['m_niquel', 'níquel', 'niquel', 'niquel ni'],
    help: {
      title: 'Níquel',
      summary: 'Indica el contenido de níquel del material fertilizante, si corresponde.',
      officialTerm: 'Níquel (Ni)',
      why: 'Es un metal que puede requerir control según el material aplicado.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o ficha técnica.',
      example: 'Valor indicado en el análisis',
    },
  },
  {
    keywords: ['m_zinc', 'zinc', 'zinc zn'],
    help: {
      title: 'Zinc',
      summary: 'Indica el contenido de zinc del material fertilizante, si corresponde.',
      officialTerm: 'Zinc (Zn)',
      why: 'Puede ser un micronutriente, pero también debe controlarse cuando hay límites aplicables.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o ficha técnica.',
      example: 'Valor indicado en el análisis',
    },
  },
  {
    keywords: ['m_mercurio', 'mercurio', 'mercurio hg'],
    help: {
      title: 'Mercurio',
      summary: 'Indica el contenido de mercurio del material fertilizante, si se exige este dato.',
      officialTerm: 'Mercurio (Hg)',
      why: 'Es un metal pesado sujeto a control por su posible impacto ambiental y sanitario.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o documentación técnica.',
      example: 'Valor indicado en el análisis',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; revisa el análisis y la normativa aplicable.`,
    },
  },
  {
    keywords: ['m_cromo_total', 'cromo total', 'cromo cr'],
    help: {
      title: 'Cromo total',
      summary: 'Indica el contenido total de cromo del material fertilizante, si corresponde.',
      officialTerm: 'Cromo total (Cr)',
      why: 'Puede ser un dato de control en ciertos materiales fertilizantes.',
      whereToFind: 'En el análisis del producto, certificado del proveedor o documentación técnica.',
      example: 'Valor indicado en el análisis',
    },
  },
];

const TAREAS_FITOSANITARIAS_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['fecha_inicio', 'fecha receta', 'fecha inicio'],
    help: {
      title: 'Fecha de receta o inicio',
      summary: 'Indica la fecha en que se hizo la receta, recomendación o inicio del trabajo.',
      officialTerm: 'Fecha de receta / fecha de inicio',
      why: 'Sirve para ordenar el tratamiento y relacionarlo con la recomendación técnica.',
      whereToFind: 'En la receta, recomendación del asesor, orden de trabajo o parte de campo.',
      example: '05/05/2026',
      nextStep: 'Después revisa la fecha real de aplicación.',
    },
  },
  {
    keywords: ['fecha_fin', 'fecha aplicación', 'fecha aplicacion'],
    help: {
      title: 'Fecha de aplicación',
      summary: 'Es el día en que se aplicó realmente el tratamiento.',
      officialTerm: 'Fecha de aplicación',
      why: 'Sirve para el cuaderno, trazabilidad, plazos de seguridad y revisión del tratamiento.',
      whereToFind: 'En el parte de trabajo, agenda de campo o registro del aplicador.',
      example: '06/05/2026',
      nextStep: 'Comprueba que coincide con el día real de trabajo.',
    },
  },
  {
    keywords: ['fin_fecha', 'fecha fin'],
    help: {
      title: 'Fecha de finalización',
      summary: 'Indica cuándo terminó el trabajo si ocupa más de un día.',
      officialTerm: 'Fecha fin',
      why: 'Ayuda a reflejar correctamente trabajos largos o por tramos.',
      whereToFind: 'En el parte de trabajo o planificación de la tarea.',
      example: '07/05/2026',
    },
  },
  {
    keywords: ['punto_km_inicio', 'punto km inicial'],
    help: {
      title: 'Punto kilométrico inicial',
      summary: 'Indica desde qué punto kilométrico empieza el tratamiento o trabajo.',
      officialTerm: 'Punto km inicial',
      why: 'Sirve para ubicar trabajos en carreteras, caminos, arcenes o zonas lineales.',
      whereToFind: 'En la orden de trabajo, plano, señalización o parte de campo.',
      example: 'PK 12+300',
    },
  },
  {
    keywords: ['punto_km', 'punto km final'],
    help: {
      title: 'Punto kilométrico final',
      summary: 'Indica en qué punto kilométrico termina el tratamiento o trabajo.',
      officialTerm: 'Punto km final',
      why: 'Permite delimitar exactamente el tramo tratado.',
      whereToFind: 'En la orden de trabajo, plano, señalización o parte de campo.',
      example: 'PK 13+100',
    },
  },
  {
    keywords: ['id_finca', 'finca'],
    help: {
      title: 'Finca del tratamiento',
      summary: 'Elige la finca o explotación donde se ha hecho el tratamiento.',
      officialTerm: 'Explotación / finca',
      why: 'Evita mezclar tratamientos de distintas fincas y permite generar bien el cuaderno.',
      whereToFind: 'En el listado de fincas creadas en la aplicación.',
      example: 'Finca Las Lomas',
      nextStep: 'Después selecciona sectores o parcelas tratadas.',
    },
  },
  {
    keywords: ['id_sector', 'sectores_nombres', 'sector'],
    help: {
      title: 'Sector tratado',
      summary: 'Elige el sector o grupo de parcelas donde se ha realizado el tratamiento.',
      officialTerm: 'Sector / grupo de parcelas',
      why: 'Ahorra tiempo cuando varias parcelas se tratan igual.',
      whereToFind: 'En los sectores creados dentro de la finca seleccionada.',
      example: 'Sector Olivar norte',
      nextStep: 'Si el tratamiento no afecta a todo el sector, revisa también las parcelas seleccionadas.',
    },
  },
  {
    keywords: ['ids_parcelas', 'parcelas_nombres', 'parcelas'],
    help: {
      title: 'Parcelas tratadas',
      summary: 'Selecciona las parcelas donde se ha aplicado el tratamiento.',
      officialTerm: 'Parcelas / recintos tratados',
      why: 'Si eliges mal la parcela, el cuaderno o el envío oficial pueden quedar incorrectos.',
      whereToFind: 'En la finca seleccionada, sector elegido o datos importados de parcelas.',
      example: 'Parcela 12, Olivar casa, Recinto 1',
      nextStep: 'Comprueba que la superficie sumada coincide con lo tratado realmente.',
    },
  },
  {
    keywords: ['sup_cultivada', 'sup tratada franja protección', 'sup tratada franja proteccion'],
    help: {
      title: 'Superficie tratada en franja de protección',
      summary: 'Indica la superficie tratada cuando solo se trabaja una franja o parte concreta.',
      officialTerm: 'Superficie tratada',
      why: 'Sirve para ajustar dosis, litros aplicados y justificación del tratamiento.',
      whereToFind: 'En la medición de campo, mapa, SIGPAC o parte de trabajo.',
      example: '0,25 ha',
      nextStep: 'No pongas la superficie total si solo se trató una parte.',
    },
  },
  {
    keywords: ['ids_trabajadores', 'trabajadores_nombres', 'aplicadores'],
    help: {
      title: 'Aplicadores',
      summary: 'Selecciona las personas que han realizado el tratamiento.',
      officialTerm: 'Aplicador / trabajador',
      why: 'Permite saber quién aplicó el producto y mantener la trazabilidad del trabajo.',
      whereToFind: 'En el listado de trabajadores o aplicadores activos.',
      example: 'Aplicador con carné fitosanitario',
      nextStep: 'Comprueba que la persona seleccionada tiene la capacitación necesaria cuando corresponda.',
    },
  },
  {
    keywords: ['ids_asignados', 'nombres_asignados', 'asignador'],
    help: {
      title: 'Asignador de la tarea',
      summary: 'Indica quién asigna o encarga esta tarea.',
      officialTerm: 'Asignador / responsable',
      why: 'Ayuda a saber quién creó, asignó o supervisó el trabajo.',
      whereToFind: 'En el listado de usuarios de la aplicación.',
      example: 'Responsable de campo, administrador, técnico',
    },
  },
  {
    keywords: ['id_maquinaria', 'maquinaria'],
    help: {
      title: 'Maquinaria utilizada',
      summary: 'Selecciona la máquina o equipo usado para aplicar el tratamiento.',
      officialTerm: 'Equipo de aplicación / maquinaria',
      why: 'Ayuda a documentar cómo se hizo la aplicación y controlar equipos activos o inspecciones.',
      whereToFind: 'En el listado de maquinaria activa de la explotación.',
      example: 'Tractor con cuba, atomizador, mochila, barra herbicida',
      nextStep: 'Si no aparece la máquina, revisa que esté activa y asociada al cliente correcto.',
    },
  },
  {
    keywords: ['id_tratamiento', 'tratamiento_nombre', 'tratamientos'],
    help: {
      title: 'Producto o tratamiento',
      summary: 'Selecciona el tratamiento o producto fitosanitario que se va a aplicar.',
      officialTerm: 'Tratamiento fitosanitario',
      why: 'Sirve para registrar qué producto se ha usado y poder revisar dosis, cultivo y condiciones.',
      whereToFind: 'En el listado de tratamientos o productos creados en la aplicación.',
      example: 'Tratamiento contra repilo, herbicida en lindes',
      nextStep: 'Pulsa el botón de información para revisar los productos antes de guardar.',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; comprueba siempre autorización, cultivo, dosis y condiciones de uso.`,
    },
  },
  {
    keywords: ['momento_aplicacion', 'momento de aplicación', 'momento de aplicacion'],
    help: {
      title: 'Momento de aplicación',
      summary: 'Indica en qué momento o fase se realiza el tratamiento.',
      officialTerm: 'Momento de aplicación',
      why: 'Algunos tratamientos dependen del estado del cultivo, la plaga o la campaña.',
      whereToFind: 'En la recomendación técnica, receta, etiqueta del producto o criterio del asesor.',
      example: 'Prefloración, postcosecha, antes de lluvia, brotación',
    },
  },
  {
    keywords: ['coste_tratamiento', 'coste tratamiento', 'coste (€)'],
    help: {
      title: 'Coste del tratamiento',
      summary: 'Indica el coste económico del tratamiento.',
      officialTerm: 'Coste (€)',
      why: 'Ayuda a calcular gastos por parcela, finca o campaña.',
      whereToFind: 'En facturas, costes internos, precio del producto o coste de servicio.',
      example: '85,50 €',
    },
  },
  {
    keywords: ['tipo_tratamiento', 'tipo de tratamiento'],
    help: {
      title: 'Tipo de tratamiento',
      summary: 'Indica qué clase de tratamiento se ha realizado.',
      officialTerm: 'Tipo de tratamiento',
      why: 'Ayuda a clasificar el trabajo y revisar después las actuaciones realizadas.',
      whereToFind: 'Según el objetivo del tratamiento o la recomendación técnica.',
      example: 'Herbicida, fungicida, insecticida, acaricida',
    },
  },
  {
    keywords: ['ph_caldo', 'ph caldo', 'pH caldo'],
    help: {
      title: 'pH del caldo',
      summary: 'Indica el pH de la mezcla de agua y producto antes o durante la aplicación.',
      officialTerm: 'pH del caldo',
      why: 'Puede influir en la eficacia o estabilidad de algunos productos.',
      whereToFind: 'Midiéndolo con tiras, medidor de pH o indicación técnica.',
      example: 'pH 6,5',
      nextStep: 'Si no se mide, no inventes el dato.',
    },
  },
  {
    keywords: ['litros', 'volumen de caldo', 'litros caldo', 'caldo litros'],
    help: {
      title: 'Volumen de caldo',
      summary: 'Indica la cantidad de mezcla preparada o recomendada para aplicar.',
      officialTerm: 'Volumen de caldo',
      why: 'Sirve para calcular producto, agua y cobertura de la superficie tratada.',
      whereToFind: 'En la recomendación técnica, etiqueta del producto o cálculo de campo.',
      example: '500 l/ha, 1000 litros totales',
      nextStep: 'Revisa si el campo pide litros totales o litros por hectárea.',
    },
  },
  {
    keywords: ['litros1', 'litros 1'],
    help: {
      title: 'Litros del primer producto',
      summary: 'Indica los litros o cantidad aplicada del primer producto de la mezcla.',
      officialTerm: 'Litros producto 1',
      why: 'Sirve para separar cantidades cuando una tarea incluye varios productos.',
      whereToFind: 'En la receta, recomendación técnica o cálculo de mezcla.',
      example: '25 l',
    },
  },
  {
    keywords: ['litros2', 'litros 2'],
    help: {
      title: 'Litros del segundo producto',
      summary: 'Indica los litros o cantidad aplicada del segundo producto de la mezcla.',
      officialTerm: 'Litros producto 2',
      why: 'Evita mezclar cantidades de productos distintos.',
      whereToFind: 'En la receta, recomendación técnica o cálculo de mezcla.',
      example: '10 l',
    },
  },
  {
    keywords: ['litros3', 'litros 3'],
    help: {
      title: 'Litros del tercer producto',
      summary: 'Indica los litros o cantidad aplicada del tercer producto de la mezcla.',
      officialTerm: 'Litros producto 3',
      why: 'Permite registrar mezclas con varios productos de forma separada.',
      whereToFind: 'En la receta, recomendación técnica o cálculo de mezcla.',
      example: '5 l',
    },
  },
  {
    keywords: ['mochila', 'es mochila'],
    help: {
      title: 'Aplicación con mochila',
      summary: 'Marca si el tratamiento se ha hecho con mochila manual.',
      officialTerm: 'Mochila de aplicación',
      why: 'Ayuda a diferenciar aplicaciones manuales de aplicaciones con maquinaria.',
      whereToFind: 'Según el equipo usado en campo.',
      example: 'Activado si se aplicó con mochila.',
    },
  },
  {
    keywords: ['sup_tratada', 'superficie tratada'],
    help: {
      title: 'Superficie tratada',
      summary: 'Indica si se ha tratado toda la superficie, una parte o una franja.',
      officialTerm: 'Superficie tratada',
      why: 'Ayuda a ajustar dosis, litros, costes y justificación.',
      whereToFind: 'En la observación del trabajo o parte de campo.',
      example: 'Total, parcial, franja',
    },
  },
  {
    keywords: ['orden_asesor', 'orden asesor'],
    help: {
      title: 'Orden del asesor',
      summary: 'Marca si el tratamiento viene indicado por una orden o recomendación del asesor.',
      officialTerm: 'Orden de asesoramiento',
      why: 'Sirve para relacionar el tratamiento con una recomendación técnica.',
      whereToFind: 'En la receta, informe o documento del asesor.',
      example: 'Orden asesor 2026-001',
    },
  },
  {
    keywords: ['aplicacion', 'modo de aplicación', 'modo de aplicacion'],
    help: {
      title: 'Modo de aplicación',
      summary: 'Indica dónde o cómo se ha aplicado el tratamiento.',
      officialTerm: 'Modo de aplicación',
      why: 'Ayuda a concretar el tipo de zona tratada.',
      whereToFind: 'En la orden de trabajo o parte de campo.',
      example: 'Arcén, medianera',
    },
  },
  {
    keywords: ['procura'],
    help: {
      title: 'Procura',
      summary: 'Indica el tipo de procura o acuerdo de gestión aplicado a la tarea.',
      officialTerm: 'Procura',
      why: 'Sirve para clasificar trabajos según el acuerdo o forma de gestión usada.',
      whereToFind: 'En la organización interna, contrato o indicación del cliente.',
      example: 'Procura, media procura, libre',
    },
  },
  {
    keywords: ['tipo_plaga', 'tipo de plaga'],
    help: {
      title: 'Tipo de plaga',
      summary: 'Indica qué tipo de problema se quiere controlar.',
      officialTerm: 'Tipo de plaga',
      why: 'Ayuda a justificar el tratamiento y revisar si el producto corresponde al problema.',
      whereToFind: 'En la observación de campo, aviso fitosanitario o recomendación técnica.',
      example: 'Plaga, enfermedad, malas hierbas',
    },
  },
  {
    keywords: ['motivo'],
    help: {
      title: 'Motivo del tratamiento',
      summary: 'Explica por qué se realiza el tratamiento.',
      officialTerm: 'Motivo / causa de actuación',
      why: 'Sirve para justificar que el tratamiento responde a una necesidad real.',
      whereToFind: 'En la observación de campo, recomendación técnica o aviso de plaga.',
      example: 'Presencia de pulgón, malas hierbas, repilo',
    },
  },
  {
    keywords: ['codigo_justificacion', 'justificacion_actuacion', 'justificación de la actuación', 'justificacion de la actuacion'],
    help: {
      title: 'Justificación de la actuación',
      summary: 'Indica la razón técnica o agronómica por la que se hace el tratamiento.',
      officialTerm: 'Justificación de la actuación',
      why: 'Ayuda a demostrar que el tratamiento se ha realizado por una necesidad concreta.',
      whereToFind: 'En la observación de campo, recomendación del asesor, aviso fitosanitario o guía GIP.',
      example: 'Control de plaga detectada, tratamiento preventivo justificado, malas hierbas en lindes',
      nextStep: 'Elige la opción que mejor explique por qué se ha tratado.',
    },
  },
  {
    keywords: ['desplazamiento'],
    help: {
      title: 'Desplazamiento',
      summary: 'Indica el desplazamiento asociado al trabajo.',
      officialTerm: 'Desplazamiento',
      why: 'Sirve para calcular costes o registrar servicios realizados fuera de base.',
      whereToFind: 'En el parte de trabajo o criterio interno de la empresa.',
    },
  },
  {
    keywords: ['mano_obra', 'mano obra'],
    help: {
      title: 'Mano de obra',
      summary: 'Indica la mano de obra asociada al tratamiento.',
      officialTerm: 'Mano de obra',
      why: 'Ayuda a calcular costes o clasificar el servicio realizado.',
      whereToFind: 'En el parte de trabajo, cuadrilla o datos internos.',
    },
  },
  {
    keywords: ['observaciones', 'recomendación', 'recomendacion'],
    help: {
      title: 'Recomendación u observaciones',
      summary: 'Campo libre para añadir aclaraciones sobre el tratamiento.',
      why: 'Ayuda a explicar detalles que no encajan en otros campos.',
      whereToFind: 'En la recomendación técnica, parte de campo o indicaciones del asesor.',
      example: 'Aplicar solo en lindes. Evitar viento. Revisar parcela norte en 7 días.',
    },
  },
  {
    keywords: ['limpieza_epis', 'limpieza de epis'],
    help: {
      title: 'Limpieza de EPIs',
      summary: 'Marca si se han limpiado los equipos de protección individual tras el tratamiento.',
      officialTerm: 'Limpieza de EPIs',
      why: 'Ayuda a dejar constancia de buenas prácticas de seguridad.',
      whereToFind: 'En el parte de trabajo o protocolo interno.',
      nextStep: 'Marca la opción solo si la limpieza se ha realizado.',
    },
  },
  {
    keywords: ['dia_limpieza', 'día de limpieza', 'dia de limpieza'],
    help: {
      title: 'Día de limpieza',
      summary: 'Indica cuándo se limpiaron los EPIs o equipos relacionados.',
      officialTerm: 'Fecha de limpieza',
      why: 'Permite relacionar la limpieza con el tratamiento realizado.',
      whereToFind: 'En el parte de trabajo o registro interno.',
      example: '06/05/2026',
    },
  },
  {
    keywords: ['it', 'instrucción técnica', 'instruccion tecnica'],
    help: {
      title: 'Instrucción técnica',
      summary: 'Indica la instrucción técnica seguida para realizar el tratamiento.',
      officialTerm: 'IT / instrucción técnica',
      why: 'Ayuda a relacionar la tarea con un procedimiento interno o técnico.',
      whereToFind: 'En la documentación de calidad, protocolo interno o indicaciones del técnico.',
      example: 'IT-TRAT-03',
    },
  },
  {
    keywords: ['lote', 'n° pedido', 'nº pedido'],
    help: {
      title: 'Lote o pedido',
      summary: 'Indica el lote, pedido o referencia relacionada con el tratamiento.',
      officialTerm: 'Lote / pedido',
      why: 'Sirve para trazabilidad, facturación o relación con producción.',
      whereToFind: 'En albaranes, pedidos, etiquetas o sistema interno.',
      example: 'L-2026-15, Pedido 4582',
    },
  },
  {
    keywords: ['tecnica', 'técnica'],
    help: {
      title: 'Técnica aplicada',
      summary: 'Indica la técnica usada para realizar el tratamiento.',
      officialTerm: 'Técnica de aplicación',
      why: 'Ayuda a describir mejor cómo se hizo el trabajo.',
      whereToFind: 'En la orden de trabajo, protocolo o criterio técnico.',
    },
  },
  {
    keywords: ['notas_cliente', 'notas cliente'],
    help: {
      title: 'Notas del cliente',
      summary: 'Muestra avisos o comentarios importantes del cliente.',
      why: 'Sirve para recordar indicaciones antes de realizar o revisar la tarea.',
      whereToFind: 'Viene de la ficha del cliente.',
      nextStep: 'Si el campo no se puede editar, cambia las notas desde la ficha del cliente.',
    },
  },
  {
    keywords: ['estado_tarea', 'estado de la tarea'],
    help: {
      title: 'Estado de la tarea',
      summary: 'Indica en qué situación está el tratamiento.',
      officialTerm: 'Estado de tarea',
      why: 'Ayuda a saber si está pendiente, realizado, revisado o cerrado.',
      whereToFind: 'Según la planificación o avance real del trabajo.',
      example: 'Pendiente, realizado, revisado',
    },
  },
  {
    keywords: ['empresa_pagadora', 'empresa pagadora'],
    help: {
      title: 'Empresa pagadora',
      summary: 'Indica qué empresa paga o asume el coste del trabajo.',
      officialTerm: 'Empresa pagadora',
      why: 'Sirve para facturación y control económico.',
      whereToFind: 'En el contrato, pedido, cliente o indicación administrativa.',
    },
  },
  {
    keywords: ['velocidad', 'velocidad km/h'],
    help: {
      title: 'Velocidad del tractor',
      summary: 'Indica la velocidad de avance durante la aplicación.',
      officialTerm: 'Velocidad (km/h)',
      why: 'Influye en la dosis aplicada, cobertura y uniformidad del tratamiento.',
      whereToFind: 'En el tractor, GPS, monitor de trabajo o parte de campo.',
      example: '5 km/h',
    },
  },
  {
    keywords: ['marcha'],
    help: {
      title: 'Marcha usada',
      summary: 'Indica la marcha del tractor durante la aplicación.',
      officialTerm: 'Marcha',
      why: 'Ayuda a repetir condiciones de trabajo parecidas en futuras aplicaciones.',
      whereToFind: 'En el parte del aplicador o configuración usada durante el tratamiento.',
      example: '2ª corta, 3ª larga',
    },
  },
  {
    keywords: ['rpm'],
    help: {
      title: 'Revoluciones del motor',
      summary: 'Indica las revoluciones por minuto usadas durante el tratamiento.',
      officialTerm: 'RPM',
      why: 'Puede afectar al caudal, presión y funcionamiento del equipo.',
      whereToFind: 'En el cuadro del tractor o monitor de trabajo.',
      example: '1800 rpm',
    },
  },
  {
    keywords: ['presion', 'presión atm-bar', 'presion atm-bar'],
    help: {
      title: 'Presión de trabajo',
      summary: 'Indica la presión usada en el equipo de aplicación.',
      officialTerm: 'Presión (atm/bar)',
      why: 'Afecta al tamaño de gota, caudal, cobertura y deriva.',
      whereToFind: 'En el manómetro del equipo o configuración de la máquina.',
      example: '3 bar, 10 bar',
      nextStep: 'Usa la presión real de trabajo, no la máxima de la máquina.',
    },
  },
  {
    keywords: ['boquillas'],
    help: {
      title: 'Boquillas usadas',
      summary: 'Indica el tipo o referencia de boquillas utilizadas.',
      officialTerm: 'Boquillas',
      why: 'Influyen en caudal, tamaño de gota, cobertura y riesgo de deriva.',
      whereToFind: 'En la boquilla, ficha técnica del fabricante o configuración del equipo.',
      example: 'XR 110-03, antideriva, cono hueco',
    },
  },
  {
    keywords: ['km_recorridos', 'km recorridos'],
    help: {
      title: 'Kilómetros recorridos',
      summary: 'Indica la distancia recorrida durante el trabajo.',
      officialTerm: 'Km recorridos',
      why: 'Ayuda a calcular costes, tramos tratados o rendimiento del trabajo.',
      whereToFind: 'En el GPS, parte de trabajo o contador del vehículo.',
      example: '12,5 km',
    },
  },
  {
    keywords: ['hora_inicio', 'hora inicio'],
    help: {
      title: 'Hora de inicio',
      summary: 'Indica a qué hora empezó el tratamiento.',
      officialTerm: 'Hora inicio',
      why: 'Ayuda a justificar condiciones de aplicación y calcular horas trabajadas.',
      whereToFind: 'En el parte de trabajo o registro del aplicador.',
      example: '08:30',
    },
  },
  {
    keywords: ['hora_fin', 'hora fin'],
    help: {
      title: 'Hora de finalización',
      summary: 'Indica a qué hora terminó el tratamiento.',
      officialTerm: 'Hora fin',
      why: 'Sirve para calcular la duración real del trabajo.',
      whereToFind: 'En el parte de trabajo o registro del aplicador.',
      example: '11:15',
    },
  },
  {
    keywords: ['horas_totales', 'horas'],
    help: {
      title: 'Horas totales',
      summary: 'Muestra o indica el tiempo total empleado en la tarea.',
      officialTerm: 'Horas de trabajo',
      why: 'Ayuda a calcular costes, rendimiento y duración del tratamiento.',
      whereToFind: 'Puede calcularse con hora de inicio y hora fin.',
      example: '2 h 45 min',
    },
  },
  {
    keywords: ['fecha_recoleccion', 'fecha recolección', 'fecha recoleccion'],
    help: {
      title: 'Fecha de recolección',
      summary: 'Indica cuándo está prevista o realizada la recolección.',
      officialTerm: 'Fecha de recolección',
      why: 'Ayuda a comprobar plazos de seguridad antes de cosechar.',
      whereToFind: 'En la planificación de campaña o parte de recolección.',
      example: '20/06/2026',
      nextStep: 'Revisa siempre el plazo de seguridad del producto aplicado.',
    },
  },
  {
    keywords: ['eficacia', 'eficacia_nombre', 'eficacia del tratamiento'],
    help: {
      title: 'Eficacia del tratamiento',
      summary: 'Indica si el tratamiento ha funcionado bien.',
      officialTerm: 'Eficacia del tratamiento',
      why: 'Permite revisar resultados y decidir si hace falta seguimiento.',
      whereToFind: 'En la revisión de campo posterior al tratamiento.',
      example: 'Buena, media, baja',
      nextStep: 'Valóralo después de observar la parcela.',
    },
  },
  {
    keywords: ['aplicado', 'revisado'],
    help: {
      title: 'Aplicado o revisado',
      summary: 'Marca si la tarea ya se ha aplicado o revisado.',
      officialTerm: 'Estado aplicado / revisado',
      why: 'Ayuda a diferenciar tareas pendientes de tareas ya realizadas.',
      nextStep: 'No lo marques hasta que el trabajo esté hecho o comprobado.',
    },
  },
  {
    keywords: ['validado'],
    help: {
      title: 'Validado',
      summary: 'Marca si un responsable ha revisado y dado por buena la tarea.',
      officialTerm: 'Validación',
      why: 'Ayuda a cerrar tareas revisadas antes de generar documentos o informes.',
      nextStep: 'Valida solo cuando los datos estén comprobados.',
    },
  },
  {
    keywords: ['soleado'],
    help: {
      title: 'Estado del cielo',
      summary: 'Indica si el día estaba cubierto, semicubierto o despejado.',
      officialTerm: 'Condición meteorológica',
      why: 'Ayuda a justificar las condiciones del tratamiento.',
      whereToFind: 'En la observación de campo o datos meteorológicos.',
      example: 'Cubierto, semicubierto, despejado',
    },
  },
  {
    keywords: ['cubas_totales', 'cubas totales'],
    help: {
      title: 'Cubas totales',
      summary: 'Indica cuántas cubas se usaron en el tratamiento.',
      officialTerm: 'Número de cubas',
      why: 'Ayuda a calcular litros preparados, producto usado y rendimiento.',
      whereToFind: 'En el parte del aplicador o control de cargas.',
      example: '2 cubas, 3,5 cubas',
    },
  },
  {
    keywords: ['litros_aplicados', 'litros aplicados', 'vol caldo gastado'],
    help: {
      title: 'Litros aplicados',
      summary: 'Indica cuántos litros de caldo se han gastado realmente.',
      officialTerm: 'Volumen aplicado',
      why: 'Sirve para comparar lo preparado con lo aplicado y calcular consumo real.',
      whereToFind: 'En el contador, cuba, parte de trabajo o cálculo del aplicador.',
      example: '850 l',
    },
  },
  {
    keywords: ['litros_aplicados_ha', 'litros aplicados/ha'],
    help: {
      title: 'Litros aplicados por hectárea',
      summary: 'Muestra el volumen aplicado dividido por la superficie tratada.',
      officialTerm: 'Litros/ha',
      why: 'Ayuda a comprobar si el volumen aplicado es coherente con la recomendación.',
      whereToFind: 'Normalmente lo calcula la aplicación con litros aplicados y superficie tratada.',
      example: '500 l/ha',
      nextStep: 'Si sale 0 o un valor raro, revisa litros aplicados y superficie tratada.',
    },
  },
  {
    keywords: ['facturado'],
    help: {
      title: 'Facturado',
      summary: 'Marca si este tratamiento ya se ha facturado.',
      officialTerm: 'Estado de facturación',
      why: 'Ayuda a controlar trabajos pendientes de facturar.',
      nextStep: 'Márcalo solo cuando la factura o albarán esté gestionado.',
    },
  },
  {
    keywords: ['factura_albaran', 'factura/albarán', 'factura albaran'],
    help: {
      title: 'Factura o albarán',
      summary: 'Indica la referencia de factura o albarán relacionada con el tratamiento.',
      officialTerm: 'Factura / albarán',
      why: 'Sirve para relacionar el trabajo con documentación económica.',
      whereToFind: 'En la factura, albarán o sistema administrativo.',
      example: 'F-2026-0015, ALB-345',
    },
  },
  {
    keywords: ['temperatura', 'temperatura ℃'],
    help: {
      title: 'Temperatura',
      summary: 'Indica la temperatura durante la aplicación.',
      officialTerm: 'Temperatura',
      why: 'Puede influir en seguridad, eficacia, evaporación y condiciones de aplicación.',
      whereToFind: 'En estación meteorológica, aplicación del tiempo o medición de campo.',
      example: '22 °C',
    },
  },
  {
    keywords: ['hr', 'humedad r', 'humedad %'],
    help: {
      title: 'Humedad relativa',
      summary: 'Indica la humedad del aire durante la aplicación.',
      officialTerm: 'Humedad relativa',
      why: 'Ayuda a justificar las condiciones de aplicación.',
      whereToFind: 'En estación meteorológica, aplicación del tiempo o medición de campo.',
      example: '65 %',
    },
  },
  {
    keywords: ['viento', 'viento km/h'],
    help: {
      title: 'Viento',
      summary: 'Indica la velocidad del viento durante la aplicación.',
      officialTerm: 'Viento',
      why: 'Es importante para evitar deriva y aplicar en condiciones adecuadas.',
      whereToFind: 'En estación meteorológica, anemómetro o aplicación del tiempo.',
      example: '8 km/h',
      nextStep: 'Si hay viento fuerte, revisa si conviene aplicar.',
    },
  },
  {
    keywords: ['precip', 'precipit l/m²', 'precipitacion', 'precipitación'],
    help: {
      title: 'Precipitación',
      summary: 'Indica si ha llovido o la precipitación registrada.',
      officialTerm: 'Precipitación',
      why: 'La lluvia puede afectar a la eficacia o lavado del producto.',
      whereToFind: 'En estación meteorológica, pluviómetro o aplicación del tiempo.',
      example: '0 l/m²',
    },
  },
  {
    keywords: ['clima'],
    help: {
      title: 'Clima observado',
      summary: 'Indica el estado general del tiempo durante el tratamiento.',
      officialTerm: 'Condición climática',
      why: 'Ayuda a justificar que el tratamiento se hizo en condiciones adecuadas.',
      whereToFind: 'En la observación de campo o datos meteorológicos.',
      example: 'Cubierto, semidespejado, despejado',
    },
  },
  {
    keywords: ['climatologia', 'obtener clima'],
    help: {
      title: 'Obtener clima',
      summary: 'Permite intentar rellenar datos meteorológicos automáticamente.',
      officialTerm: 'Consulta meteorológica',
      why: 'Ahorra tiempo y ayuda a completar temperatura, humedad, viento o precipitación.',
      nextStep: 'Revisa los datos obtenidos antes de guardar.',
    },
  },
  {
    keywords: ['cubeto_evaporacion', 'cubeto evaporación', 'cubeto evaporacion'],
    help: {
      title: 'Cubeto de evaporación',
      summary: 'Marca si el sobrante se gestiona en un cubeto de evaporación.',
      officialTerm: 'Cubeto de evaporación',
      why: 'Ayuda a dejar constancia de cómo se gestiona el caldo sobrante.',
      whereToFind: 'En el punto de gestión de sobrantes o protocolo interno.',
      nextStep: 'Márcalo solo si se ha usado realmente.',
    },
  },
  {
    keywords: ['punto_carga', 'punto carga'],
    help: {
      title: 'Punto de carga',
      summary: 'Marca si se ha usado un punto de carga preparado para llenar el equipo.',
      officialTerm: 'Punto de carga',
      why: 'Ayuda a registrar buenas prácticas en la preparación del tratamiento.',
      whereToFind: 'En la zona donde se carga la cuba o equipo.',
    },
  },
  {
    keywords: ['litros_sobrante', 'litros caldo sobrante'],
    help: {
      title: 'Litros de caldo sobrante',
      summary: 'Indica cuánto caldo ha sobrado después de la aplicación.',
      officialTerm: 'Caldo sobrante',
      why: 'Sirve para controlar sobrantes y evitar vertidos o usos incorrectos.',
      whereToFind: 'Midiendo lo que queda en la cuba o estimándolo al terminar.',
      example: '15 l',
      nextStep: 'Registra la gestión del sobrante según el protocolo de la explotación.',
    },
  },
  {
    keywords: ['dia_tratamiento', 'día tratamiento', 'dia tratamiento'],
    help: {
      title: 'Día del tratamiento',
      summary: 'Indica el día relacionado con la gestión del tratamiento o sobrante.',
      officialTerm: 'Día de tratamiento',
      why: 'Ayuda a relacionar el registro con la aplicación realizada.',
      whereToFind: 'En el parte de campo o fecha real de aplicación.',
      example: '06/05/2026',
    },
  },
  {
    keywords: ['orden_trabajo', 'orden de trabajo'],
    help: {
      title: 'Orden de trabajo',
      summary: 'Describe qué debe hacerse en esta tarea.',
      officialTerm: 'Orden de trabajo',
      why: 'Sirve para dar instrucciones claras al aplicador o cuadrilla.',
      whereToFind: 'La redacta el responsable, técnico o administrador.',
      example: 'Aplicar herbicida en lindes de la parcela norte. Evitar zona junto a acequia.',
    },
  },
];

const TAREAS_FERTILIZANTES_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['id_cliente', 'cliente'],
    help: {
      title: 'Cliente o titular',
      summary: 'Elige el cliente o titular al que pertenece esta fertilización.',
      officialTerm: 'Titular / cliente',
      why: 'Evita mezclar trabajos de distintos clientes o explotaciones.',
      whereToFind: 'En el listado de clientes de la aplicación.',
      example: 'Juan García, Agrícola Norte S.L.',
      nextStep: 'Después selecciona la finca correcta.',
    },
  },
  {
    keywords: ['id_finca', 'finca'],
    help: {
      title: 'Finca de la fertilización',
      summary: 'Elige la finca o explotación donde se ha realizado el abonado.',
      officialTerm: 'Explotación / finca',
      why: 'Sirve para que la fertilización quede en el cuaderno de la finca correcta.',
      whereToFind: 'En el listado de fincas creadas en la aplicación.',
      example: 'Finca Las Lomas',
      nextStep: 'Después selecciona sectores o parcelas afectadas.',
    },
  },
  {
    keywords: ['ids_sectores', 'sectores_nombres', 'sectores'],
    help: {
      title: 'Sectores abonados',
      summary: 'Selecciona los sectores o grupos de parcelas donde se ha aplicado el fertilizante.',
      officialTerm: 'Sector / grupo de parcelas',
      why: 'Ahorra tiempo cuando varias parcelas reciben el mismo abonado.',
      whereToFind: 'En los sectores creados dentro de la finca seleccionada.',
      example: 'Sector Olivar norte',
      nextStep: 'Si solo se abonaron algunas parcelas, revísalas antes de guardar.',
    },
  },
  {
    keywords: ['id_parcelas', 'parcelas_nombres', 'parcelas'],
    help: {
      title: 'Parcelas abonadas',
      summary: 'Selecciona las parcelas donde se ha aplicado el fertilizante.',
      officialTerm: 'Parcelas / recintos fertilizados',
      why: 'La parcela elegida afecta a la superficie, dosis, consumo y cuaderno.',
      whereToFind: 'En la finca o sector seleccionado.',
      example: 'Parcela 12, Cítricos norte',
      nextStep: 'Comprueba que la superficie sumada coincide con la zona realmente abonada.',
    },
  },
  {
    keywords: ['facturado'],
    help: {
      title: 'Facturado',
      summary: 'Marca si esta fertilización ya se ha facturado.',
      officialTerm: 'Estado de facturación',
      why: 'Ayuda a controlar trabajos pendientes de facturar.',
      nextStep: 'Márcalo solo cuando la factura o albarán esté gestionado.',
    },
  },
  {
    keywords: ['fecha_inicio', 'fecha inicio'],
    help: {
      title: 'Fecha de inicio',
      summary: 'Indica cuándo empezó la fertilización.',
      officialTerm: 'Fecha inicio',
      why: 'Sirve para ordenar el trabajo y reflejar correctamente cuándo se hizo.',
      whereToFind: 'En el parte de trabajo, planificación o registro del operario.',
      example: '05/05/2026',
    },
  },
  {
    keywords: ['fecha_fin', 'fecha fin', 'fecha'],
    help: {
      title: 'Fecha de fertilización',
      summary: 'Indica la fecha en que se realizó o terminó el abonado.',
      officialTerm: 'Fecha de aplicación',
      why: 'Es un dato importante para el cuaderno y el seguimiento de la campaña.',
      whereToFind: 'En el parte de trabajo, agenda de campo o registro del aplicador.',
      example: '05/05/2026',
    },
  },
  {
    keywords: ['hora_inicio', 'hora inicio'],
    help: {
      title: 'Hora de inicio',
      summary: 'Indica a qué hora empezó el trabajo.',
      officialTerm: 'Hora inicio',
      why: 'Ayuda a calcular duración, costes y organización del trabajo.',
      whereToFind: 'En el parte de trabajo o registro del operario.',
      example: '08:30',
    },
  },
  {
    keywords: ['hora_fin', 'hora fin'],
    help: {
      title: 'Hora de fin',
      summary: 'Indica a qué hora terminó el trabajo.',
      officialTerm: 'Hora fin',
      why: 'Permite calcular las horas reales empleadas.',
      whereToFind: 'En el parte de trabajo o registro del operario.',
      example: '11:15',
    },
  },
  {
    keywords: ['horas_totales', 'horas'],
    help: {
      title: 'Horas trabajadas',
      summary: 'Muestra o indica el tiempo total usado en la fertilización.',
      officialTerm: 'Horas de trabajo',
      why: 'Ayuda a calcular coste, rendimiento y duración de la tarea.',
      whereToFind: 'Puede calcularse con hora de inicio y hora de fin.',
      example: '2 h 45 min',
    },
  },
  {
    keywords: ['tipo_fertilizacion', 'tipo fertilización', 'tipo fertilizacion'],
    help: {
      title: 'Tipo de fertilización',
      summary: 'Indica qué clase de abonado se ha realizado.',
      officialTerm: 'Tipo de fertilización',
      why: 'Sirve para clasificar la tarea y relacionarla con el cuaderno o datos SIEX cuando corresponda.',
      whereToFind: 'En la planificación de abonado, recomendación técnica o forma real de aplicación.',
      example: 'Fondo, cobertera, fertirrigación, foliar',
      nextStep: 'Elige la opción que mejor represente el trabajo realizado.',
    },
  },
  {
    keywords: ['albaran_factura', 'albarán/factura', 'albaran/factura', 'factura/albarán', 'factura/albaran'],
    help: {
      title: 'Albarán o factura',
      summary: 'Indica la referencia administrativa relacionada con esta fertilización.',
      officialTerm: 'Albarán / factura',
      why: 'Ayuda a relacionar el trabajo con compras, servicios o facturación.',
      whereToFind: 'En la factura, albarán o sistema administrativo.',
      example: 'ALB-2026-015, F-2026-0032',
    },
  },
  {
    keywords: ['prioridad', 'prioridad realizada', 'prioridad/realizada'],
    help: {
      title: 'Prioridad o realizada',
      summary: 'Indica la prioridad de la tarea o si ya está realizada, según la configuración.',
      officialTerm: 'Prioridad / estado de realización',
      why: 'Ayuda a organizar trabajos pendientes y trabajos ya hechos.',
      whereToFind: 'Según la planificación interna de la explotación o empresa.',
      example: 'Alta, media, baja, realizada',
    },
  },
  {
    keywords: ['procura'],
    help: {
      title: 'Procura',
      summary: 'Indica el tipo de procura o acuerdo de gestión aplicado a la tarea.',
      officialTerm: 'Procura',
      why: 'Sirve para clasificar trabajos según el acuerdo o forma de gestión usada.',
      whereToFind: 'En la organización interna, contrato o indicación del cliente.',
      example: 'Procura, media procura, libre',
    },
  },
  {
    keywords: ['horas_dia', 'horas/día', 'horas dia'],
    help: {
      title: 'Horas por día',
      summary: 'Indica cuántas horas al día se dedica a esta tarea.',
      officialTerm: 'Horas/día',
      why: 'Ayuda a planificar trabajos repetidos o repartidos en varios días.',
      whereToFind: 'En la planificación del trabajo o parte de la cuadrilla.',
      example: '4 h/día',
    },
  },
  {
    keywords: ['numero_dias', 'días/semana', 'dias/semana'],
    help: {
      title: 'Días por semana',
      summary: 'Indica cuántos días por semana se realiza o planifica el trabajo.',
      officialTerm: 'Días/semana',
      why: 'Ayuda a organizar trabajos recurrentes.',
      whereToFind: 'En la planificación semanal de la explotación.',
      example: '3 días/semana',
    },
  },
  {
    keywords: ['dias_semana', 'días', 'dias'],
    help: {
      title: 'Días de trabajo',
      summary: 'Selecciona los días en que se realiza o programa la tarea.',
      officialTerm: 'Días de la semana',
      why: 'Ayuda a planificar trabajos repetidos.',
      whereToFind: 'En la planificación de la semana o calendario de trabajo.',
      example: 'Lunes, miércoles y viernes',
    },
  },
  {
    keywords: ['nombre_cuba', 'nombre/cuba'],
    help: {
      title: 'Nombre o cuba',
      summary: 'Indica el nombre de la cuba, depósito o equipo usado.',
      officialTerm: 'Cuba / equipo de aplicación',
      why: 'Ayuda a saber con qué equipo se hizo la fertilización.',
      whereToFind: 'En la maquinaria, depósito, parte de trabajo o nombre interno usado en campo.',
      example: 'Cuba 1, Depósito norte, Cuba fertirriego',
    },
  },
  {
    keywords: ['consumo_agua', 'consumo agua m3', 'consumo agua m³'],
    help: {
      title: 'Consumo de agua',
      summary: 'Indica cuánta agua se ha usado en la tarea.',
      officialTerm: 'Consumo de agua',
      why: 'Es útil en fertirrigación y para controlar el manejo del riego.',
      whereToFind: 'En contador, programador de riego, comunidad de regantes o parte de campo.',
      example: '25 m³',
    },
  },
  {
    keywords: ['notas_cliente', 'notas cliente'],
    help: {
      title: 'Notas del cliente',
      summary: 'Muestra indicaciones o avisos importantes del cliente.',
      why: 'Ayuda a tener en cuenta preferencias, restricciones o aclaraciones antes de trabajar.',
      whereToFind: 'Viene de la ficha del cliente.',
      nextStep: 'Si no se debe cambiar desde aquí, edítalo en la ficha del cliente.',
    },
  },
  {
    keywords: ['ids_trabajadores', 'trabajadores_nombres', 'profesionales'],
    help: {
      title: 'Profesionales',
      summary: 'Selecciona las personas que han realizado o participado en la fertilización.',
      officialTerm: 'Profesionales / trabajadores',
      why: 'Permite saber quién hizo el trabajo y mantener trazabilidad.',
      whereToFind: 'En el listado de trabajadores activos.',
      example: 'Operario, aplicador, responsable de campo',
    },
  },
  {
    keywords: ['id_maquinaria', 'maquinaria'],
    help: {
      title: 'Maquinaria utilizada',
      summary: 'Selecciona la máquina o equipo usado en la fertilización.',
      officialTerm: 'Maquinaria / equipo de aplicación',
      why: 'Ayuda a documentar cómo se hizo el abonado.',
      whereToFind: 'En el listado de maquinaria activa.',
      example: 'Tractor, abonadora, cuba, equipo de fertirrigación',
    },
  },
  {
    keywords: ['aplicado'],
    help: {
      title: 'Aplicado',
      summary: 'Marca si la fertilización ya se ha realizado.',
      officialTerm: 'Estado aplicado',
      why: 'Diferencia tareas pendientes de tareas ya hechas.',
      nextStep: 'Márcalo solo cuando el trabajo esté realmente realizado.',
    },
  },
  {
    keywords: ['ids_asignados', 'asignador'],
    help: {
      title: 'Asignador',
      summary: 'Indica quién asigna o encarga esta tarea.',
      officialTerm: 'Asignador / responsable',
      why: 'Ayuda a saber quién creó, asignó o supervisó el trabajo.',
      whereToFind: 'En el listado de usuarios de la aplicación.',
      example: 'Responsable de campo, administrador, técnico',
    },
  },
  {
    keywords: ['codigo_metodo_aplicacion', 'métodos de aplicación fertilizantes', 'metodos de aplicacion fertilizantes'],
    help: {
      title: 'Método de aplicación',
      summary: 'Indica cómo se ha aplicado el fertilizante.',
      officialTerm: 'Método de aplicación de fertilizantes',
      why: 'Sirve para reflejar si el abonado fue al suelo, por riego, foliar u otro método.',
      whereToFind: 'En el parte de trabajo, planificación de abonado o forma real de aplicación.',
      example: 'Aplicación al suelo, fertirrigación, foliar',
      nextStep: 'Elige el método que mejor describa cómo se aplicó el producto.',
    },
  },
  {
    keywords: ['codigos_buena_practica', 'buenas prácticas fertilizantes', 'buenas practicas fertilizantes'],
    help: {
      title: 'Buenas prácticas de fertilización',
      summary: 'Selecciona las buenas prácticas aplicadas en esta fertilización.',
      officialTerm: 'Buenas prácticas agrícolas en fertilización',
      why: 'Ayuda a justificar que el aporte se hizo de forma adecuada y sostenible.',
      whereToFind: 'En la planificación, asesoramiento técnico o normas internas de la explotación.',
      example: 'Ajuste a necesidades del cultivo, evitar lluvia, fraccionar aportes',
      normativeNote: `Información orientativa revisada a ${NORMATIVE_REVIEW_DATE}; puede variar según comunidad autónoma y normativa aplicable.`,
    },
  },
  {
    keywords: ['grupo'],
    help: {
      title: 'Grupo',
      summary: 'Indica el grupo interno relacionado con la tarea o cultivo.',
      officialTerm: 'Grupo',
      why: 'Ayuda a clasificar fertilizaciones dentro de la organización interna.',
      whereToFind: 'En la planificación de la empresa o criterio del técnico.',
      example: 'Grupo 1, cítricos, olivar regadío',
    },
  },
  {
    keywords: ['tipo_riego', 'tipo riego'],
    help: {
      title: 'Tipo de riego',
      summary: 'Indica cómo se riega la parcela relacionada con la fertilización.',
      officialTerm: 'Tipo de riego',
      why: 'Es importante si el fertilizante se aplica con agua o depende del manejo del riego.',
      whereToFind: 'En la instalación de riego, comunidad de regantes o datos de la finca.',
      example: 'Goteo, aspersión, gravedad',
    },
  },
  {
    keywords: ['produccion_estimada', 'producción estimada', 'produccion estimada'],
    help: {
      title: 'Producción estimada',
      summary: 'Indica la producción que se espera obtener.',
      officialTerm: 'Producción estimada',
      why: 'Ayuda a ajustar necesidades de nutrientes y planificación de abonado.',
      whereToFind: 'En previsiones de campaña, histórico de cosechas o criterio técnico.',
      example: '25.000 kg, 30 t/ha',
    },
  },
  {
    keywords: ['necesidad_maxima', 'necesidad máxima', 'necesidad maxima'],
    help: {
      title: 'Necesidad máxima',
      summary: 'Indica la necesidad máxima prevista de nutrientes o aporte.',
      officialTerm: 'Necesidad máxima',
      why: 'Ayuda a evitar excesos y ajustar el abonado a las necesidades del cultivo.',
      whereToFind: 'En el plan de abonado, recomendación técnica o cálculo agronómico.',
      example: '120 kg N/ha',
    },
  },
  {
    keywords: ['aporte'],
    help: {
      title: 'Aporte',
      summary: 'Indica la cantidad de nutrientes o fertilizante que se aporta.',
      officialTerm: 'Aporte de nutrientes',
      why: 'Sirve para controlar cuánto se está añadiendo al suelo o cultivo.',
      whereToFind: 'En el cálculo de dosis, producto aplicado o plan de abonado.',
      example: '80 kg N/ha, 500 kg de fertilizante',
    },
  },
  {
    keywords: ['cosecha_obtenida', 'cosecha obtenida'],
    help: {
      title: 'Cosecha obtenida',
      summary: 'Indica la producción real conseguida.',
      officialTerm: 'Cosecha obtenida',
      why: 'Ayuda a comparar previsión, abonado aplicado y resultado de campaña.',
      whereToFind: 'En partes de recolección, albaranes o registros de producción.',
      example: '22.500 kg',
    },
  },
  {
    keywords: ['estado_tarea', 'estado de la tarea'],
    help: {
      title: 'Estado de la tarea',
      summary: 'Indica en qué situación está la fertilización.',
      officialTerm: 'Estado de tarea',
      why: 'Ayuda a saber si está pendiente, realizada, revisada o cerrada.',
      whereToFind: 'Según la planificación o avance real del trabajo.',
      example: 'Pendiente, realizada, revisada',
    },
  },
  {
    keywords: ['empresa_pagadora', 'empresa pagadora'],
    help: {
      title: 'Empresa pagadora',
      summary: 'Indica qué empresa paga o asume el coste del trabajo.',
      officialTerm: 'Empresa pagadora',
      why: 'Sirve para facturación y control económico.',
      whereToFind: 'En el contrato, pedido, cliente o indicación administrativa.',
    },
  },
  {
    keywords: ['recomendaciones_riego', 'recomendaciones de riego'],
    help: {
      title: 'Recomendaciones de riego',
      summary: 'Añade indicaciones sobre cómo regar en relación con esta fertilización.',
      officialTerm: 'Recomendaciones de riego',
      why: 'Es útil cuando el abonado depende del agua o de la fertirrigación.',
      whereToFind: 'En la recomendación técnica, programación de riego o asesoramiento.',
      example: 'Regar 2 horas después de aplicar. Evitar riego fuerte tras el abonado.',
    },
  },
  {
    keywords: ['observaciones'],
    help: {
      title: 'Observaciones',
      summary: 'Campo libre para añadir aclaraciones sobre la fertilización.',
      why: 'Ayuda a explicar detalles que no encajan en otros campos.',
      whereToFind: 'En el parte de campo, recomendación técnica o incidencias del trabajo.',
      example: 'Con inhibidor de nitrógeno. Aplicado solo en sector norte.',
    },
  },
  {
    keywords: ['ferti_group'],
    help: {
      title: 'Productos fertilizantes',
      summary: 'Añade uno o varios productos usados en esta fertilización.',
      officialTerm: 'Productos fertilizantes aplicados',
      why: 'Permite registrar producto, kilos totales y cantidades calculadas.',
      whereToFind: 'En el parte de trabajo, factura, albarán o producto usado en campo.',
      nextStep: 'Pulsa “Agregar Producto” si se aplicaron varios fertilizantes.',
    },
  },
  {
    keywords: ['producto ferti', 'producto'],
    help: {
      title: 'Producto fertilizante',
      summary: 'Selecciona el fertilizante aplicado.',
      officialTerm: 'Producto fertilizante',
      why: 'Sirve para saber qué producto se usó y calcular dosis, coste y nutrientes.',
      whereToFind: 'En el listado de productos fertilizantes creados en la aplicación.',
      example: 'NPK 15-15-15, urea, compost, corrector nutricional',
    },
  },
  {
    keywords: ['dosis', 'kg totales'],
    help: {
      title: 'Kilos totales aplicados',
      summary: 'Indica la cantidad total de fertilizante aplicada.',
      officialTerm: 'Dosis / cantidad total',
      why: 'Es clave para calcular consumo, coste y aporte de nutrientes.',
      whereToFind: 'En el parte de trabajo, báscula, saco usado o cálculo de campo.',
      example: '250 kg',
      nextStep: 'No lo confundas con kg/ha si el campo pide kilos totales.',
    },
  },
  {
    keywords: ['gramos'],
    help: {
      title: 'Gramos calculados',
      summary: 'Muestra una cantidad calculada en gramos, según la configuración de la tarea.',
      officialTerm: 'Gramos',
      why: 'Ayuda a ver cantidades pequeñas o por unidad cuando la aplicación lo calcula.',
      whereToFind: 'Normalmente lo calcula la aplicación.',
      nextStep: 'Si el valor parece raro, revisa producto, dosis y superficie.',
    },
  },
  {
    keywords: ['kg_ha', 'kg/ha'],
    help: {
      title: 'Kilos por hectárea',
      summary: 'Muestra la cantidad aplicada por hectárea.',
      officialTerm: 'kg/ha',
      why: 'Ayuda a comprobar si la dosis aplicada es coherente con la recomendación.',
      whereToFind: 'Normalmente lo calcula la aplicación con kilos totales y superficie.',
      example: '150 kg/ha',
      nextStep: 'Si sale 0 o un valor extraño, revisa superficie y kilos totales.',
    },
  },
  {
    keywords: ['orden_trabajo', 'orden de trabajo'],
    help: {
      title: 'Orden de trabajo',
      summary: 'Describe qué debe hacerse en esta fertilización.',
      officialTerm: 'Orden de trabajo',
      why: 'Sirve para dar instrucciones claras al profesional o cuadrilla.',
      whereToFind: 'La redacta el responsable, técnico o administrador.',
      example: 'Aplicar 250 kg de NPK en parcelas del sector norte.',
    },
  },
];

/**
 * Builds short, plain-language help for form fields used by farmers.
 */
export function buildAgriculturalFieldHelp(
  fieldName: string | undefined,
  fieldLabel: string | undefined,
  explicitHelpText?: string,
  context?: AgriculturalHelpContext
): string {
  const details = buildAgriculturalFieldHelpDetails(fieldName, fieldLabel, explicitHelpText, context);

  return formatAgriculturalFieldHelp(details);
}

const MINIMUM_SAFE_KEYWORD_LENGTH = 4;

const UNSAFE_PARTIAL_HELP_KEYWORDS: ReadonlySet<string> = new Set([
  'codigo',
  'código',
  'numero',
  'número',
  'zona',
  'producto',
  'productos',
  'producto fertilizante',
  'productos fertilizantes',
  'fertilizante',
  'fertilizantes',
  'fitosanitario',
  'fitosanitarios',
  'abono',
  'abonado',
  'fertilizacion',
  'fertilización',
  'nombre',
  'tipo',
  'fecha',
  'observaciones',
  'formato',
  'distancia',
  'coordenadas',
  'asesor',
].map((keyword) => normalizeSearchText(keyword)));

const TRABAJADORES_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['nombre'],
    help: {
      title: 'Nombre',
      summary: 'Indica el nombre real de la persona.',
      officialTerm: 'Nombre del trabajador / aplicador',
      why: 'Sirve para identificar correctamente a la persona en tareas, tratamientos, fichajes, partes o autorizaciones.',
      whereToFind: 'En el DNI/NIE, contrato, ficha del trabajador o documentación interna.',
      example: 'Juan, María, Antonio',
      nextStep: 'Escribe el nombre real, sin apodos, para evitar confusiones en documentos o listados.',
    },
  },
  {
    keywords: ['primer_apellido', 'primer apellido'],
    help: {
      title: 'Primer apellido',
      summary: 'Indica el primer apellido de la persona.',
      officialTerm: 'Primer apellido',
      why: 'Ayuda a identificar a la persona cuando hay varios trabajadores con el mismo nombre.',
      whereToFind: 'En el DNI/NIE, contrato o ficha del trabajador.',
      example: 'García',
      nextStep: 'Comprueba que coincide con la documentación oficial.',
    },
  },
  {
    keywords: ['segundo_apellido', 'segundo apellido'],
    help: {
      title: 'Segundo apellido',
      summary: 'Indica el segundo apellido de la persona si lo tiene.',
      officialTerm: 'Segundo apellido',
      why: 'Completa la identificación de la persona en listados, documentos y registros.',
      whereToFind: 'En el DNI/NIE, contrato o ficha del trabajador.',
      example: 'Martínez',
      nextStep: 'Si la persona no tiene segundo apellido, deja el campo vacío si la aplicación lo permite.',
    },
  },
  {
    keywords: ['nif'],
    help: {
      title: 'NIF / NIE',
      summary: 'Es el documento de identificación de la persona.',
      officialTerm: 'NIF / NIE',
      why: 'Sirve para identificar de forma única al trabajador, aplicador o usuario.',
      whereToFind: 'En el DNI, NIE, contrato o documentación laboral.',
      example: '12345678Z, X1234567L',
      nextStep: 'Revísalo con cuidado: una letra o número incorrecto puede causar problemas en documentos y validaciones.',
    },
  },
  {
    keywords: ['password', 'password_fichaje', 'contraseña', 'contrasena'],
    help: {
      title: 'Contraseña',
      summary: 'Clave que usará la persona para acceder o fichar cuando la aplicación lo permita.',
      officialTerm: 'Contraseña / clave de acceso',
      why: 'Permite proteger el acceso de cada usuario y evitar que otra persona actúe en su nombre.',
      whereToFind: 'La define el administrador o la persona responsable de gestionar usuarios.',
      example: 'Clave temporal entregada al trabajador',
      nextStep: 'Usa una contraseña que la persona pueda recordar, pero que no sea fácil de adivinar.',
    },
  },
  {
    keywords: ['cuadrilla'],
    help: {
      title: 'Cuadrilla',
      summary: 'Grupo de trabajo al que pertenece esta persona.',
      officialTerm: 'Cuadrilla / equipo de trabajo',
      why: 'Ayuda a organizar trabajadores por equipos, campañas, fincas o responsables.',
      whereToFind: 'En la planificación interna de la explotación o empresa.',
      example: 'Cuadrilla poda, Cuadrilla recolección, Equipo mañana',
      nextStep: 'Selecciona la cuadrilla correcta para que los partes y listados salgan ordenados.',
    },
  },
  {
    keywords: ['numss', 'nº afiliación', 'nº afiliacion', 'numero afiliacion', 'número afiliación'],
    help: {
      title: 'Número de afiliación',
      summary: 'Número de afiliación a la Seguridad Social de la persona.',
      officialTerm: 'Número de afiliación a la Seguridad Social',
      why: 'Puede ser necesario para gestión laboral, fichajes o documentación de trabajadores.',
      whereToFind: 'En la documentación laboral, nómina, contrato o vida laboral.',
      example: 'Número de afiliación del trabajador',
      nextStep: 'Introduce el número tal como aparece en la documentación laboral.',
    },
  },
  {
    keywords: ['id_maquina', 'máquina', 'maquina'],
    help: {
      title: 'Máquina asignada',
      summary: 'Indica la máquina que usa habitualmente esta persona.',
      officialTerm: 'Máquina asignada',
      why: 'Ayuda a relacionar trabajadores con tractores, equipos o maquinaria usada en tareas.',
      whereToFind: 'En la planificación de trabajo o asignación interna de maquinaria.',
      example: 'Tractor John Deere, Atomizador 1000 l',
      nextStep: 'Selecciona solo una máquina activa y realmente asignada a esta persona.',
    },
  },
  {
    keywords: ['id_cargo', 'cargo'],
    help: {
      title: 'Cargo',
      summary: 'Indica la función o responsabilidad de la persona dentro de la explotación o empresa.',
      officialTerm: 'Cargo / puesto',
      why: 'Sirve para diferenciar aplicadores, responsables, encargados, asesores u otros perfiles.',
      whereToFind: 'En la organización interna, contrato o planificación de personal.',
      example: 'Aplicador, encargado, responsable, peón agrícola',
      nextStep: 'Elige el cargo que mejor describa su trabajo habitual.',
    },
  },
  {
    keywords: ['email'],
    help: {
      title: 'Correo electrónico',
      summary: 'Correo de contacto de la persona.',
      officialTerm: 'Email',
      why: 'Puede usarse para avisos, acceso a plataforma, recuperación de contraseña o comunicación interna.',
      whereToFind: 'Lo facilita el trabajador o la empresa.',
      example: 'nombre@empresa.es',
      nextStep: 'Comprueba que esté bien escrito antes de guardar.',
    },
  },
  {
    keywords: ['id_comercio', 'empresa servicios agrícolas', 'empresa servicios agricolas', 'proveedor'],
    help: {
      title: 'Empresa de servicios o proveedor',
      summary: 'Indica la empresa relacionada con este trabajador o servicio.',
      officialTerm: 'Proveedor / empresa de servicios agrícolas',
      why: 'Ayuda a saber si la persona pertenece a una empresa externa o proveedor concreto.',
      whereToFind: 'En contratos, facturas, albaranes o listado de empresas de la aplicación.',
      example: 'Servicios Agrícolas Martínez, Cooperativa Local',
      nextStep: 'Selecciona la empresa correcta para evitar mezclar trabajadores propios y externos.',
    },
  },
  {
    keywords: ['username', 'usuario'],
    help: {
      title: 'Usuario',
      summary: 'Nombre de usuario con el que esta persona accederá a la plataforma.',
      officialTerm: 'Usuario de acceso',
      why: 'Sirve para identificar quién entra en la aplicación y qué acciones realiza.',
      whereToFind: 'Lo define el administrador o responsable de la aplicación.',
      example: 'jgarcia, maria.martinez',
      nextStep: 'Usa un usuario fácil de reconocer y evita duplicados.',
    },
  },
  {
    keywords: ['password_repeat', 'repetir contraseña', 'repetir contrasena'],
    help: {
      title: 'Repetir contraseña',
      summary: 'Vuelve a escribir la contraseña para confirmar que no hay errores.',
      officialTerm: 'Confirmación de contraseña',
      why: 'Evita guardar una clave mal escrita.',
      whereToFind: 'Debe coincidir exactamente con el campo contraseña.',
      example: 'La misma contraseña escrita en el campo anterior',
      nextStep: 'Si no coincide, vuelve a escribir ambas contraseñas.',
    },
  },
  {
    keywords: ['acceso_plataforma', 'acceso a plataforma'],
    help: {
      title: 'Acceso a plataforma',
      summary: 'Indica si esta persona puede entrar en la aplicación.',
      officialTerm: 'Permiso de acceso',
      why: 'Permite controlar quién puede consultar, registrar o modificar datos.',
      whereToFind: 'Lo decide el administrador o responsable de la explotación.',
      example: 'Activado para encargados o responsables',
      nextStep: 'Actívalo solo si la persona necesita usar la aplicación.',
    },
  },
  {
    keywords: ['observaciones'],
    help: {
      title: 'Observaciones',
      summary: 'Campo libre para añadir notas útiles sobre esta persona.',
      why: 'Sirve para recordar información interna que no encaja en otros campos.',
      example: 'Disponible por las mañanas. Especialista en tratamientos. Revisar carnet antes de campaña.',
      nextStep: 'Escribe solo información útil para la gestión diaria.',
    },
  },
  {
    keywords: ['id_cliente_asignado', 'cliente asignado', 'titular asignado'],
    help: {
      title: 'Cliente asignado',
      summary: 'Indica a qué cliente, titular o explotación queda asociada esta persona.',
      officialTerm: 'Cliente / titular asignado',
      why: 'Evita mezclar trabajadores entre distintas fincas, clientes o titulares.',
      whereToFind: 'En el listado de clientes o titulares de la aplicación.',
      example: 'Finca Las Lomas, Agrícola Norte S.L.',
      nextStep: 'Comprueba que la persona queda asociada al cliente correcto.',
    },
  },
  {
    keywords: ['propiedad'],
    help: {
      title: 'Propiedad',
      summary: 'Indica si la persona o recurso pertenece a una propiedad concreta.',
      officialTerm: 'Propiedad',
      why: 'Ayuda a organizar trabajadores o recursos por titularidad o finca.',
      whereToFind: 'En la organización interna de la empresa o explotación.',
      example: 'Propio, alquilado, externo',
      nextStep: 'Selecciona la opción que corresponda según la gestión de la empresa.',
    },
  },
  {
    keywords: ['es_almacen', 'almacén', 'almacen'],
    help: {
      title: 'Almacén',
      summary: 'Indica si esta persona está relacionada con tareas de almacén.',
      officialTerm: 'Perfil de almacén',
      why: 'Puede usarse para filtrar permisos, tareas o responsabilidades internas.',
      whereToFind: 'Según la función real de la persona dentro de la empresa.',
      example: 'Responsable de almacén, operario de almacén',
      nextStep: 'Actívalo solo si esta persona trabaja o gestiona almacén.',
    },
  },
  {
    keywords: ['activo'],
    help: {
      title: 'Activo',
      summary: 'Indica si esta persona se usa actualmente en la aplicación.',
      officialTerm: 'Estado activo',
      why: 'Permite separar trabajadores actuales de antiguos o inactivos.',
      nextStep: 'Desactívalo solo si no quieres que aparezca en nuevas tareas o registros.',
    },
  },
  {
    keywords: ['adjunto'],
    help: {
      title: 'Archivo adjunto',
      summary: 'Permite guardar documentación o fotografía relacionada con esta persona.',
      officialTerm: 'Adjunto',
      why: 'Ayuda a conservar carnets, autorizaciones, documentos o fotos de identificación.',
      whereToFind: 'Puedes subirlo desde tu ordenador, móvil o gestor documental.',
      example: 'Carnet aplicador.pdf, autorización firmada, foto del trabajador',
      nextStep: 'Adjunta documentos útiles para consultas futuras.',
    },
  },
  {
    keywords: ['ropo'],
    help: {
      title: 'ROPO',
      summary: 'Número o referencia del Registro Oficial de Productores y Operadores de medios de defensa fitosanitaria.',
      officialTerm: 'ROPO',
      why: 'Sirve para acreditar que la persona o entidad está habilitada para actividades relacionadas con productos fitosanitarios.',
      whereToFind: 'En el carnet, certificado o consulta pública del ROPO.',
      example: 'Número ROPO del aplicador o asesor',
      nextStep: 'Si no lo conoces, consulta la documentación del carnet o el registro oficial.',
    },
  },
  {
    keywords: ['tipo', 'tipo carnet'],
    help: {
      title: 'Tipo de carnet',
      summary: 'Indica el nivel o tipo de carnet fitosanitario de la persona.',
      officialTerm: 'Tipo de carnet de aplicador',
      why: 'Ayuda a saber qué trabajos fitosanitarios puede realizar la persona.',
      whereToFind: 'En el carnet de aplicador o certificado de formación.',
      example: 'Básico, cualificado, fumigador, piloto aplicador, asesor',
      nextStep: 'Selecciona el tipo que aparece en la documentación oficial.',
    },
  },
  {
    keywords: ['exp', 'fecha expedición', 'fecha expedicion'],
    help: {
      title: 'Fecha de expedición',
      summary: 'Fecha en la que se emitió el carnet o certificado.',
      officialTerm: 'Fecha de expedición',
      why: 'Sirve para comprobar desde cuándo está habilitada la persona.',
      whereToFind: 'En el carnet, certificado o resolución emitida por la administración.',
      example: '15/02/2023',
      nextStep: 'Introduce la fecha que aparece en el documento.',
    },
  },
  {
    keywords: ['cad', 'fecha caducidad'],
    help: {
      title: 'Fecha de caducidad',
      summary: 'Fecha hasta la que el carnet o habilitación sigue vigente.',
      officialTerm: 'Fecha de caducidad',
      why: 'Ayuda a evitar que una persona con carnet caducado figure como aplicador válido.',
      whereToFind: 'En el carnet, certificado o consulta del registro.',
      example: '15/02/2033',
      nextStep: 'Revisa esta fecha antes de campañas o tratamientos importantes.',
    },
  },
];

const PRODUCTOS_FITO_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['otra_denominacion_comun', 'otra denominacion comun', 'otra denominación común'],
    help: {
      title: 'Otra denominación común',
      summary: 'Activa esta opción si el producto aparece en el registro con una denominación común distinta a la habitual.',
      officialTerm: 'Otra denominación común',
      why: 'Ayuda a buscar correctamente productos fitosanitarios que pueden aparecer con nombres alternativos.',
      whereToFind: 'En la etiqueta del producto, ficha técnica, factura o consulta del Registro de Productos Fitosanitarios.',
      example: 'Producto con nombre comercial distinto al nombre común usado en el registro.',
      nextStep: 'Actívalo solo si no encuentras el producto con la denominación estándar.',
    },
  },
  {
    keywords: ['num_registro', 'nº registro', 'numero registro', 'número registro'],
    help: {
      title: 'Número de registro',
      summary: 'Es el número oficial con el que el producto fitosanitario está identificado en el Registro.',
      officialTerm: 'Nº Registro',
      why: 'Sirve para seleccionar el producto autorizado correcto y evitar confundir productos parecidos.',
      whereToFind: 'En la etiqueta del envase, ficha del producto, factura o Registro de Productos Fitosanitarios del MAPA.',
      example: 'ES-01234, 12345',
      nextStep: 'Selecciona el producto desde el desplegable y comprueba que coincide con la etiqueta del envase.',
    },
  },
  {
    keywords: ['nombre', 'nombre del producto'],
    help: {
      title: 'Nombre del producto',
      summary: 'Nombre comercial o identificativo del producto fitosanitario.',
      officialTerm: 'Nombre comercial',
      why: 'Ayuda a reconocer el producto en almacén, compras y tareas fitosanitarias.',
      whereToFind: 'En la etiqueta del envase, factura, albarán o ficha del producto.',
      example: 'Nombre comercial que aparece en la etiqueta.',
      nextStep: 'Si el nombre se rellena desde el registro, revisa que coincide con el producto seleccionado.',
    },
  },
  {
    keywords: ['imagen'],
    help: {
      title: 'Imagen del producto',
      summary: 'Imagen o espacio visual asociado al producto.',
      officialTerm: 'Imagen / referencia visual',
      why: 'Puede ayudar a reconocer el producto rápidamente en almacén.',
      whereToFind: 'Foto del envase o imagen cargada en la aplicación.',
      example: 'Fotografía frontal del envase.',
      nextStep: 'Usa una imagen clara si la aplicación permite adjuntarla.',
    },
  },
  {
    keywords: ['precio', 'precio coste', 'precio de coste'],
    help: {
      title: 'Precio',
      summary: 'Coste de compra del producto.',
      officialTerm: 'Precio de coste',
      why: 'Sirve para calcular el coste de tratamientos, existencias o compras.',
      whereToFind: 'En la factura, albarán, tarifa del proveedor o registro interno de compras.',
      example: '24,50 €/l, 80 €/envase',
      nextStep: 'Introduce el precio sin símbolo de euro si el campo ya lo indica.',
    },
  },
  {
    keywords: ['precio_venta', 'precio de venta'],
    help: {
      title: 'Precio de venta',
      summary: 'Precio al que se vende o repercute el producto.',
      officialTerm: 'Precio de venta',
      why: 'Sirve para presupuestos, ventas internas o cálculo de márgenes.',
      whereToFind: 'En la tarifa de venta, catálogo interno o criterio comercial de la empresa.',
      example: '32 €/l, 95 €/envase',
      nextStep: 'Comprueba que no estás introduciendo el precio de coste si el campo pide precio de venta.',
    },
  },
  {
    keywords: ['familia'],
    help: {
      title: 'Familia',
      summary: 'Grupo o categoría interna del producto.',
      officialTerm: 'Familia de producto',
      why: 'Ayuda a ordenar productos en almacén, listados y búsquedas.',
      whereToFind: 'En la clasificación interna de la empresa o en el catálogo de productos.',
      example: 'Herbicida, fungicida, insecticida, acaricida.',
      nextStep: 'Selecciona la familia que mejor represente el uso principal del producto.',
    },
  },
  {
    keywords: ['unidades', 'uds medida', 'uds. medida', 'unidades medida'],
    help: {
      title: 'Unidades de medida',
      summary: 'Unidad con la que se mide o gestiona el producto.',
      officialTerm: 'Unidad de medida',
      why: 'Evita errores al calcular dosis, stock, compras o costes.',
      whereToFind: 'En la etiqueta del envase, factura o formato de compra.',
      example: 'l, kg, g, ml, envase',
      nextStep: 'Elige la unidad que coincide con cómo compras o aplicas el producto.',
    },
  },
  {
    keywords: ['envase'],
    help: {
      title: 'Envase',
      summary: 'Formato o tamaño del recipiente del producto.',
      officialTerm: 'Envase',
      why: 'Ayuda a controlar existencias, compras y uso real en almacén.',
      whereToFind: 'En la etiqueta del producto, caja, factura o albarán.',
      example: '1 l, 5 l, 20 kg, caja de 12 unidades.',
      nextStep: 'Indica el tamaño del envase de forma clara.',
    },
  },
  {
    keywords: ['dosis'],
    help: {
      title: 'Dosis',
      summary: 'Cantidad recomendada o autorizada del producto para una aplicación.',
      officialTerm: 'Dosis autorizada / dosis de uso',
      why: 'Ayuda a aplicar el producto correctamente y a evitar errores en tratamientos.',
      whereToFind: 'En la etiqueta del envase, ficha técnica o Registro de Productos Fitosanitarios.',
      example: '0,5 l/ha, 250 g/hl, según cultivo y plaga.',
      nextStep: 'Revisa siempre cultivo, plaga, dosis y condiciones antes de usar el producto.',
    },
  },
  {
    keywords: ['num_maximo_aplicaciones', 'nº máximo aplicaciones', 'nº maximo aplicaciones', 'numero maximo aplicaciones'],
    help: {
      title: 'Número máximo de aplicaciones',
      summary: 'Número máximo de veces que se puede aplicar el producto según las condiciones autorizadas.',
      officialTerm: 'Nº máximo de aplicaciones',
      why: 'Ayuda a respetar los límites de uso y evitar superar aplicaciones permitidas.',
      whereToFind: 'En la etiqueta, ficha técnica o Registro de Productos Fitosanitarios.',
      example: 'Máximo 2 aplicaciones por campaña.',
      nextStep: 'Anota este dato si aparece en la autorización o etiqueta del producto.',
    },
  },
  {
    keywords: ['observaciones'],
    help: {
      title: 'Observaciones',
      summary: 'Notas internas sobre el producto fitosanitario.',
      why: 'Sirve para recordar condiciones de uso, proveedor, restricciones o comentarios prácticos.',
      whereToFind: 'Información interna, etiqueta, ficha técnica o indicaciones del asesor.',
      example: 'Revisar plazo de seguridad. Usar solo en determinados cultivos.',
      nextStep: 'Escribe solo información útil para compras, almacén o tratamientos.',
    },
  },
  {
    keywords: ['ultima_fecha_compra', 'última fecha compra', 'ultima fecha compra'],
    help: {
      title: 'Última fecha de compra',
      summary: 'Fecha en la que se compró por última vez este producto.',
      officialTerm: 'Última fecha de compra',
      why: 'Ayuda a controlar compras, existencias, rotación de stock y trazabilidad interna.',
      whereToFind: 'En la factura, albarán o histórico de compras.',
      example: '15/03/2026',
      nextStep: 'Actualízala cuando registres una compra nueva.',
    },
  },
  {
    keywords: ['materia_activa', 'materia activa'],
    help: {
      title: 'Materia activa',
      summary: 'Sustancia o sustancias que hacen que el producto tenga efecto fitosanitario.',
      officialTerm: 'Sustancia activa / materia activa',
      why: 'Permite entender qué componente actúa contra la plaga, enfermedad o mala hierba.',
      whereToFind: 'En la etiqueta, ficha técnica o Registro de Productos Fitosanitarios.',
      example: 'Abamectina, cobre, azufre, glifosato.',
      nextStep: 'Si el campo no se puede editar, normalmente se rellena desde el producto seleccionado.',
    },
  },
];

const TAREAS_FERTILIZANTES_EXTRA_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  {
    keywords: ['id_finca', 'finca'],
    help: {
      title: 'Finca',
      summary: 'Explotación o finca donde se registra la fertilización.',
      officialTerm: 'Finca',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Finca Las Lomas',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['ids_sectores', 'sectores_nombres', 'sector', 'sectores'],
    help: {
      title: 'Sectores',
      summary: 'Agrupación de parcelas sobre la que se aplica la fertilización.',
      officialTerm: 'Sectores',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Sector cítricos norte',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['id_parcelas', 'parcelas_nombres', 'parcela', 'parcelas'],
    help: {
      title: 'Parcelas fertilizadas',
      summary: 'Parcelas o recintos donde se realiza la aplicación de fertilizante.',
      officialTerm: 'Parcelas fertilizadas',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Parcelas 1, 2 y 3',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['id_cliente', 'cliente'],
    help: {
      title: 'Cliente / titular',
      summary: 'Cliente o titular al que pertenece la tarea.',
      officialTerm: 'Cliente / titular',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Agrícola Norte S.L.',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['facturado'],
    help: {
      title: 'Facturado',
      summary: 'Indica si la tarea ya se ha facturado.',
      officialTerm: 'Facturado',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Activado cuando existe factura o albarán',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['fecha_inicio', 'fecha inicio'],
    help: {
      title: 'Fecha de inicio',
      summary: 'Fecha en la que empieza la tarea o planificación de fertilización.',
      officialTerm: 'Fecha de inicio',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '15/03/2026',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['fecha_fin', 'fecha fin', 'fecha'],
    help: {
      title: 'Fecha de finalización',
      summary: 'Fecha real o prevista en la que termina la aplicación.',
      officialTerm: 'Fecha de finalización',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '15/03/2026',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['hora_inicio', 'hora inicio'],
    help: {
      title: 'Hora de inicio',
      summary: 'Hora a la que comienza la tarea.',
      officialTerm: 'Hora de inicio',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '08:30',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['hora_fin', 'hora fin'],
    help: {
      title: 'Hora de fin',
      summary: 'Hora a la que termina la tarea.',
      officialTerm: 'Hora de fin',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '11:15',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['horas_totales', 'horas'],
    help: {
      title: 'Horas totales',
      summary: 'Duración total de la tarea.',
      officialTerm: 'Horas totales',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '2 h 45 min',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['tipo_fertilizacion', 'tipo fertilización', 'tipo fertilizacion'],
    help: {
      title: 'Tipo de fertilización',
      summary: 'Clasificación de la fertilización realizada.',
      officialTerm: 'Tipo de fertilización',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Fertirrigación, abonado de fondo, abonado de cobertera',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['albaran_factura', 'albarán/factura', 'albaran/factura', 'factura/albarán', 'factura/albaran'],
    help: {
      title: 'Albarán / factura',
      summary: 'Referencia documental relacionada con la compra o servicio.',
      officialTerm: 'Albarán / factura',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'ALB-2026-015',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['prioridad', 'prioridad/realizada'],
    help: {
      title: 'Prioridad / realizada',
      summary: 'Indica prioridad de ejecución o si la tarea ya se ha realizado.',
      officialTerm: 'Prioridad / realizada',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Alta, media, baja o realizada',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['procura'],
    help: {
      title: 'Procura',
      summary: 'Criterio interno sobre el régimen o forma de actuación.',
      officialTerm: 'Procura',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Procura, media procura o libre',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['horas_dia', 'horas/día', 'horas/dia'],
    help: {
      title: 'Horas por día',
      summary: 'Horas previstas o realizadas cada día.',
      officialTerm: 'Horas por día',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '6 h/día',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['numero_dias', 'días/semana', 'dias/semana'],
    help: {
      title: 'Días por semana',
      summary: 'Número de días semanales previstos para la tarea.',
      officialTerm: 'Días por semana',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '3 días/semana',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['dias_semana', 'días', 'dias'],
    help: {
      title: 'Días de la semana',
      summary: 'Días concretos en los que se programa la tarea.',
      officialTerm: 'Días de la semana',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Lunes, miércoles y viernes',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['nombre_cuba', 'nombre/cuba'],
    help: {
      title: 'Nombre / cuba',
      summary: 'Identificador de la cuba o equipo usado en la aplicación.',
      officialTerm: 'Nombre / cuba',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Cuba 1, Cuba fertirriego norte',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['consumo_agua', 'consumo agua m³', 'consumo agua m3'],
    help: {
      title: 'Consumo de agua',
      summary: 'Volumen de agua usado durante la aplicación.',
      officialTerm: 'Consumo de agua',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '12 m³',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['notas_cliente', 'notas cliente'],
    help: {
      title: 'Notas del cliente',
      summary: 'Indicaciones del cliente que conviene revisar antes de actuar.',
      officialTerm: 'Notas del cliente',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'No entrar por la puerta norte',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['ids_trabajadores', 'trabajadores_nombres', 'profesionales'],
    help: {
      title: 'Profesionales',
      summary: 'Personas que realizan o participan en la fertilización.',
      officialTerm: 'Profesionales',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Juan García, María López',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['id_maquinaria', 'maquinaria'],
    help: {
      title: 'Maquinaria',
      summary: 'Máquina o equipo utilizado para aplicar el fertilizante.',
      officialTerm: 'Maquinaria',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Tractor con abonadora, cuba, inyectora',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['aplicado'],
    help: {
      title: 'Aplicado',
      summary: 'Indica si la fertilización ya se ha realizado.',
      officialTerm: 'Aplicado',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Activado cuando la tarea está confirmada',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['ids_asignados', 'asignador'],
    help: {
      title: 'Asignador',
      summary: 'Persona que asigna o supervisa la tarea.',
      officialTerm: 'Asignador',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Encargado o responsable técnico',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['codigo_metodo_aplicacion', 'métodos de aplicación fertilizantes', 'metodos de aplicacion fertilizantes'],
    help: {
      title: 'Método de aplicación',
      summary: 'Forma en la que se aplica el fertilizante.',
      officialTerm: 'Método de aplicación',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Fertirrigación, aplicación localizada, aplicación en superficie',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['codigos_buena_practica', 'buenas prácticas fertilizantes', 'buenas practicas fertilizantes'],
    help: {
      title: 'Buenas prácticas fertilizantes',
      summary: 'Buenas prácticas asociadas a la aplicación realizada.',
      officialTerm: 'Buenas prácticas fertilizantes',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Evitar lluvia intensa, ajustar dosis, no aplicar cerca de cauces',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['grupo'],
    help: {
      title: 'Grupo',
      summary: 'Agrupación interna de la tarea o cultivo.',
      officialTerm: 'Grupo',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Grupo cereal invierno',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['tipo_riego', 'tipo riego'],
    help: {
      title: 'Tipo de riego',
      summary: 'Sistema de riego relacionado con la fertilización.',
      officialTerm: 'Tipo de riego',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Goteo, aspersión, gravedad',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['produccion_estimada', 'producción estimada', 'produccion estimada'],
    help: {
      title: 'Producción estimada',
      summary: 'Producción prevista para calcular necesidades de abonado.',
      officialTerm: 'Producción estimada',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '8.000 kg/ha',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['necesidad_maxima', 'necesidad máxima', 'necesidad maxima'],
    help: {
      title: 'Necesidad máxima',
      summary: 'Necesidad máxima estimada de nutrientes.',
      officialTerm: 'Necesidad máxima',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '180 kg N/ha',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['aporte'],
    help: {
      title: 'Aporte',
      summary: 'Cantidad de nutriente o fertilizante aportada.',
      officialTerm: 'Aporte',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '40 kg N/ha',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['cosecha_obtenida', 'cosecha obtenida'],
    help: {
      title: 'Cosecha obtenida',
      summary: 'Producción real conseguida al final del ciclo.',
      officialTerm: 'Cosecha obtenida',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '7.500 kg/ha',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['estado_tarea', 'estado de la tarea'],
    help: {
      title: 'Estado de la tarea',
      summary: 'Situación actual de la tarea.',
      officialTerm: 'Estado de la tarea',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Pendiente, realizada, revisada',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['empresa_pagadora', 'empresa pagadora'],
    help: {
      title: 'Empresa pagadora',
      summary: 'Empresa que asumirá el coste o facturación del trabajo.',
      officialTerm: 'Empresa pagadora',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Cooperativa o empresa cliente',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['recomendaciones_riego', 'recomendaciones de riego'],
    help: {
      title: 'Recomendaciones de riego',
      summary: 'Indicaciones de riego relacionadas con la fertilización.',
      officialTerm: 'Recomendaciones de riego',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Regar después de aplicar o mantener riego 30 minutos',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['observaciones'],
    help: {
      title: 'Observaciones',
      summary: 'Notas útiles sobre la fertilización, productos o incidencias.',
      officialTerm: 'Observaciones',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Con inhibidor de nitrógeno. Aplicar antes de lluvia suave.',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['ferti_group'],
    help: {
      title: 'Productos fertilizantes',
      summary: 'Bloque donde se añaden uno o varios productos fertilizantes de la tarea.',
      officialTerm: 'Productos fertilizantes',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'NPK 15-15-15 y nitrato cálcico',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['producto', 'producto ferti', 'ids_productos', 'productos_nombres'],
    help: {
      title: 'Producto fertilizante',
      summary: 'Producto usado en la aplicación.',
      officialTerm: 'Producto fertilizante',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Abono NPK 15-15-15',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['dosis', 'kg totales'],
    help: {
      title: 'Kg totales',
      summary: 'Cantidad total de producto aplicado.',
      officialTerm: 'Kg totales',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '250 kg',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['gramos'],
    help: {
      title: 'Gramos',
      summary: 'Cantidad calculada o expresada en gramos cuando el formulario lo requiere.',
      officialTerm: 'Gramos',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '500 g',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['kg_ha', 'kg/ha'],
    help: {
      title: 'Kg por hectárea',
      summary: 'Cantidad de producto aplicada por superficie.',
      officialTerm: 'Kg por hectárea',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: '300 kg/ha',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  },
  {
    keywords: ['orden_trabajo', 'orden de trabajo'],
    help: {
      title: 'Orden de trabajo',
      summary: 'Instrucciones internas para ejecutar la tarea.',
      officialTerm: 'Orden de trabajo',
      why: 'Ayuda a dejar la tarea de fertilización bien registrada en el cuaderno y facilita el control de dosis, costes, trazabilidad y envío posterior si procede.',
      whereToFind: 'En la planificación de abonado, etiqueta o ficha del fertilizante, factura, albarán, recomendación técnica o datos reales de la aplicación.',
      example: 'Aplicar por la mañana y revisar presión de la cuba',
      nextStep: 'Copia el dato real de la aplicación. Si la tarea aún no se ha realizado, usa la información prevista o recomendada.',
    },
  }
];

const ORDERED_FIELD_HELP_RULES: readonly FieldHelpRule[] = [
  ...TAREAS_FERTILIZANTES_EXTRA_FIELD_HELP_RULES,
  ...PRODUCTOS_FITO_FIELD_HELP_RULES,
  ...TRABAJADORES_FIELD_HELP_RULES,
  ...PARCELAS_FIELD_HELP_RULES,
  ...SECTORES_FIELD_HELP_RULES,
  ...MAQUINARIA_FIELD_HELP_RULES,
  ...APLICADORES_FIELD_HELP_RULES,
  ...CARNET_APLICADOR_FIELD_HELP_RULES,
  ...PRODUCTOS_FITOSANITARIOS_FIELD_HELP_RULES,
  ...PRODUCTOS_FERTILIZANTES_FIELD_HELP_RULES,
  ...TAREAS_FITOSANITARIAS_FIELD_HELP_RULES,
  ...TAREAS_FERTILIZANTES_FIELD_HELP_RULES,
  ...FIELD_HELP_RULES,
];

/**
 * Builds structured help so templates can show an inline, clickable explanation.
 */
export function buildAgriculturalFieldHelpDetails(
  fieldName: string | undefined,
  fieldLabel: string | undefined,
  explicitHelpText?: string,
  context?: AgriculturalHelpContext
): AgriculturalFieldHelpDetails {
  const helpText = explicitHelpText?.trim();

  if (helpText) {
    return {
      title: buildFallbackTitle(fieldLabel),
      summary: helpText,
      nextStep: 'Lee esta ayuda antes de guardar el formulario.',
    };
  }

  const normalizedFieldName = normalizeSearchText(fieldName ?? '');
  const normalizedFieldLabel = normalizeSearchText(fieldLabel ?? '');
  const contextualRules = getContextualFieldHelpRules(context);
  const contextualFallback = buildContextualFieldNameFallback(normalizedFieldName, context);

  if (contextualFallback) {
    return contextualFallback;
  }

  const contextualRule =
    findRuleByExactKeyword(contextualRules, normalizedFieldName) ??
    findRuleByExactKeyword(contextualRules, normalizedFieldLabel) ??
    findRuleBySafeLabelMatch(contextualRules, normalizedFieldLabel);

  if (contextualRule) {
    return contextualRule.help;
  }

  const matchingRule =
    findRuleByExactKeyword(ORDERED_FIELD_HELP_RULES, normalizedFieldName) ??
    findRuleByExactKeyword(ORDERED_FIELD_HELP_RULES, normalizedFieldLabel) ??
    findRuleBySafeLabelMatch(ORDERED_FIELD_HELP_RULES, normalizedFieldLabel);

  if (matchingRule) {
    return matchingRule.help;
  }

  return buildFallbackHelpDetails(fieldLabel);
}


function getContextualFieldHelpRules(context: AgriculturalHelpContext | undefined): readonly FieldHelpRule[] {
  const normalizedContext = normalizeContext(context);

  if (isTareasFertilizantesContext(normalizedContext)) {
    return [
      ...TAREAS_FERTILIZANTES_EXTRA_FIELD_HELP_RULES,
      ...TAREAS_FERTILIZANTES_FIELD_HELP_RULES,
      ...PRODUCTOS_FERTILIZANTES_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (isTareasFitosanitariasContext(normalizedContext)) {
    return [
      ...TAREAS_FITOSANITARIAS_FIELD_HELP_RULES,
      ...PRODUCTOS_FITO_FIELD_HELP_RULES,
      ...PRODUCTOS_FITOSANITARIOS_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['sectores', 'sector', 'grupo de parcelas', 'grupo de parcela'])) {
    return [
      ...SECTORES_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['parcelas', 'parcela', 'campos', 'campo'])) {
    return [
      ...PARCELAS_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['maquinaria', 'maquina', 'máquina', 'equipo'])) {
    return [
      ...MAQUINARIA_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['aplicadores', 'aplicador', 'trabajador', 'trabajadores'])) {
    return [
      ...TRABAJADORES_FIELD_HELP_RULES,
      ...APLICADORES_FIELD_HELP_RULES,
      ...CARNET_APLICADOR_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['productos fitosanitarios', 'producto fitosanitario', 'vademecum', 'vademécum'])) {
    return [
      ...PRODUCTOS_FITO_FIELD_HELP_RULES,
      ...PRODUCTOS_FITOSANITARIOS_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (isProductosFertilizantesContext(normalizedContext)) {
    return [
      ...PRODUCTOS_FERTILIZANTES_FIELD_HELP_RULES,
      ...FIELD_HELP_RULES,
    ];
  }

  if (hasAnyContextToken(normalizedContext, ['fincas', 'finca', 'explotacion', 'explotación'])) {
    return [
      ...FIELD_HELP_RULES,
    ];
  }

  return [];
}







function isTareasFertilizantesContext(normalizedContext: string): boolean {
  return hasAnyContextToken(normalizedContext, [
    'tarea fertilizante',
    'tareas fertilizantes',
    'tarea de fertilizacion',
    'tarea de fertilización',
    'tareas de fertilizacion',
    'tareas de fertilización',
    'fertilizaciones',
    'abonados',
    'tipo fertilizacion',
    'tipo fertilización',
    'metodos de aplicacion fertilizantes',
    'métodos de aplicación fertilizantes',
    'buenas practicas fertilizantes',
    'buenas prácticas fertilizantes',
    'producto ferti',
    'kg totales',
    'kg/ha',
  ]) || (
    hasAnyContextToken(normalizedContext, ['fertilizacion', 'fertilización', 'abonado']) &&
    !hasAnyContextToken(normalizedContext, [
      'producto fertilizante',
      'productos fertilizantes',
      'nombre o marca comercial',
      'tipo de abono organico',
      'tipo de abono orgánico',
      'material fertilizante',
      'metales pesados',
      'composicion',
      'composición',
    ])
  );
}

function isProductosFertilizantesContext(normalizedContext: string): boolean {
  return hasAnyContextToken(normalizedContext, [
    'producto fertilizante',
    'productos fertilizantes',
    'nombre o marca comercial',
    'tipo de abono organico',
    'tipo de abono orgánico',
    'material fertilizante',
    'metales pesados',
    'composicion',
    'composición',
  ]);
}

function isTareasFitosanitariasContext(normalizedContext: string): boolean {
  return hasAnyContextToken(normalizedContext, [
    'tarea fitosanitaria',
    'tareas fitosanitarias',
    'tratamiento fitosanitario',
    'tratamientos fitosanitarios',
    'tarea fito',
    'tareas fito',
  ]);
}

function buildContextualFieldNameFallback(
  normalizedFieldName: string,
  context: AgriculturalHelpContext | undefined
): AgriculturalFieldHelpDetails | undefined {
  const normalizedContext = normalizeContext(context);

  if (!hasAnyContextToken(normalizedContext, ['sectores', 'sector', 'grupo de parcelas', 'grupo de parcela'])) {
    return undefined;
  }

  if (normalizedFieldName === 'nombre') {
    return {
      title: 'Nombre del sector',
      summary: 'Es el nombre con el que reconocerás este sector o grupo de parcelas dentro de la finca.',
      officialTerm: 'Sector / grupo de parcelas',
      why: 'Ayuda a elegir el grupo correcto al apuntar tratamientos, fertilizaciones, riegos u otras tareas.',
      whereToFind: 'Puedes usar el nombre que ya utilizas en el campo o en tus notas.',
      example: 'Olivar norte, Cítricos goteo 1, Invernadero 2',
      nextStep: 'Usa un nombre corto y fácil de reconocer.',
    };
  }

  return undefined;
}

function normalizeContext(context: AgriculturalHelpContext | undefined): string {
  const values = [context?.formName, context?.formHeader, context?.sectionName]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  return normalizeSearchText(values.join(' '));
}

function hasAnyContextToken(normalizedContext: string, tokens: readonly string[]): boolean {
  return tokens.some((token) => normalizedContext.includes(normalizeSearchText(token)));
}

/**
 * Finds a rule only when the keyword exactly matches the normalized field name or label.
 */
function findRuleByExactKeyword(
  rules: readonly FieldHelpRule[],
  normalizedText: string
): FieldHelpRule | undefined {
  if (!normalizedText) {
    return undefined;
  }

  return rules.find((rule) =>
    rule.keywords.some((keyword) => normalizeSearchText(keyword) === normalizedText)
  );
}

/**
 * Finds a rule by partial label matching, avoiding dangerous generic keywords.
 */
function findRuleBySafeLabelMatch(
  rules: readonly FieldHelpRule[],
  normalizedFieldLabel: string
): FieldHelpRule | undefined {
  if (!normalizedFieldLabel) {
    return undefined;
  }

  return rules.find((rule) =>
    rule.keywords.some((keyword) => hasSafeKeywordMatch(normalizedFieldLabel, keyword))
  );
}

/**
 * Allows partial matching only for specific, non-dangerous keywords.
 */
function hasSafeKeywordMatch(normalizedFieldLabel: string, keyword: string): boolean {
  const normalizedKeyword = normalizeSearchText(keyword);

  if (!normalizedKeyword) {
    return false;
  }

  if (normalizedFieldLabel === normalizedKeyword) {
    return true;
  }

  if (UNSAFE_PARTIAL_HELP_KEYWORDS.has(normalizedKeyword)) {
    return false;
  }

  if (normalizedKeyword.length <= MINIMUM_SAFE_KEYWORD_LENGTH) {
    return false;
  }

  return normalizedFieldLabel.includes(normalizedKeyword);
}

export function formatAgriculturalFieldHelp(details: AgriculturalFieldHelpDetails): string {
  const parts = [details.summary];

  if (details.officialTerm) {
    parts.push(`Término oficial: ${details.officialTerm}.`);
  }

  if (details.whereToFind) {
    parts.push(`Dónde encontrarlo: ${details.whereToFind}.`);
  }

  if (details.example) {
    parts.push(`Ejemplo: ${details.example}.`);
  }

  return parts.join(' ');
}

function buildFallbackHelpDetails(fieldLabel: string | undefined): AgriculturalFieldHelpDetails {
  const label = fieldLabel?.trim();

  if (label) {
    return {
      title: buildFallbackTitle(label),
      summary: `Completa "${label}" con el dato real de la explotación, recurso o tarea.`,
      why: 'Un dato claro evita errores al guardar, revisar o generar documentación.',
      whereToFind: 'Revisa tus registros, documentos de la explotación o datos ya cargados en la aplicación.',
      nextStep: 'Si no estás seguro, deja el registro como borrador o consulta antes de enviarlo.',
    };
  }

  return {
    title: 'Ayuda del campo',
    summary: EMPTY_TEXT,
  };
}

function buildFallbackTitle(fieldLabel: string | undefined): string {
  const label = fieldLabel?.trim();

  return label ? `Ayuda sobre ${label}` : 'Ayuda del campo';
}

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u00a0\s]+/g, ' ')
    .trim();
}
